import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { findPaymentTerm, paymentTermWording } from '../utils/paymentTerms';
import merge from 'lodash.merge';
import { useLocation, useHistory, Link } from 'react-router-dom';
import Layout from './Layout';
import ConfirmationPopup from './ConfirmationPopup';
import './OfferGenerator.css';
import { qualityPresets, unitPresets, qualityParametersPresets } from './QualityPresetsStorage';
import { OfferContext } from '../OfferContext';
import InputMask from 'react-input-mask';
import { useFlashMessage, DEFAULT_DURATION } from './FlashMessage';
import { extractApiError, getErrorMessage } from '../utils/errorMessages';
import { recapSaveRefusal } from '../utils/recapSaveRefusal';
import RecordLoadProblem, { describeLoadFailure } from './ui/RecordLoadProblem';

//PDF Imports
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import hmsLogoBase64 from '../assets/logoBase64.json';
import withAuth from '../utils/withAuth';
import { fetchHMSEntities } from '../services/hmsEntityService';
// Import PortsDropdown at the top of the file
import PortsDropdown from './PortsDropdown';
// Import formatValidityDateTime function
import { formatValidityDateTime } from '../utils/dateUtils';
// Import OfferPartnerSearch for CRM-connected search
import OfferPartnerSearch from './OfferPartnerSearch';
import PledgeBankField, { pledgeOf, pledgeFieldsFor } from './shared/PledgeBankField';
// Add imports for debounce utility
import { debounce } from 'lodash';
// Import DatePicker for deal validity date
import DatePicker from './ui/DatePicker';
import DateRangePicker from './ui/DateRangePicker';
import AmountInput from './ui/AmountInput';
import BackToTopButton from './BackToTopButton';
import ActionButton from './ActionButton';
import logo from '../assets/images/IMT_logo.png';
import Footer from './ui/Footer';
import CreatedByLine from './ui/CreatedByLine';
import HeaderNameEditor from './ui/HeaderNameEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave } from '@fortawesome/free-solid-svg-icons';
import ChatWindow from './ChatWindow';
import { CloseIconSmall } from './ChecklistIcons';
import TradeRecapBackModal from './TradeRecapBackModal';
import DealConfirmationModal from './DealConfirmationModal';
// Import RecapProductSearch for product search with debouncing
import RecapProductSearch from './RecapProductSearch';
import DealProductLines from './DealProductLines';
import RecapProductQuantities, { recapQuantitiesProblem } from './RecapProductQuantities';
import HMSButton from './ui/HMSButton';
import {
  OFFER_LINE_KEYS, adoptSavedIds, blankLine, lineProductLabel, linesForSave, linesHeader, linesSignature,
  recapLinesFromOffer, remainingPerLine, severalLines, severalProductsClauseText, takenByRecaps,
} from '../utils/dealProductLines';
import { formatQuantityDisplay } from '../utils/quantityDisplay';
// Import services for dropdowns
import { fetchDeliveryDropdowns } from '../services/deliveryService';
import systemDataService from '../services/systemDataService';
import { CURRENCY_OPTIONS } from '../utils/constants';
import { goToTradeOps, useTradeOpsDesk } from '../utils/tradeOpsDesk';
import { PRICE_DECIMALS, QUANTITY_DECIMALS, formatFigure, subtractFigures } from '../utils/precision';


// Validation function for quantity input
const validateQuantity = (value) => {
  // Allow any text
  return true;
};

// Parse tolerance string into separate lower and upper components
const parseToleranceString = (toleranceString) => {
  if (!toleranceString) {
    return { lowerValue: '', lowerUnit: '%', upperValue: '', upperUnit: '%' };
  }
  // AI returns tolerance as a number (int), coerce to string before parsing
  const str = String(toleranceString);
  if (str.includes('to')) {
    const [lower, upper] = str.split('to').map(part => part.trim());
    const lowerMatch = lower.match(/^(\d*\.?\d*)(.*)$/);
    const upperMatch = upper.match(/^(\d*\.?\d*)(.*)$/);
    return {
      lowerValue: lowerMatch ? lowerMatch[1] : '',
      lowerUnit: (lowerMatch && lowerMatch[2]) ? lowerMatch[2] : '%',
      upperValue: upperMatch ? upperMatch[1] : '',
      upperUnit: (upperMatch && upperMatch[2]) ? upperMatch[2] : '%'
    };
  }
  // Single value - place in upper tolerance only
  const match = str.match(/^(\d*\.?\d*)(.*)$/);
  if (match) {
    return {
      lowerValue: '',
      lowerUnit: '%',
      upperValue: match[1],
      upperUnit: match[2] || '%'
    };
  }
  return { lowerValue: '', lowerUnit: '%', upperValue: '', upperUnit: '%' };
};

// Format tolerance field from separate values into display string
const formatToleranceField = (lowerValue, lowerUnit, upperValue, upperUnit) => {
  if (!lowerValue && !upperValue) return '';
  if (lowerValue && upperValue) return `${lowerValue}${lowerUnit} to ${upperValue}${upperUnit}`;
  if (lowerValue) return `${lowerValue}${lowerUnit}`;
  if (upperValue) return `${upperValue}${upperUnit}`;
  return '';
};

// Utility function for quantity formatting
const formatQuantityValue = (value) => {
  if (!value) return '';
  // Check if the value is a number (contains only digits, commas, periods, spaces, hyphens)
  const isNumeric = /^[\d,.\s-]+$/.test(value);
  if (isNumeric) {
    // For numeric values, format with SPACES as thousands separator (not commas)
    const parts = value.split(/[-\s]+/); // Split by hyphens or spaces
    return parts.map(part => {
      // Format each part if it's a number
      if (/^\d+$/.test(part.replace(/,/g, ''))) {
        const cleanValue = part.replace(/,/g, '');
        const numValue = parseInt(cleanValue, 10);
        if (!isNaN(numValue)) {
          // Format with space as thousands separator
          return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        }
      }
      return part;
    }).join(' - '); // Rejoin with hyphens (common for ranges)
  }
  // For non-numeric or mixed values, return as is
  return value;
};

// Helper function to get dropdown options for parameters that have them
const getParameterOptions = (paramName) => {
  const paramNameLower = paramName.toLowerCase();
  
  if (paramNameLower.includes('calorific') || paramNameLower.includes('ncv') || paramNameLower.includes('gcv')) {
    return [
      'Net Calorific Value(ARB)',
      'Gross Calorific Value(ARB)',
      'Gross Calorific Value(ADB)',
      'Gross Calorific Value(DB)'
    ];
  }
  
  if (paramNameLower.includes('ash')) {
    return [
      'Ash(ARB)',
      'Ash(ADB)',
      'Ash(DB)'
    ];
  }
  
  if (paramNameLower.includes('volatile matter') || paramNameLower.includes('vm')) {
    return [
      'Volatile Matter(ARB)',
      'Volatile Matter(ADB)',
      'Volatile Matter(DB)'
    ];
  }
  
  if (paramNameLower.includes('sulfur') || paramNameLower.includes('sulphur')) {
    return [
      'Sulfur(ARB)',
      'Sulfur(ADB)',
      'Sulfur(DB)'
    ];
  }
  
  return [];
};

function OfferGenerator() {
  const { offerResponse, setOfferResponse } = useContext(OfferContext);
  const location = useLocation();
  const history = useHistory();
  const { showSuccess, showError, showMessage } = useFlashMessage();

  // Add new state for tracking unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(Date.now());
  const AUTOSAVE_INTERVAL = 10000; // 30 seconds interval
  const AUTOSAVE_DEBOUNCE = 30000; // Update from 3 seconds to 1 minute debounce
  const debouncedSaveRef = useRef(null); // Add ref to store debounced function
  const [originalState, setOriginalState] = useState(null); // Add original state for rollback
  const originalDataRef = useRef(null);

  const [associatedTradeRecapId, setAssociatedTradeRecapId] = useState(null);
  const [hmsEntities, setHMSEntities] = useState([]); // Add HMS entities state
  const [isQuantityValid, setIsQuantityValid] = useState(true);
  const [hasAppliedDealType, setHasAppliedDealType] = useState(false);
  
  // Add state for custom quality rows
  const [customQualityRows, setCustomQualityRows] = useState([]);
  const [savedCustomRows, setSavedCustomRows] = useState([]);
  const [deletedCustomRows, setDeletedCustomRows] = useState([]); // Add this new state
  const [deleteModeActive, setDeleteModeActive] = useState(false);
  
  // Add a state to track if the deal exists in the database
  const [isDealInDatabase, setIsDealInDatabase] = useState(false);

  // Cleanup effect to reset context when unmounting
  useEffect(() => {
    return () => {
      setOfferResponse(null);
      setHasAppliedDealType(false);
    };
  }, [setOfferResponse]);

  // Add useEffect to fetch HMS entities
  useEffect(() => {
    const loadHMSEntities = async () => {
      try {
        const entities = await fetchHMSEntities();
        setHMSEntities(entities);
      } catch (error) {
        console.error('Error fetching HMS entities:', error);
      }
    };
    loadHMSEntities();
  }, []);

  // Load countries for Origin dropdown
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const deliveryOptions = await fetchDeliveryDropdowns();
        setCountries(deliveryOptions.countries || []);
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    };
    loadCountries();
  }, []);

  // Load units for Quantity dropdown
  useEffect(() => {
    const loadUnits = async () => {
      try {
        const unitsData = await systemDataService.getUnits();
        const activeUnits = unitsData.filter(unit => unit.is_active);
        setUnits(activeUnits);
      } catch (error) {
        console.error('Error fetching units:', error);
      }
    };
    loadUnits();
  }, []);

  // Initialize URL parameters first
  const offerType = new URLSearchParams(location.search).get('type') || 'new';
  const offerIdFromQuery = new URLSearchParams(location.search).get('offerId');
  const offerTypeText = offerType === 'rsa' ? 'RSA' : offerType === 'indo' ? 'INDO' : 'New';
  const queryParams = new URLSearchParams(location.search);
  const groupId = queryParams.get('groupId');
  const sourceOfferId = queryParams.get('sourceOfferId');
  const isGrouping = queryParams.get('grouping') === 'true';
  const dealTypeFromQuery = new URLSearchParams(location.search).get('dealType');

  // Trade Ops flavour (Solids vs Fuels) is propagated through location.state from
  // the parent layout. Defaults to 'solids' for legacy direct links.
  const commodityType = (location.state?.commodityType === 'fuels') ? 'fuels' : 'solids';
  // Row 127: Back and Save follow the shared rule -- the loaded deal's own
  // commodity, else the hint, else (Back only) the desk this person can open.
  // The hint alone saved a fuels deal opened by link back as Cargo. The bounce
  // below stays on the hint, so a legacy fuels deal opened by link stays viewable.
  const [dealCommodity, setDealCommodity] = useState(null);
  const tradeOpsDesk = useTradeOpsDesk({
    recordCommodity: dealCommodity,
    hintCommodity: location.state?.commodityType,
  });

  // Bunkers (fuels) no longer has a Bid/Offer screen — the PM dropped that
  // flow and the Enquiry -> Trade Recap path is now the only Bunkers route.
  // Defence-in-depth: if a Fuels user URL-types their way into
  // /offer-generator, bounce them back to the Bunkers Trade Ops landing.
  useEffect(() => {
    if (commodityType === 'fuels') {
      history.replace('/dashboard/trades/fuels');
    }
  }, [commodityType, history]);
  const [dealType, setDealType] = useState('Offer');
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  // Row 125: why the deal named by ?offerId= didn't load. While set, the
  // screen shows RecordLoadProblem INSTEAD of the form: a failed load used to
  // leave the empty form, whose Save created a brand-new deal.
  const [dealLoadProblem, setDealLoadProblem] = useState(null);
  const [dealLoadAttempt, setDealLoadAttempt] = useState(0);

  // Update the initial state to include a controlled checkbox state
  const [isDealTypeBid, setIsDealTypeBid] = useState(false);

  // Add a state to track if we're viewing a drafted deal
  const [isViewingDraft, setIsViewingDraft] = useState(false);

  // Initialize isDealInDatabase based on if we're viewing an existing deal
  useEffect(() => {
    // If we're viewing an existing deal (from URL or grouping), set isDealInDatabase to true
    if (offerIdFromQuery || (isGrouping && sourceOfferId)) {
      setIsDealInDatabase(true);
      if (offerIdFromQuery) {
        setIsViewingDraft(true);
      }
    }
  }, [offerIdFromQuery, isGrouping, sourceOfferId]);

  // Effect to handle deal type from URL
  useEffect(() => {
    if (isFormLoaded && dealTypeFromQuery === 'Bid' && !isViewingDraft) {
      setDealType('Bid');
      setIsDealTypeBid(true);
      
      // Get current seller and buyer values
      const currentSeller = offerData['Seller']?.['Selling Entity'] || '';
      const currentBuyer = offerData['Buyer']?.["Buyer's Entity"] || '';

      // Only swap if not viewing a draft
      setSelectedSeller(currentBuyer);
      setSelectedBuyer(currentSeller);
      
      // Update offerData with swapped values
      setOfferData(prevData => ({
        ...prevData,
        'Seller': { 'Selling Entity': currentBuyer },
        'Buyer': { "Buyer's Entity": currentSeller }
      }));

      // Set both as manually selected to prevent overwriting
      setManuallySelectedSeller(true);
      setManuallySelectedBuyer(true);
    } else if (isFormLoaded && dealTypeFromQuery === 'Bid' && isViewingDraft) {
      // Just set the deal type without swapping for drafted deals
      setDealType('Bid');
      setIsDealTypeBid(true);
    }
  }, [dealTypeFromQuery, isFormLoaded, isViewingDraft]);

  // Effect to handle deal type from offerResponse
  useEffect(() => {
    if (offerResponse && !hasAppliedDealType) {
      const newUpdatedFields = { ...updatedFields };

      if (offerResponse.Seller?.['Selling Entity']) {
        setSelectedSeller(offerResponse.Seller['Selling Entity']);
        newUpdatedFields['seller'] = true;
        setManuallySelectedSeller(false);
      }

      if (offerResponse.Buyer?.["Buyer's Entity"]) {
        setSelectedBuyer(offerResponse.Buyer["Buyer's Entity"]);
        newUpdatedFields['buyer'] = true;
        setManuallySelectedBuyer(false);
      }

      setUpdatedFields(newUpdatedFields);
      setHasAppliedDealType(true);
    }
  }, [offerResponse, hasAppliedDealType]);

  const [isIndicationOnly, setIsIndicationOnly] = useState(false);
  // Only the TYPED deal name lives here ('' = automatic). The automatic name
  // is the server's (offer/deal_names.py), refreshed from every answer.
  const [customDealName, setCustomDealName] = useState('');
  const [automaticDealName, setAutomaticDealName] = useState('');
  
  // ActionButton state for Hamster Deal Builder slideout
  const [isHamsterBuilderOpen, setIsHamsterBuilderOpen] = useState(false);
  const hamsterPanelRef = useRef(null);
  const [hamsterBuilderLog, setHamsterBuilderLog] = useState([]);



  // Add cleanup effect for body style
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Add isActive helper for navigation
  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const isDashboardActive = () => {
    return location.pathname.startsWith('/dashboard') ? "active" : "";
  };

  const handleLogout = () => {
    // Use the authService for logout
    try {
      // Simply remove isLoggedIn flag - the actual logout will be handled by the interceptors
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('user_id');
      history.push('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const [clauses, setClauses] = useState([]);
  const [offerData, setOfferData] = useState({
    // Core Offer Details
    offer_transaction_type: dealType || 'Offer', // Changed from 'Sale'
    offer_current_status: 'Draft',
    
    // Clause Values - all match with HMSUserOffer fields
    selling_company_entity: '',
    buying_company_entity: '',
    trade_incoterm_type: '',
    commodity_product_type: '',
    commodity_origin_country: '',
    commodity_additional_details: '',
    commodity_custom_override: '',
    quantity_in_metric_tons: '',
    quantity_tolerance_percentage: '',
    quantity_option_holder: '',
    quantity_custom_override: '',
    delivery_terms_and_conditions: '',
    loading_ports_details: '',
    price_determination_type: '',
    price_currency_type: '',
    price_fixed_amount: '',
    price_premium_or_discount: '',
    price_cv_basis_type: '',
    price_kcal_per_kg: '',
    price_index_applicable_period: '',
    price_custom_override: '',
    payment_method_type: '',           // Add this
    payment_detailed_terms: '',        // Add this
    shipping_terms_type: '',           // Add this
    shipping_detailed_conditions: '',   // Add this
    other_terms_category: '',          // Add this
    other_terms_detailed_conditions: '', // Add this
    deal_validity_date: '',
    deal_validity_time: '',
    deal_validity_timezone: '',

    "Quality": {
      "Quality Specifications": {
        "Total Moisture (ARB)": { "typical": "", "minMax": "" },
        "Inherent Moisture (ADB)": { "typical": "", "minMax": "" },
        "Net Calorific Value(ARB)": { "typical": "", "minMax": "" },
        "Ash(ARB)": { "typical": "", "minMax": "" },
        "Volatile Matter(ARB)": { "typical": "", "minMax": "" },
        "Sulfur(ARB)": { "typical": "", "minMax": "" },
        "Fixed Carbon (ADB)": { "typical": "", "minMax": "" },
        "Sizing": { "typical": "", "minMax": "" },
        "HGI": { "typical": "", "minMax": "" },
        "AFT (IDT, red atm)": { "typical": "", "minMax": "" }
      },
    },
    "Quality Settings": {
      "selected_preset": ""
    },
  });

  const clausesWithUneditableTextbox = [
    'Seller',
    'Buyer',
    'Incoterms',
    'Commodity',
    'Quantity',
    'Delivery',
    'Loading Port(s)',
    'Base Price',
    'Quality Adjustments',
    'Quality and Quantity Determination',
    'Payment',
    'Shipping Terms',
    'Other Terms',
    'Deal Validity'
  ];


  //useStates for dropdowns and textboxes
  const [selectedSeller, setSelectedSeller] = useState(''); // New state for selected seller
  const [sellerFormattedText, setSellerFormattedText] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState(''); // New state for selected buyer
  const [buyerFormattedText, setBuyerFormattedText] = useState('');
  const [selectedIncoterm, setSelectedIncoterm] = useState('')
  const [incotermFormattedText, setIncotermFormattedText] = useState('')
  const [selectedProduct, setSelectedProduct] = useState('');
  // The picked catalogue product's line name ("Steam Coal - RB1"): the first
  // line when the deal grows a second product (server: catalogue_product_label).
  const [selectedProductLabel, setSelectedProductLabel] = useState('');
  const [selectedProductVariantId, setSelectedProductVariantId] = useState(null);
  // Several products (go-live card 1248h535zfw): two or more lines, or none
  // (one product lives in the Product / Quantity / Price boxes, as before).
  const [offerProducts, setOfferProducts] = useState([]);
  // The recap pop-up's quantity per product, what is still free of each, and
  // what is left on the bid as a whole (it closes on its total).
  const [recapLineQuantities, setRecapLineQuantities] = useState([]);
  const [recapLineFree, setRecapLineFree] = useState([]);
  const [recapBidRemaining, setRecapBidRemaining] = useState(0);
  const [selectedOrigin, setSelectedOrigin] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [commodityFormattedText, setCommodityFormattedText] = useState('');
  const [quantityWeight, setQuantityWeight] = useState('');
  const [quantityFormattedText, setQuantityFormattedText] = useState('');
  const [quantityUnit, setQuantityUnit] = useState('');
  const [quantityTolerance, setQuantityTolerance] = useState('');
  const [quantityToleranceLowerValue, setQuantityToleranceLowerValue] = useState('');
  const [quantityToleranceLowerUnit, setQuantityToleranceLowerUnit] = useState('%');
  const [quantityToleranceUpperValue, setQuantityToleranceUpperValue] = useState('');
  const [quantityToleranceUpperUnit, setQuantityToleranceUpperUnit] = useState('%');
  const [quantityOption, setQuantityOption] = useState('');
  const [countries, setCountries] = useState([]);
  const [units, setUnits] = useState([]);
  const [delivery, setDelivery] = useState('');
  const [deliveryFormattedText, setDeliveryFormattedText] = useState('');
  const [laycanStartDate, setLaycanStartDate] = useState('');
  const [laycanEndDate, setLaycanEndDate] = useState('');
  const [loadingPort, setLoadingPort] = useState('');
  const [loadingPortFormattedText, setLoadingPortFormattedText] = useState('');
  const [priceType, setPriceType] = useState('');
  const [priceFormattedText, setPriceFormattedText] = useState('');
  const [basePriceCurrency, setBasePriceCurrency] = useState('');
  const [basePriceForFixedPrice, setBasePriceForFixedPrice] = useState('');
  const [basePriceForIndex, setBasePriceForIndex] = useState('');
  const [basePriceCVBasis, setBasePriceCVBasis] = useState('');
  const [basePriceKcalKg, setBasePriceKcalKg] = useState('');
  const [basePriceApplicableIndexPeriod, setBasePriceApplicableIndexPeriod] = useState('');
  const [qualityAdjustmentsProRata, setQualityAdjustmentsProRata] = useState(false);
  const [qualityAdjustmentsOtherPremiumPenalties, setQualityAdjustmentsOtherPremiumPenalties] = useState(false);
  const [qualityAdjustmentsStandardWording, setQualityAdjustmentsStandardWording] = useState(false);
  const [qualityAndQuantityDeterminationSurveyor, setQualityAndQuantityDeterminationSurveyor] = useState('');
  const [qualityQuantityFormattedText, setQualityQuantityFormattedText] = useState('');
  const [offerId, setOfferId] = useState(null);
  // Audit trail (Chris 2026-10-19): who created the deal and when, from the server.
  const [createdBy, setCreatedBy] = useState({ name: null, when: null });
  const [paymentWordingPresets, setPaymentWordingPresets] = useState([]);
  const [shippingWordingPresets, setShippingWordingPresets] = useState([]);
  const [otherTermsWordingPresets, setOtherTermsWordingPresets] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  // Pledge next to the payment terms: { is_pledged, pledge_bank_key, pledge_bank_name }.
  const [pledge, setPledge] = useState(() => pledgeOf(null));
  const [paymentWording, setPaymentWording] = useState('');
  const [selectedShippingMethod, setSelectedShippingMethod] = useState('');
  const [shippingWording, setShippingWording] = useState('');
  const [selectedOtherTermsMethod, setSelectedOtherTermsMethod] = useState('');
  const [otherTermsWording, setOtherTermsWording] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [showDraftPopup, setShowDraftPopup] = useState(false);
  const [isDraftedOffer, setIsDraftedOffer] = useState(false); // New state variable
  
  //Offer Validity Data
  const [offerValidityDate, setOfferValidityDate] = useState('');
  const [isDateValid, setIsDateValid] = useState(true);
  const [isTimeValid, setIsTimeValid] = useState(true);

  const getDefaultTime = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };
  const [offerValidityTime, setOfferValidityTime] = useState(getDefaultTime());

  const [offerValidityTimeZone, setOfferValidityTimeZone] = useState('');
  const [dealValidityFormattedText, setDealValidityFormattedText] = useState('');
  const commonTimezones = [
    { label: 'ECT +2, Berlin', value: 'Europe/Berlin' },  // HQ
    { label: 'UTC +0, London', value: 'Europe/London' },  // Europe
    { label: 'EST -5, New York', value: 'America/New_York' },  // USA (Eastern)
    { label: 'CST -6, Chicago', value: 'America/Chicago' },  // USA (Central)
    { label: 'PST -8, Los Angeles', value: 'America/Los_Angeles' },  // USA (Pacific)
    { label: 'BRT -3, São Paulo', value: 'America/Sao_Paulo' },  // South America (Colombia/Brazil)
    { label: 'SAST +2, Johannesburg', value: 'Africa/Johannesburg' },  // Africa (South Africa/Mozambique)
    { label: 'AST +3, Riyadh', value: 'Asia/Riyadh' },  // Middle East (Saudi Arabia)
    { label: 'JST +9, Tokyo', value: 'Asia/Tokyo' },  // Asia (Vietnam/Japan)
    { label: 'SGT +8, Singapore', value: 'Asia/Singapore' },  // Singapore/Indonesia
    { label: 'AEST +10, Sydney', value: 'Australia/Sydney' },  // Australia
    { label: 'CET +1, Warsaw', value: 'Europe/Warsaw' },  // Poland
    { label: 'MSK +3, Moscow', value: 'Europe/Moscow' }  // Russia
];
// Add this state to track updated fields
const [updatedFields, setUpdatedFields] = useState({});


  //useEffect hooks


  // Effect to fetch quality parameters from the API on component mount
  const [qualityParameters, setQualityParameters] = useState([]);
  const [qualityValues, setQualityValues] = useState({});
  const [qualityValuesSaved, setQualityValuesSaved] = useState([]);
  const [columnNames, setColumnNames] = useState({
      thirdColumn: 'Typical',
      fourthColumn: 'Min/Max'
  });
  
  const [qualityColumnHeader, setQualityColumnHeader] = useState('Typical'); // New state for column header
  const [minMaxColumnHeader, setMinMaxColumnHeader] = useState('Min/Max'); // New state for Min/Max/Rejection

  const [selectedOptions, setSelectedOptions] = useState({});

  //Trade Recap State
  const [showRecapPopup, setShowRecapPopup] = useState(false);

  // Add status state
  const [dealStatus, setDealStatus] = useState('');

  // First, add a new state to track if the component has been initially loaded
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    // Only process offerResponse on initial load
    if (isInitialLoad && offerResponse) {
      const newUpdatedFields = { ...updatedFields };
  
      // Extract and set deal type from offerResponse
      if (offerResponse.dealType) {
        const isDealTypeBid = offerResponse.dealType.toLowerCase() === 'bid';
        setIsDealTypeBid(isDealTypeBid);
        setDealType(isDealTypeBid ? 'Bid' : 'Offer');
      }

      if (offerResponse.Seller?.['Selling Entity']) {
        setSelectedSeller(offerResponse.Seller['Selling Entity']);
        newUpdatedFields['seller'] = true;
        setManuallySelectedSeller(false);
      }
  
      if (offerResponse.Buyer?.["Buyer's Entity"]) {
        setSelectedBuyer(offerResponse.Buyer["Buyer's Entity"]);
        newUpdatedFields['buyer'] = true;
        setManuallySelectedBuyer(false);
      }
  
      setUpdatedFields(newUpdatedFields);
      setIsInitialLoad(false); // Mark initial load as complete
    }
  }, [offerResponse]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axiosInstance.get('/api/offer/quality-parameters/');
        setQualityParameters(response.data);
  
        // Initialize selected options for parameters with options
        const initialOptions = {};
        response.data.forEach(param => {
          if (param.has_options && param.options.length > 0) {
            // Find the saved option for this parameter
            const savedOption = qualityValuesSaved.find(item => item.quality_parameter_id === param.id);
            if (savedOption) {
              initialOptions[param.id] = savedOption.selected_option;
            } else {
              // Default to the first option if no saved option is found
              initialOptions[param.id] = param.options[0].option_value;
            }
          }
        });
  
        setSelectedOptions(initialOptions);
        console.log('Selected Options:', initialOptions);
      } catch (error) {
        console.error('Error fetching quality parameters:', error);
      }
    };
    fetchData();
  }, [qualityValuesSaved]);
  useEffect(() => {
    //console.log('Resetting updatedFields because offerId changed');
    setUpdatedFields({});
    // Reset other manually selected states
    setManuallySelectedSeller(true);
    setManuallySelectedBuyer(true);
    setManuallySelectedIncoterm(true);
    setManuallySelectedProduct(true);
    setManuallySelectedOrigin(true);
    setManuallySelectedQuantityOption(true);
    setManuallySelectedPriceType(true);
    setManuallySelectedCurrency(true);
    setManuallySelectedCVBasis(true);
    setManuallySelectedProRata(true);
    setManuallySelectedPaymentMethod(true);
    setManuallySelectedShippingTerms(true);
    setManuallySelectedOtherTerms(true);
    setManuallySelectedTimeZone(true);
    
    // ... reset other manually selected fields
  }, [offerId]);

  // Check if the page has been reloaded
  const checkForPageReload = () => {
    console.log('Checking for page reload...');
    if (window.performance) {
      console.log('window.performance is available');
      if (window.performance.getEntriesByType) {
        console.log('Using getEntriesByType API');
        const navigation = window.performance.getEntriesByType("navigation");
        if (navigation.length > 0) {
          console.log('Navigation type:', navigation[0].type);
          const isReload = navigation[0].type === "reload";
          console.log('Is page reload?', isReload);
          return isReload;
        }
      }
      if (window.performance.navigation) {
        console.log('Using legacy navigation API');
        console.log('Performance navigation type:', window.performance.navigation.type);
        const isReload = window.performance.navigation.type === 1;
        console.log('Is page reload?', isReload); 
        return isReload;
      }
    }
    console.log('No performance API available, defaulting to false');
    return false;
  };


  useEffect(() => {
    // Reset updatedFields when the component mounts or when offerId changes
    setAssociatedTradeRecapId(null);

    const fetchData = async () => {
      // True until the deal named in the address is fully in the form, so a
      // failure anywhere before that (not the presets after it) is kept.
      let loadingAddressedDeal = Boolean(offerIdFromQuery);
      setDealLoadProblem(null);
      try {
        const isReload = checkForPageReload();
        let newOfferId = null;

        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        const userId = localStorage.getItem('user_id'); // Retrieve user ID from local storage
        console.log('User ID:', userId);
        if (!isLoggedIn || !userId) {
          history.push('/login');
          return;
        }
        //Offer Validity Data
        const now = new Date();
        const formattedDate = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()}`;
        setOfferValidityDate(formattedDate); // Prefill date
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const clausesResponse = await axiosInstance.get('/api/offer/clauses/');
        //console.log('Clauses response:', clausesResponse);
        const sortedClauses = clausesResponse.data.map(clause => {
          clause.components.sort((a, b) => {
            if (a.name === 'Payment Method' || a.name === 'Shipping Terms' || a.name === 'Other Terms') return -1;
            if (b.name === 'Payment Method' || b.name === 'Shipping Terms' || b.name === 'Other Terms') return 1;
            return 0;
          });
          clause.components.forEach(component => {
            if (component.name === 'Other Terms wording') {
              component.name = 'Other Terms Wording'; // Change this to whatever you want
            }
          });
          return clause;
        });

        setClauses(sortedClauses);

        if (offerIdFromQuery || (isGrouping && sourceOfferId))  {
          const idToFetch = offerIdFromQuery || sourceOfferId;
          console.log('Fetching offer data for ID:', idToFetch);
          // Rest of the existing data fetching code using idToFetch
          const offerResponse = await axiosInstance.get(`/api/offer/user-offers/${idToFetch}/`);
          console.log('Offer Response:', offerResponse.data);
          const fetchedOfferId = offerResponse.data.offer_id;
          if (offerIdFromQuery) {
            setDealCommodity(offerResponse.data.commodity_type || null);
          }
          setOfferId(fetchedOfferId);
          if (offerIdFromQuery) {
            setCreatedBy({
              name: offerResponse.data.created_by_name || null,
              when: offerResponse.data.offer_creation_timestamp || null,
            });
          }
          
          // Check for associated recap using the fetched ID directly (not state)
          try {
            const recapResponse = await axiosInstance.get(`/api/offer/trade-recaps/?offer_id=${fetchedOfferId}`);
            if (recapResponse.data.length > 0) {
              setAssociatedTradeRecapId(recapResponse.data[0].id);
              console.log('Found associated recap:', recapResponse.data[0].id, 'for offer:', fetchedOfferId);
            } else {
              setAssociatedTradeRecapId(null);
              console.log('No associated recap found for offer:', fetchedOfferId);
            }
          } catch (recapError) {
            console.error('Error fetching recap data:', recapError);
            setAssociatedTradeRecapId(null);
          }
          
          // Mark as existing in database since we're viewing an existing deal
          setIsDealInDatabase(true);
          
          setDealStatus(offerResponse.data.offer_current_status);
          setIsIndicationOnly(offerResponse.data.offer_is_indication_only || false); // Add this line to set indication toggle state
          setCustomDealName(offerResponse.data.deal_display_name || ''); // Load the custom deal name
          setAutomaticDealName(offerResponse.data.automatic_name);
          if (offerResponse.data.hamster_builder_log) {
            setHamsterBuilderLog(offerResponse.data.hamster_builder_log);
          }
          // The catalogue product, so a save sends it back (with several
          // products the server sets it from the first line), and the lines.
          if (offerResponse.data.stock_item_variant) {
            setSelectedProductVariantId(offerResponse.data.stock_item_variant);
          }
          setSelectedProductLabel(offerResponse.data.product_label || '');
          setOfferProducts(severalLines(offerResponse.data.products));
          
          // Fetch quality values using the new endpoint
          const qualityValuesResponse = await axiosInstance.get(`/api/offer/user-offers/${idToFetch}/quality-values/`);
          console.log('Quality Values Response:', qualityValuesResponse.data);
          setQualityValuesSaved(qualityValuesResponse.data);

          // Initialize qualityValues state
          const initialValues = {};
          qualityValuesResponse.data.forEach(item => {
            if (item.is_custom) {
              // For custom parameters, use custom_ID format and preserve property_name and unit
              const customId = `custom_${item.id}`;
              initialValues[customId] = {
                typical_or_guaranteed_value: item.typical_or_guaranteed_value,
                min_max_or_rejection_value: item.min_max_or_rejection_value,
                propertyName: item.property_name,
                unit: item.unit
              };
            } else {
              initialValues[item.quality_parameter_id] = {
                typical_or_guaranteed_value: item.typical_or_guaranteed_value,
                min_max_or_rejection_value: item.min_max_or_rejection_value
              };
            }
          });
          console.log('Initial Quality Values:', initialValues);
          setQualityValues(initialValues);

          if (qualityValuesResponse.data.length > 0) {
            setQualityColumnHeader(qualityValuesResponse.data[0].typical_or_guaranteed);
            setMinMaxColumnHeader(qualityValuesResponse.data[0].minmax_or_rejection);
          
            const options = {};
            qualityValuesResponse.data.forEach(item => {
              console.log('Processing quality value item:', item);
              // Map using quality_parameter_id instead of id
              if (item.selected_option) {
                options[item.quality_parameter_id] = item.selected_option;
              }
            });
            
            console.log('Final mapped options:', options);
            setSelectedOptions(options);
          }

          // Convert flattened data to clause_values format for transformOfferData
          const clauseValues = [
            { component: 1, text_value: offerResponse.data.selling_company_entity },
            { component: 2, text_value: offerResponse.data.buying_company_entity },
            { component: 3, text_value: offerResponse.data.trade_incoterm_type },
            { component: 4, text_value: offerResponse.data.commodity_product_type },
            { component: 5, text_value: offerResponse.data.commodity_origin_country },
            { component: 6, text_value: offerResponse.data.commodity_additional_details },
            { component: 32, text_value: offerResponse.data.commodity_custom_override },
            { component: 7, text_value: offerResponse.data.quantity_in_metric_tons },
            { component: 8, text_value: offerResponse.data.quantity_tolerance_percentage },
            { component: 9, text_value: offerResponse.data.quantity_option_holder },
            { component: 33, text_value: offerResponse.data.quantity_custom_override },
            { component: 10, text_value: offerResponse.data.delivery_terms_and_conditions },
            { component: 31, text_value: offerResponse.data.loading_ports_details },
            { component: 12, text_value: offerResponse.data.price_determination_type },
            { component: 13, text_value: offerResponse.data.price_currency_type },
            { component: 14, text_value: offerResponse.data.price_fixed_amount },
            { component: 15, text_value: offerResponse.data.price_premium_or_discount },
            { component: 16, text_value: offerResponse.data.price_cv_basis_type },
            { component: 17, text_value: offerResponse.data.price_kcal_per_kg },
            { component: 18, text_value: offerResponse.data.price_index_applicable_period },
            { component: 34, text_value: offerResponse.data.price_custom_override },
            { component: 21, text_value: offerResponse.data.payment_method_type },
            { component: 24, text_value: offerResponse.data.shipping_terms_type },
            { component: 26, text_value: offerResponse.data.other_terms_category },
            { component: 19, text_value: offerResponse.data.quality_prorata_cv_adjustments },
            { component: 20, text_value: offerResponse.data.quality_other_adjustments },
            { component: 35, text_value: offerResponse.data.quality_adjustments_custom_override },
            { component: 22, text_value: offerResponse.data.payment_detailed_terms },
            { component: 23, text_value: offerResponse.data.quality_quantity_surveyor_details },
            { component: 25, text_value: offerResponse.data.shipping_detailed_conditions },
            { component: 27, text_value: offerResponse.data.other_terms_detailed_conditions },
            { component: 28, text_value: offerResponse.data.deal_validity_date },
            { component: 29, text_value: offerResponse.data.deal_validity_time },
            { component: 30, text_value: offerResponse.data.deal_validity_timezone },
            { component: 36, text_value: offerResponse.data.quality_quantity_custom_override },
            { component: 37, text_value: offerResponse.data.discharging_ports_details },
          ].filter(clause => clause.text_value != null);

          // Transform clause_values to match offerData structure
          const transformedOfferData = transformOfferData(clauseValues);
          console.log('Transformed offer data:', transformedOfferData);

          setOfferData(transformedOfferData);
          console.log('Setting individual field values...');
          // Set saved dropdown and text values correctly so that clause overview displays the info
          setSelectedSeller(transformedOfferData.Seller['Selling Entity']);
          setSellerFormattedText(offerResponse.data.selling_company_formatted_text || '');
          setSelectedBuyer(transformedOfferData.Buyer['Buyer\'s Entity']);
          setBuyerFormattedText(offerResponse.data.buying_company_formatted_text || '');
          setSelectedIncoterm(transformedOfferData.Incoterms['Incoterm']);
          setIncotermFormattedText(offerResponse.data.trade_incoterm_formatted_text || '');
          setSelectedProduct(transformedOfferData.Commodity['Product']);
          setCommodityFormattedText(offerResponse.data.commodity_formatted_text || '');
          setSelectedOrigin(transformedOfferData.Commodity['Origin']);
          setAdditionalInfo(transformedOfferData.Commodity['Additional Information']);
          
          setQuantityWeight(transformedOfferData.Quantity['Quantity']);
          setQuantityUnit(offerResponse.data.quantity_unit || '');
          // Load tolerance from saved deal fields
          if (offerResponse.data.quantity_tolerance_lower_value || offerResponse.data.quantity_tolerance_upper_value) {
            setQuantityToleranceLowerValue(offerResponse.data.quantity_tolerance_lower_value || '');
            setQuantityToleranceLowerUnit(offerResponse.data.quantity_tolerance_lower_unit || '%');
            setQuantityToleranceUpperValue(offerResponse.data.quantity_tolerance_upper_value || '');
            setQuantityToleranceUpperUnit(offerResponse.data.quantity_tolerance_upper_unit || '%');
          } else if (offerResponse.data.quantity_tolerance_percentage) {
            const parsed = parseToleranceString(offerResponse.data.quantity_tolerance_percentage);
            setQuantityToleranceLowerValue(parsed.lowerValue);
            setQuantityToleranceLowerUnit(parsed.lowerUnit);
            setQuantityToleranceUpperValue(parsed.upperValue);
            setQuantityToleranceUpperUnit(parsed.upperUnit);
          }
          
          setQuantityOption(transformedOfferData.Quantity['Quantity Option']);
          setQuantityFormattedText(offerResponse.data.quantity_formatted_text || '');
          setDelivery(transformedOfferData.Delivery['Delivery']);
          setDeliveryFormattedText(offerResponse.data.delivery_formatted_text || '');
          
          // Use explicit laycan fields if available, fall back to regex parsing
          if (transformedOfferData.Delivery['Laycan Start'] || transformedOfferData.Delivery['Laycan End']) {
            setLaycanStartDate(transformedOfferData.Delivery['Laycan Start'] || '');
            setLaycanEndDate(transformedOfferData.Delivery['Laycan End'] || '');
          } else {
            const deliveryText = transformedOfferData.Delivery['Delivery'];
            if (deliveryText) {
              const laycanMatch = deliveryText.match(/Tentative loading laycan:\s*(\S+)\s+to\s+(\S+)/);
              if (laycanMatch) {
                setLaycanStartDate(laycanMatch[1]);
                setLaycanEndDate(laycanMatch[2]);
              }
            }
          }
          
          // Set the port based on incoterm - either use loading port or discharging port
          if (transformedOfferData.Incoterms.Incoterm === 'FOB') {
            setLoadingPort(transformedOfferData['Loading Port(s)']['Loading Port(s)']);
          } else {
            // For CIF/CFR, use discharging port
            setLoadingPort(transformedOfferData['Discharging Port(s)']['Discharging Port(s)']);
          }
          setLoadingPortFormattedText(offerResponse.data.loading_ports_formatted_text || '');
          
          setQualityAdjustmentsProRata(transformedOfferData['Quality Adjustments']['Pro-rata CV adjustments'] === 'Yes');
          setQualityAdjustmentsOtherPremiumPenalties(transformedOfferData['Quality Adjustments']['Other Premium/Penalties']);
          setQualityAdjustmentsStandardWording(transformedOfferData['Quality Adjustments']['Standard Wording']);
          setQualityAndQuantityDeterminationSurveyor(transformedOfferData['Quality and Quantity Determination']?.['Surveyor(s) or leave empty for generic wording'] || '');
          setQualityQuantityFormattedText(offerResponse.data.quality_quantity_formatted_text || '');
          setOfferValidityDate(transformedOfferData['Deal Validity']?.['Date'] || '');
          setOfferValidityTime(transformedOfferData['Deal Validity']?.['Time'] || '');
          setOfferValidityTimeZone(transformedOfferData['Deal Validity']?.['Time-Zone'] || '');
          setDealValidityFormattedText(offerResponse.data.deal_validity_formatted_text || '');
          setPriceType(transformedOfferData['Base Price']?.['Price type'] || '');
          setBasePriceCurrency(transformedOfferData['Base Price']?.['Currency'] || '');
          setBasePriceForFixedPrice(transformedOfferData['Base Price']?.['For Fixed Price'] || '');
          setBasePriceForIndex(transformedOfferData['Base Price']?.['For Index: Premium/Discount'] || '');
          setBasePriceCVBasis(transformedOfferData['Base Price']?.['CV Basis'] || '');
          setBasePriceKcalKg(transformedOfferData['Base Price']?.['Kcal/Kg'] || '');
          setBasePriceApplicableIndexPeriod(transformedOfferData['Base Price']?.['Applicable Index Period'] || '');
          setPriceFormattedText(offerResponse.data.price_formatted_text || '');
          setPaymentWording(transformedOfferData['Payment']?.['Payment Wording'] || '');
          setShippingWording(transformedOfferData['Shipping Terms']?.['Shipping Terms Wording'] || '');
          setOtherTermsWording(transformedOfferData['Other Terms']?.['Other Terms Wording'] || '');
          setIsDraftedOffer(true);

          // Set the wording values directly for a drafted offer
          setPaymentWording(offerResponse.data.payment_detailed_terms || '');
          setShippingWording(offerResponse.data.shipping_detailed_conditions || '');
          setOtherTermsWording(offerResponse.data.other_terms_detailed_conditions || '');
          
          // First get the presets
          const paymentPresetsResponse = await axiosInstance.get('/api/offer/payment_presets/');
          const shippingPresetsResponse = await axiosInstance.get('/api/offer/shipping_presets/');
          const otherTermsPresetsResponse = await axiosInstance.get('/api/offer/other_terms_presets/');

          // Store presets
          setPaymentWordingPresets(paymentPresetsResponse.data);
          setShippingWordingPresets(shippingPresetsResponse.data);
          setOtherTermsWordingPresets(otherTermsPresetsResponse.data);

          // Get both methods and stored wordings from clause_values
          const paymentMethod = offerResponse.data.payment_method_type;
          const storedPaymentWording = offerResponse.data.payment_detailed_terms;
          
          const shippingMethod = offerResponse.data.shipping_terms_type;
          const storedShippingWording = offerResponse.data.shipping_detailed_conditions;
          
          const otherTermsMethod = offerResponse.data.other_terms_category;
          const storedOtherTermsWording = offerResponse.data.other_terms_detailed_conditions;

          // Set the methods
          setSelectedPaymentMethod(offerResponse.data.payment_method_type || '');
          setPledge(pledgeOf(offerResponse.data));
          setSelectedShippingMethod(offerResponse.data.shipping_terms_type || '');
          setSelectedOtherTermsMethod(offerResponse.data.other_terms_category || '');

          // For each type, use stored wording if it exists, otherwise use preset wording
          if (paymentMethod) {
            if (storedPaymentWording) {
              setPaymentWording(storedPaymentWording);
              // Check if this wording matches any preset
              const matchingPreset = paymentPresetsResponse.data.find(
                preset => preset.wording === storedPaymentWording
              );
              // If no matching preset, it's likely custom wording
              if (!matchingPreset && storedPaymentWording.trim()) {
                setSelectedPaymentMethod('Custom Wording');
              }
            } else {
              // Match on term code OR legacy name: since the Payment Terms
              // catalogues merged, a recap can hand this field a term code
              // ("NET45") where it used to always be a clause name.
              setPaymentWording(paymentTermWording(paymentPresetsResponse.data, paymentMethod));
            }
          }

          if (shippingMethod) {
            if (storedShippingWording) {
              setShippingWording(storedShippingWording);
              // Check if this wording matches any preset
              const matchingPreset = shippingPresetsResponse.data.find(preset => {
                // Process the preset to compare it properly
                const processedPresetWording = preset.wording
                  .split('\n')
                  .slice(2) // Skip the heading and the empty line after it
                  .join('\n')
                  .trim();
                return processedPresetWording === storedShippingWording;
              });
              // If no matching preset, it's likely custom wording
              if (!matchingPreset && storedShippingWording.trim()) {
                setSelectedShippingMethod('Custom Wording');
              }
            } else {
              const shippingPreset = shippingPresetsResponse.data.find(preset => preset.name === shippingMethod);
              setShippingWording(shippingPreset?.wording || '');
            }
          }

          if (otherTermsMethod) {
            if (storedOtherTermsWording) {
              setOtherTermsWording(storedOtherTermsWording);
              // Check if this wording matches any preset
              const matchingPreset = otherTermsPresetsResponse.data.find(preset => {
                // Process the preset to compare it properly
                const processedPresetWording = preset.wording
                  .split('\n')
                  .slice(2) // Skip the heading and the empty line after it
                  .join('\n')
                  .trim();
                return processedPresetWording === storedOtherTermsWording;
              });
              // If no matching preset, it's likely custom wording
              if (!matchingPreset && storedOtherTermsWording.trim()) {
                setSelectedOtherTermsMethod('Custom Wording');
              }
            } else {
              const otherTermsPreset = otherTermsPresetsResponse.data.find(preset => preset.name === otherTermsMethod);
              setOtherTermsWording(otherTermsPreset?.wording || '');
            }
          }
          loadingAddressedDeal = false;
        } else {
          // For new deals, don't create in the database yet, just initialize the form
          console.log('Initializing new offer form without saving to database');
          
          try {
            // Fetch prefilled data from the backend for new offers
            const prefilledDataResponse = await axiosInstance.get('/api/offer/prefilled-data/');
            const prefilled_data = prefilledDataResponse.data;
            console.log('Received prefilled data:', prefilled_data);
            
            // Set the form data with prefilled values
            setOfferData(prefilled_data);
            
            // Set individual fields from prefilled data
            if (prefilled_data.Seller?.['Selling Entity']) {
              setSelectedSeller(prefilled_data.Seller['Selling Entity']);
            }
            
            if (prefilled_data.Buyer?.["Buyer's Entity"]) {
              setSelectedBuyer(prefilled_data.Buyer["Buyer's Entity"]);
            }
            
            if (prefilled_data.Incoterms?.['Incoterm']) {
              setSelectedIncoterm(prefilled_data.Incoterms['Incoterm']);
              // Update port type based on incoterm
              setPortType(prefilled_data.Incoterms['Incoterm'] === 'FOB' ? 'Loading Port(s)' : 'Discharging Port(s)');
            }
            
            if (prefilled_data.Commodity?.['Product']) {
              setSelectedProduct(prefilled_data.Commodity['Product']);
            }
            
            if (prefilled_data.Commodity?.['Origin']) {
              setSelectedOrigin(prefilled_data.Commodity['Origin']);
            }
            
            if (prefilled_data.Commodity?.['Additional Information']) {
              setAdditionalInfo(prefilled_data.Commodity['Additional Information']);
            }
            
            if (prefilled_data.Quantity?.['Quantity']) {
              setQuantityWeight(prefilled_data.Quantity['Quantity']);
            }
            
            if (prefilled_data['Tolerance Upper'] !== undefined || prefilled_data['Tolerance Lower'] !== undefined) {
              setQuantityToleranceUpperValue(String(prefilled_data['Tolerance Upper'] || ''));
              setQuantityToleranceUpperUnit(prefilled_data['Tolerance Upper Unit'] || '%');
              setQuantityToleranceLowerValue(String(prefilled_data['Tolerance Lower'] || ''));
              setQuantityToleranceLowerUnit(prefilled_data['Tolerance Lower Unit'] || '%');
            }
            
            if (prefilled_data.Quantity?.['Quantity Option']) {
              setQuantityOption(prefilled_data.Quantity['Quantity Option']);
            }
            
            if (prefilled_data.Delivery?.['Delivery']) {
              setDelivery(prefilled_data.Delivery['Delivery']);
            }
            // Use explicit laycan fields if available, fall back to regex parsing
            if (prefilled_data.Delivery?.['Laycan Start'] || prefilled_data.Delivery?.['Laycan End']) {
              setLaycanStartDate(prefilled_data.Delivery['Laycan Start'] || '');
              setLaycanEndDate(prefilled_data.Delivery['Laycan End'] || '');
            } else if (prefilled_data.Delivery?.['Delivery']) {
              const deliveryText = prefilled_data.Delivery['Delivery'];
              if (deliveryText) {
                const laycanMatch = deliveryText.match(/Tentative loading laycan:\s*(\S+)\s+to\s+(\S+)/);
                if (laycanMatch) {
                  setLaycanStartDate(laycanMatch[1]);
                  setLaycanEndDate(laycanMatch[2]);
                }
              }
            }
            
            if (prefilled_data['Loading Port(s)']?.['Loading Port(s)']) {
              setLoadingPort(prefilled_data['Loading Port(s)']['Loading Port(s)']);
            }
            
            if (prefilled_data['Base Price']?.['Price type']) {
              setPriceType(prefilled_data['Base Price']['Price type']);
            }
            
            if (prefilled_data['Base Price']?.['Currency']) {
              setBasePriceCurrency(prefilled_data['Base Price']['Currency']);
            }
            
            if (prefilled_data['Base Price']?.['For Fixed Price']) {
              setBasePriceForFixedPrice(prefilled_data['Base Price']['For Fixed Price']);
            }
            
            if (prefilled_data['Base Price']?.['For Index: Premium/Discount']) {
              setBasePriceForIndex(prefilled_data['Base Price']['For Index: Premium/Discount']);
            }
            
            if (prefilled_data['Base Price']?.['CV Basis']) {
              setBasePriceCVBasis(prefilled_data['Base Price']['CV Basis']);
            }
            
            if (prefilled_data['Base Price']?.['Kcal/Kg']) {
              setBasePriceKcalKg(prefilled_data['Base Price']['Kcal/Kg']);
            }
            
            if (prefilled_data['Base Price']?.['Applicable Index Period']) {
              setBasePriceApplicableIndexPeriod(prefilled_data['Base Price']['Applicable Index Period']);
            }
            
            if (prefilled_data['Quality Adjustments']?.['Pro-rata CV adjustments']) {
              setQualityAdjustmentsProRata(prefilled_data['Quality Adjustments']['Pro-rata CV adjustments'] === 'Yes');
            }
            
            if (prefilled_data['Quality Adjustments']?.['Other Premium/Penalties']) {
              setQualityAdjustmentsOtherPremiumPenalties(prefilled_data['Quality Adjustments']['Other Premium/Penalties']);
            }
            
            if (prefilled_data['Quality Adjustments']?.['Standard Wording']) {
              setQualityAdjustmentsStandardWording(prefilled_data['Quality Adjustments']['Standard Wording']);
            }
            
            if (prefilled_data['Payment']?.['Payment Method']) {
              setSelectedPaymentMethod(prefilled_data['Payment']['Payment Method']);
            }
            
            if (prefilled_data['Shipping Terms']?.['Shipping Terms']) {
              setSelectedShippingMethod(prefilled_data['Shipping Terms']['Shipping Terms']);
            }
            
            if (prefilled_data['Other Terms']?.['Other Terms']) {
              setSelectedOtherTermsMethod(prefilled_data['Other Terms']['Other Terms']);
            }
            
            if (prefilled_data['Quality and Quantity Determination']?.['Surveyor(s) or leave empty for generic wording']) {
              setQualityAndQuantityDeterminationSurveyor(prefilled_data['Quality and Quantity Determination']['Surveyor(s) or leave empty for generic wording']);
            }
          } catch (error) {
            console.error('Error fetching prefilled data:', error);
            // Fallback to basic defaults if the API call fails
            const defaultFormData = {
              'Seller': { 'Selling Entity': '' },
              'Buyer': { "Buyer's Entity": '' },
              'Incoterms': { 'Incoterm': '' },
              'Commodity': { 'Product': '', 'Origin': '', 'Additional Information': '' },
              'Quantity': { 'Quantity': '', 'Tolerance': '', 'Quantity Option': '' },
              'Delivery': { 'Delivery': '' },
              'Loading Port(s)': { 'Loading Port(s)': '' },
              'Discharging Port(s)': { 'Discharging Port(s)': '' },
              'Base Price': { 
                'Price type': '', 
                'Currency': '', 
                'For Fixed Price': '', 
                'For Index: Premium/Discount': '',
                'CV Basis': '',
                'Kcal/Kg': '',
                'Applicable Index Period': '' 
              },
              'Quality Adjustments': {
                'Pro-rata CV adjustments': 'No',
                'Other Premium/Penalties': '',
                'Standard Wording': ''
              },
              'Quality and Quantity Determination': {
                'Surveyor(s) or leave empty for generic wording': ''
              },
              'Payment': { 'Payment Method': '', 'Payment Wording': '' },
              'Shipping Terms': { 'Shipping Terms': '', 'Shipping Terms Wording': '' },
              'Other Terms': { 'Other Terms': '', 'Other Terms Wording': '' },
              'Deal Validity': { 'Date': formattedDate, 'Time': oneHourLater.toTimeString().split(' ')[0].substring(0, 5), 'Time-Zone': timezone }
            };
            
            // Set the form data with default values
            setOfferData(defaultFormData);
            
            // Set offer validity data
            setOfferValidityDate(formattedDate); // Prefill date
            setOfferValidityTime(oneHourLater.toTimeString().split(' ')[0].substring(0, 5)); // Prefill time
            setOfferValidityTimeZone(timezone); // Prefill timezone
          }
        }

        const paymentPresetsResponse = await axiosInstance.get('/api/offer/payment_presets/');
        setPaymentWordingPresets(paymentPresetsResponse.data);

        const shippingPresetsResponse = await axiosInstance.get('/api/offer/shipping_presets/');
        setShippingWordingPresets(shippingPresetsResponse.data);

        const otherTermsPresetsResponse = await axiosInstance.get('/api/offer/other_terms_presets/');
        setOtherTermsWordingPresets(otherTermsPresetsResponse.data);

        // Set form as loaded after all data is fetched and state is set
        setIsFormLoaded(true);

      } catch (error) {
        console.error('Error fetching data:', error);
        if (loadingAddressedDeal) {
          setDealLoadProblem(describeLoadFailure(error));
        }
      }
    };
    fetchData();
  }, [history, location.search, dealLoadAttempt]);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        if (offerId) {
          const recapResponse = await axiosInstance.get(`/api/offer/trade-recaps/?offer_id=${offerId}`);
          if (cancelled) return;
          if (recapResponse.data.length > 0) {
            setAssociatedTradeRecapId(recapResponse.data[0].id);
            console.log('Found associated recap:', recapResponse.data[0].id);
          } else {
            setAssociatedTradeRecapId(null);
            console.log('No associated recap found');
          }
        } else {
          setAssociatedTradeRecapId(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching recap data:', error);
        }
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [offerId]);


  //Transform offer data to match the API format
  const transformOfferData = (clauseValues, qualityValues, qualitySettings) => {
    const transformedData = {
      "Seller": { "Selling Entity": "" },
      "Buyer": { "Buyer's Entity": "" },
      "Incoterms": { "Incoterm": "" },
      "Commodity": {},
      "Quantity": {},
      "Delivery": {},
      "Loading Port(s)": {},
      "Discharging Port(s)": {},
      "Quality": {
        "Quality Specifications": {
          "Total Moisture (ARB)": { "typical": "", "minMax": "" },
          "Inherent Moisture (ADB)": { "typical": "", "minMax": "" },
          "Net Calorific Value(ARB)": { "typical": "", "minMax": "" },
          "Ash(ARB)": { "typical": "", "minMax": "" },
          "Volatile Matter(ARB)": { "typical": "", "minMax": "" },
          "Sulfur(ARB)": { "typical": "", "minMax": "" },
          "Fixed Carbon (ADB)": { "typical": "", "minMax": "" },
          "Sizing": { "typical": "", "minMax": "" },
          "HGI": { "typical": "", "minMax": "" },
          "AFT (IDT, red atm)": { "typical": "", "minMax": "" }
        },
        "selected_preset": qualitySettings?.selected_preset || ""
      },
      // "Quality Settings": {
      //   "selected_preset": qualitySettings?.selected_preset || ""
      // },
      "Base Price": {},
      "Quality Adjustments": {},
      "Payment": {},
      "Quality and Quantity Determination": {},
      "Shipping Terms": {},
      "Other Terms": {},
      "Deal Validity": { "Date": "", "Time": "", "Time-Zone": "" }
    };

    clauseValues.forEach(clause => {
      // console.log(clause.component);
      // console.log(clause.text_value);
      switch (clause.component) {
        
        case 1:
          transformedData["Seller"]["Selling Entity"] = clause.text_value;
          break;
        case 2:
          transformedData["Buyer"]["Buyer's Entity"] = clause.text_value;
          break;
        case 3:
          transformedData["Incoterms"]["Incoterm"] = clause.text_value;
          break;
        case 4:
          transformedData["Commodity"]["Product"] = clause.text_value;
          break;
        case 5:
          transformedData["Commodity"]["Origin"] = clause.text_value;
          break;
        case 6:
          transformedData["Commodity"]["Additional Information"] = clause.text_value;
          break;
        case 32:
          transformedData["Commodity"]["Overwrite Commodity"] = clause.text_value;
          break;
        case 7:
          transformedData["Quantity"]["Quantity"] = clause.text_value;
          break;
        case 8:
          transformedData["Quantity"]["Tolerance"] = clause.text_value;
          break;
        case 9:
          transformedData["Quantity"]["Quantity Option"] = clause.text_value;
          break;
        case 33:
            transformedData["Quantity"]["Overwrite Quantity"] = clause.text_value;
            break;
        case 10:
          transformedData["Delivery"]["Delivery"] = clause.text_value;
          break;
        case 31:
          transformedData["Loading Port(s)"]["Loading Port(s)"] = clause.text_value;
          break;
        case 37: // Add a new component number for discharging ports
          transformedData["Discharging Port(s)"]["Discharging Port(s)"] = clause.text_value;
          break;

        case 12:
          transformedData["Base Price"]["Price type"] = clause.text_value;
          break;
        case 13:
          transformedData["Base Price"]["Currency"] = clause.text_value;
          break;
        case 14:
          transformedData["Base Price"]["For Fixed Price"] = clause.text_value;
          break;
        case 15:
          transformedData["Base Price"]["For Index: Premium/Discount"] = clause.text_value;
          break;
        case 16:
          transformedData["Base Price"]["CV Basis"] = clause.text_value;
          break;
        case 17:
          transformedData["Base Price"]["Kcal/Kg"] = clause.text_value;
          break;
        case 18:
          transformedData["Base Price"]["Applicable Index Period"] = clause.text_value;
          break;
        case 34:
          transformedData["Base Price"]["Overwrite Base Price"] = clause.text_value;
          break;
        case 19:
          transformedData["Quality Adjustments"]["Pro-rata CV adjustments"] = clause.text_value;
          break;
        case 20:
          transformedData["Quality Adjustments"]["Other Premium/Penalties"] = clause.text_value;
          break;
        case 35:
          transformedData["Quality Adjustments"]["Overwrite Quality Adjustments"] = clause.text_value;
          break;
        case 21:
          transformedData["Payment"]["Payment Method"] = clause.text_value;
          break;
        case 22:
          transformedData["Payment"]["Payment Wording"] = clause.text_value;
          break;
        case 23:
          transformedData["Quality and Quantity Determination"]["Surveyor(s) or leave empty for generic wording"] = clause.text_value;
          break;
        case 36:
          transformedData["Quality and Quantity Determination"]["Overwrite Quality and Quantity Determination"] = clause.text_value;
          break;

        case 24:
          transformedData["Shipping Terms"]["Shipping Terms"] = clause.text_value;
          break;
        case 25:
          transformedData["Shipping Terms"]["Shipping Terms Wording"] = clause.text_value;
          break;
        case 26:
          transformedData["Other Terms"]["Other Terms"] = clause.text_value;
          break;
        case 27:
          transformedData["Other Terms"]["Other Terms Wording"] = clause.text_value;
          break;
        case 28:
          transformedData["Deal Validity"]["Date"] = clause.text_value;
          break;
        case 29:
          transformedData["Deal Validity"]["Time"] = clause.text_value;
          break;
        case 30:
          transformedData["Deal Validity"]["Time-Zone"] = clause.text_value;
          break;
          

        default:
          break;
      }
    });
    return transformedData;
  };

  //Handle preset selection
  useEffect(() => {
    if (selectedPaymentMethod && !isDraftedOffer) {
      // Don't override the wording if it's a custom wording option
      if (selectedPaymentMethod === 'Custom Wording') {
        // If it's a custom wording option, we keep the existing wording
        return;
      }
      
      setPaymentWording(paymentTermWording(paymentWordingPresets, selectedPaymentMethod));
    }
  }, [selectedPaymentMethod, paymentWordingPresets, isDraftedOffer]);
  
  //Handle shipping method selection
  useEffect(() => {
    if (selectedShippingMethod && !isDraftedOffer) {
      // Don't override the wording if it's a custom wording option
      if (selectedShippingMethod === 'Custom Wording') {
        // If it's a custom wording option, we keep the existing wording
        return;
      }
      
      const selectedPreset = shippingWordingPresets.find(preset => preset.name === selectedShippingMethod);
      if (selectedPreset) {
        // Remove the heading and any empty lines at the start of the wording
        const wording = selectedPreset.wording
          .split('\n')
          .slice(2) // Skip the heading and the empty line after it
          .join('\n')
          .trim();
        setShippingWording(wording);
      }
    }
  }, [selectedShippingMethod, shippingWordingPresets, isDraftedOffer]);
  
  // Extract custom quality rows when qualityValues changes
  useEffect(() => {
    if (Object.keys(qualityValues).length > 0) {
      extractCustomQualityRows();
    }
  }, [qualityValues]);
  
  //Handle other terms method selection
  useEffect(() => {
    if (selectedOtherTermsMethod && !isDraftedOffer) {
      // Don't override the wording if it's a custom wording option
      if (selectedOtherTermsMethod === 'Custom Wording') {
        // If it's a custom wording option, we keep the existing wording
        return;
      }
      
      const selectedPreset = otherTermsWordingPresets.find(preset => preset.name === selectedOtherTermsMethod);
      if (selectedPreset) {
        // Remove the heading and any empty lines at the start of the wording
        const wording = selectedPreset.wording
          .split('\n')
          .slice(2) // Skip the heading and the empty line after it
          .join('\n')
          .trim();
        setOtherTermsWording(wording);
      }
    }
  }, [selectedOtherTermsMethod, otherTermsWordingPresets, isDraftedOffer]);
  


// Add this state to track updated fields at the top of your component
useEffect(() => {
  if (offerResponse) {
    // Append AI extraction to the Hamster builder log
    setHamsterBuilderLog(prev => [...prev, {
      timestamp: new Date().toISOString(),
      extraction: offerResponse
    }]);

    // Recursive function to merge non-null values
    const mergeNonNull = (target, source) => {
      Object.keys(source).forEach((key) => {
        if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          // If the key is an object, recurse
          if (!target[key]) {
            target[key] = {}; // Ensure the target has the key as an object
          }
          mergeNonNull(target[key], source[key]);
        } else if (source[key] !== null) {
          // Otherwise, directly assign the value
          target[key] = source[key];
        }
      });
    };

    // Create a deep copy of the existing offerData
    const updatedOfferData = { ...offerData };

    // Merge offerResponse into offerData
    mergeNonNull(updatedOfferData, offerResponse);

    //console.log('Updated offer data:', updatedOfferData);

    // Update the offerData state with the merged data
    setOfferData(updatedOfferData);

    // Update individual field states only if they are present in the offerResponse
    const newUpdatedFields = {};

    if (offerResponse.Seller?.['Selling Entity']) {
      setSelectedSeller(offerResponse.Seller['Selling Entity']);
      newUpdatedFields['selectedSeller'] = true;
      setManuallySelectedSeller(false);
    }

    if (offerResponse.Buyer?.["Buyer's Entity"]) {
      setSelectedBuyer(offerResponse.Buyer["Buyer's Entity"]);
      newUpdatedFields['selectedBuyer'] = true;
      setManuallySelectedBuyer(false); // Reset manual selection
    }

    if (offerResponse.Incoterms?.Incoterm) {
      setSelectedIncoterm(offerResponse.Incoterms.Incoterm);
      newUpdatedFields['selectedIncoterm'] = true;
      setManuallySelectedIncoterm(false);
      
      // Update port type based on Incoterm when changed via chat window
      const newPortType = offerResponse.Incoterms.Incoterm === 'FOB' ? 'Loading Port(s)' : 'Discharging Port(s)';
      setPortType(newPortType);
    }

    if (offerResponse.Commodity?.Product) {
      setSelectedProduct(offerResponse.Commodity.Product);
      newUpdatedFields['selectedProduct'] = true;
      setManuallySelectedProduct(false);
    }

    if (offerResponse.Commodity?.Origin) {
      setSelectedOrigin(offerResponse.Commodity.Origin);
      newUpdatedFields['selectedOrigin'] = true;
      setManuallySelectedOrigin(false);
    }

    if (offerResponse.Commodity?.['Additional Information']) {
      setAdditionalInfo(offerResponse.Commodity['Additional Information']);
      newUpdatedFields['additionalInfo'] = true;
      setManuallySelectedCommodityAdditionalInfo(false);
    }

    // Support both old and new field names for backward compatibility
    const quantityValue = offerResponse.Quantity?.['Quantity'] || offerResponse.Quantity?.['Quantity (metric tons)'];
    if (quantityValue) {
      setQuantityWeight(quantityValue);
      newUpdatedFields['quantityWeight'] = true;
      setManuallySelectedQuantityWeight(false);
    }

    if (offerResponse['Tolerance Upper'] !== undefined || offerResponse['Tolerance Lower'] !== undefined) {
      setManuallySelectedQuantityTolerance(false);
      newUpdatedFields['quantityTolerance'] = true;
      if (offerResponse['Tolerance Upper'] !== undefined && offerResponse['Tolerance Upper'] !== null) {
        setQuantityToleranceUpperValue(String(offerResponse['Tolerance Upper']));
        setQuantityToleranceUpperUnit(offerResponse['Tolerance Upper Unit'] || '%');
        newUpdatedFields['quantityToleranceUpperValue'] = true;
      }
      if (offerResponse['Tolerance Lower'] !== undefined && offerResponse['Tolerance Lower'] !== null) {
        setQuantityToleranceLowerValue(String(offerResponse['Tolerance Lower']));
        setQuantityToleranceLowerUnit(offerResponse['Tolerance Lower Unit'] || '%');
        newUpdatedFields['quantityToleranceLowerValue'] = true;
      }
    }

    if (offerResponse.Quantity?.['Quantity Option']) {
      setQuantityOption(offerResponse.Quantity['Quantity Option']);
      newUpdatedFields['quantityOption'] = true;
      setManuallySelectedQuantityOption(false);
    }

    if (offerResponse.Quantity?.['Unit']) {
      const extractedUnit = offerResponse.Quantity['Unit'];
      // STRICT: Only set unit if it exists in the database units array
      const matchingUnit = units.find(u => 
        u.code.toLowerCase() === extractedUnit.toLowerCase()
      );
      if (matchingUnit) {
        setQuantityUnit(matchingUnit.code);
        newUpdatedFields['quantityUnit'] = true;
      }
      // If no match, unit remains empty - user must select manually
    }

    if (offerResponse.Delivery?.Delivery) {
      setDelivery(offerResponse.Delivery.Delivery);
      newUpdatedFields['delivery'] = true;
      setManuallySelectedDelivery(false);
    }

    // Use explicit laycan fields from AI response, fall back to regex parsing
    if (offerResponse.Delivery?.['Laycan Start'] || offerResponse.Delivery?.['Laycan End']) {
      setLaycanStartDate(offerResponse.Delivery['Laycan Start'] || '');
      setLaycanEndDate(offerResponse.Delivery['Laycan End'] || '');
      newUpdatedFields['laycanStartDate'] = true;
      newUpdatedFields['laycanEndDate'] = true;
    } else if (offerResponse.Delivery?.Delivery) {
      const deliveryText = offerResponse.Delivery.Delivery;
      if (deliveryText) {
        const laycanMatch = deliveryText.match(/Tentative loading laycan:\s*(\S+)\s+to\s+(\S+)/);
        if (laycanMatch) {
          setLaycanStartDate(laycanMatch[1]);
          setLaycanEndDate(laycanMatch[2]);
        }
      }
    }

    if (offerResponse['Loading Port(s)']?.['Loading Port(s)']) {
      setLoadingPort(offerResponse['Loading Port(s)']['Loading Port(s)']);
      newUpdatedFields['loadingPort'] = true;
      setManuallySelectedLoadingPort(false);
      
      // Update offerData with the loading port information
      setOfferData(prevData => ({
        ...prevData,
        'Loading Port(s)': {
          ...prevData['Loading Port(s)'],
          'Loading Port(s)': offerResponse['Loading Port(s)']['Loading Port(s)']
        }
      }));
    }

    if (offerResponse['Discharging Port(s)']?.['Discharging Port(s)']) {
      setLoadingPort(offerResponse['Discharging Port(s)']['Discharging Port(s)']);
      newUpdatedFields['loadingPort'] = true;
      setManuallySelectedLoadingPort(false);
      
      // Update offerData with the discharging port information
      setOfferData(prevData => ({
        ...prevData,
        'Loading Port(s)': {
          ...prevData['Loading Port(s)'],
          'Loading Port(s)': offerResponse['Discharging Port(s)']['Discharging Port(s)']
        }
      }));
    }

    if (offerResponse['Base Price']?.['Price type']) {
      setPriceType(offerResponse['Base Price']['Price type']);
      newUpdatedFields['priceType'] = true;
      setManuallySelectedPriceType(false);
    }

    if (offerResponse['Base Price']?.['Currency']) {
      setBasePriceCurrency(offerResponse['Base Price']['Currency']);
      newUpdatedFields['basePriceCurrency'] = true;
      setManuallySelectedCurrency(false);
    }

    if (offerResponse['Base Price']?.['For Fixed Price']) {
      setBasePriceForFixedPrice(offerResponse['Base Price']['For Fixed Price']);
      newUpdatedFields['basePriceForFixedPrice'] = true;
      setManuallySelectedBasePriceForFixedPrice(false);
    }

    if (offerResponse['Base Price']?.['For Index: Premium/Discount']) {
      setBasePriceForIndex(offerResponse['Base Price']['For Index: Premium/Discount']);
      newUpdatedFields['basePriceForIndex'] = true;
      setManuallySelectedBasePriceForIndexPremiumDiscount(false);
    }

    if (offerResponse['Base Price']?.['CV Basis']) {
      setBasePriceCVBasis(offerResponse['Base Price']['CV Basis']);
      newUpdatedFields['basePriceCVBasis'] = true;
      setManuallySelectedCVBasis(false);
    }

    if (offerResponse['Base Price']?.['Kcal/Kg']) {
      setBasePriceKcalKg(offerResponse['Base Price']['Kcal/Kg']);
      newUpdatedFields['basePriceKcalKg'] = true;
      setManuallySelectedBasePriceKcalKg(false);
    }

    if (offerResponse['Base Price']?.['Applicable Index Period']) {
      setBasePriceApplicableIndexPeriod(offerResponse['Base Price']['Applicable Index Period']);
      newUpdatedFields['basePriceApplicableIndexPeriod'] = true;
      setManuallySelectedBasePriceApplicableIndexPeriod(false);
    }

    if (offerResponse['Quality Adjustments']?.['Pro-rata CV adjustments']) {
      setQualityAdjustmentsProRata(offerResponse['Quality Adjustments']['Pro-rata CV adjustments']);
      newUpdatedFields['qualityAdjustmentsProRata'] = true;
      setManuallySelectedProRata(false);
    }

    if (offerResponse['Quality Adjustments']?.['Other Premium/Penalties']) {
      setQualityAdjustmentsOtherPremiumPenalties(offerResponse['Quality Adjustments']['Other Premium/Penalties']);
      newUpdatedFields['qualityAdjustmentsOtherPremiumPenalties'] = true;
      setManuallySelectedQualityAdjustmentsOtherPremiumPenalties(false);
    }

    if (offerResponse.Payment?.['Payment Wording']) {
      setPaymentWording(offerResponse.Payment['Payment Wording']);
      newUpdatedFields['paymentWording'] = true;
      
      // Check if wording doesn't match the selected payment method preset
      if (offerResponse.Payment['Payment Method']) {
        const paymentMethod = offerResponse.Payment['Payment Method'];
        // Find preset matching the payment method
        const matchingPreset = findPaymentTerm(paymentWordingPresets, paymentMethod);
        // If there's no matching preset or the wording doesn't match, set to Custom Wording
        if ((!matchingPreset || matchingPreset.wording !== offerResponse.Payment['Payment Wording']) 
            && offerResponse.Payment['Payment Wording'].trim()) {
          setSelectedPaymentMethod('Custom Wording');
        }
      }
    }

    if (offerResponse.Payment?.['Payment Method']) {
      // Only set the method if we haven't already set it to Custom Wording above
      if (selectedPaymentMethod !== 'Custom Wording') {
        setSelectedPaymentMethod(offerResponse.Payment['Payment Method']);
      }
      newUpdatedFields['selectedPaymentMethod'] = true;
      setManuallySelectedPaymentMethod(false);
    }

    if (offerResponse['Quality and Quantity Determination']?.['Surveyor(s) or leave empty for generic wording']) {
      setQualityAndQuantityDeterminationSurveyor(
        offerResponse['Quality and Quantity Determination']['Surveyor(s) or leave empty for generic wording']
      );
      newUpdatedFields['qualityAndQuantityDeterminationSurveyor'] = true;
      setManuallySelectedSurveyor(false);
    }

    if (offerResponse['Shipping Terms']?.['Shipping Terms Wording']) {
      setShippingWording(offerResponse['Shipping Terms']['Shipping Terms Wording']);
      newUpdatedFields['shippingWording'] = true;
      
      // Check if wording doesn't match the selected shipping method preset
      if (offerResponse['Shipping Terms']['Shipping Terms']) {
        const shippingMethod = offerResponse['Shipping Terms']['Shipping Terms'];
        // Find preset matching the shipping method
        const matchingPreset = shippingWordingPresets.find(preset => preset.name === shippingMethod);
        if (matchingPreset) {
          // Process the preset to compare it properly
          const processedPresetWording = matchingPreset.wording
            .split('\n')
            .slice(2) // Skip the heading and the empty line after it
            .join('\n')
            .trim();
          
          // If the wording doesn't match, set to Custom Wording
          if (processedPresetWording !== offerResponse['Shipping Terms']['Shipping Terms Wording'].trim() 
              && offerResponse['Shipping Terms']['Shipping Terms Wording'].trim()) {
            setSelectedShippingMethod('Custom Wording');
          }
        } else if (offerResponse['Shipping Terms']['Shipping Terms Wording'].trim()) {
          // No matching preset but we have wording, so it's custom
          setSelectedShippingMethod('Custom Wording');
        }
      }
    }

    if (offerResponse['Shipping Terms']?.['Shipping Terms']) {
      // Only set the method if we haven't already set it to Custom Wording above
      if (selectedShippingMethod !== 'Custom Wording') {
        setSelectedShippingMethod(offerResponse['Shipping Terms']['Shipping Terms']);
      }
      newUpdatedFields['selectedShippingMethod'] = true;
      setManuallySelectedShippingTerms(false);
    }

    if (offerResponse['Other Terms']?.['Other Terms Wording']) {
      setOtherTermsWording(offerResponse['Other Terms']['Other Terms Wording']);
      newUpdatedFields['otherTermsWording'] = true;
      
      // Check if wording doesn't match the selected other terms method preset
      if (offerResponse['Other Terms']['Other Terms']) {
        const otherTermsMethod = offerResponse['Other Terms']['Other Terms'];
        // Find preset matching the other terms method
        const matchingPreset = otherTermsWordingPresets.find(preset => preset.name === otherTermsMethod);
        if (matchingPreset) {
          // Process the preset to compare it properly
          const processedPresetWording = matchingPreset.wording
            .split('\n')
            .slice(2) // Skip the heading and the empty line after it
            .join('\n')
            .trim();
          
          // If the wording doesn't match, set to Custom Wording
          if (processedPresetWording !== offerResponse['Other Terms']['Other Terms Wording'].trim()
              && offerResponse['Other Terms']['Other Terms Wording'].trim()) {
            setSelectedOtherTermsMethod('Custom Wording');
          }
        } else if (offerResponse['Other Terms']['Other Terms Wording'].trim()) {
          // No matching preset but we have wording, so it's custom
          setSelectedOtherTermsMethod('Custom Wording');
        }
      }
    }

    if (offerResponse['Other Terms']?.['Other Terms']) {
      // Only set the method if we haven't already set it to Custom Wording above
      if (selectedOtherTermsMethod !== 'Custom Wording') {
        setSelectedOtherTermsMethod(offerResponse['Other Terms']['Other Terms']);
      }
      newUpdatedFields['selectedOtherTermsMethod'] = true;
      setManuallySelectedOtherTerms(false);
    }

    if (offerResponse['Deal Validity']?.['Date']) {
      setOfferValidityDate(offerResponse['Deal Validity']['Date']);
      newUpdatedFields['offerValidityDate'] = true;
      setManuallySelectedOfferValidityDate(false);
    }

    if (offerResponse['Deal Validity']?.['Time']) {
      setOfferValidityTime(offerResponse['Deal Validity']['Time']);
      newUpdatedFields['offerValidityTime'] = true;
      setManuallySelectedOfferValidityTime(false);
    }

    if (offerResponse['Deal Validity']?.['Time-Zone']) {
      setOfferValidityTimeZone(offerResponse['Deal Validity']['Time-Zone']);
      newUpdatedFields['offerValidityTimeZone'] = true;
      setManuallySelectedTimeZone(false);
    }

    // Process quality table values if present
    // Check for both formats: "Quality Table" (from alias) and "quality_table" (direct property)
    const qualityTableData = offerResponse['Quality Table'] || offerResponse.quality_table;
    
    if (qualityTableData) {
      console.log('Processing quality table from offerResponse:', qualityTableData);
      
      // Set column headers if provided
      if (qualityTableData.typical_column_name || qualityTableData['Typical Column Name']) {
        const typicalColumnName = qualityTableData.typical_column_name || qualityTableData['Typical Column Name'];
        setQualityColumnHeader(typicalColumnName);
        newUpdatedFields['qualityColumnHeader'] = true;
      }
      
      if (qualityTableData.min_max_column_name || qualityTableData['MinMax Column Name']) {
        const minMaxColumnName = qualityTableData.min_max_column_name || qualityTableData['MinMax Column Name'];
        setMinMaxColumnHeader(minMaxColumnName);
        newUpdatedFields['minMaxColumnHeader'] = true;
      }
      
      // Process quality values
      const qualityValues = qualityTableData.quality_values || qualityTableData['Quality Values'];
      if (qualityValues && qualityValues.length > 0) {
        const initialQualityValues = {};
        const initialSelectedOptions = { ...selectedOptions };
        
        // Fetch quality parameters if not already loaded
        if (!qualityParameters || qualityParameters.length === 0) {
          axiosInstance.get('/api/offer/quality-parameters/')
            .then(response => {
              setQualityParameters(response.data);
              processQualityValues(response.data, qualityValues);
            })
            .catch(error => {
              console.error('Error fetching quality parameters:', error);
            });
        } else {
          processQualityValues(qualityParameters, qualityValues);
        }
        
        function processQualityValues(parameters, values) {
          console.log('Processing quality values:', values);
          console.log('Available parameters:', parameters);
          
          // Helper function to normalize parameter names for comparison
          const normalizeParamName = (name) => {
            return name.toLowerCase().replace(/\s+/g, '').replace(/[()]/g, '');
          };
          
          values.forEach(qualityValue => {
            // Handle both property naming conventions
            const paramName = qualityValue.parameter_name || qualityValue['Parameter Name'] || '';
            const paramId = qualityValue.parameter_id || qualityValue['Parameter ID'];
            const selectedOption = qualityValue.selected_option || qualityValue['Selected Option'] || '';
            const typicalValue = qualityValue.typical_or_guaranteed_value || qualityValue['Typical/Guaranteed Value'] || '';
            const minMaxValue = qualityValue.min_max_or_rejection_value || qualityValue['Min/Max or Rejection Value'] || '';
            
            console.log(`Processing parameter: ${paramName}, typical: ${typicalValue}, min/max: ${minMaxValue}`);
            
            // Normalize the parameter name for comparison
            const normalizedParamName = normalizeParamName(paramName);
            
            // First try to find parameter by matching the name against options
            let parameter = parameters.find(p => 
              p.has_options && p.options.some(opt => 
                normalizeParamName(opt.option_value) === normalizedParamName
              )
            );
            
            // If no match found through options, try direct parameter matching
            if (!parameter) {
              parameter = parameters.find(p => 
                p.id === paramId || 
                p.description.toLowerCase() === paramName.toLowerCase() ||
                normalizeParamName(p.description) === normalizedParamName
              );
            }
            
            if (parameter) {
              console.log(`Matched parameter: ${paramName} to ${parameter.description} (ID: ${parameter.id})`);
              
              // Set quality values
              initialQualityValues[parameter.id] = {
                typical_or_guaranteed_value: typicalValue,
                min_max_or_rejection_value: minMaxValue
              };
              
              // Set selected option if parameter has options
              if (parameter.has_options) {
                // If we have a selected option from the data, use it
                if (selectedOption) {
                  initialSelectedOptions[parameter.id] = selectedOption;
                } 
                // If we don't have a selected option but matched by option value, use the matched value
                else if (paramName !== parameter.description) {
                  initialSelectedOptions[parameter.id] = paramName;
                }
                // Otherwise use the first available option
                else if (parameter.options && parameter.options.length > 0) {
                  initialSelectedOptions[parameter.id] = parameter.options[0].option_value;
                }
              }
            } else {
              console.warn(`Could not match parameter: ${paramName}`);
            }
          });
          
          // Update state with processed values
          setQualityValues(initialQualityValues);
          setSelectedOptions(initialSelectedOptions);
        }
        
        newUpdatedFields['qualityValues'] = true;
      }
    }

    // Update the state with the fields that have been updated
    setUpdatedFields(prevUpdatedFields => ({
      ...prevUpdatedFields,
      ...newUpdatedFields
    }));
    //console.log('Updated Fields:', newUpdatedFields); // Debugging line

  }
}, [offerResponse]);

  // Reset quality values when component mounts or new offer is created
  useEffect(() => {
    // Reset quality values when component mounts or new offer is created
    setQualityValues({});
    setSelectedOptions({});
    
    return () => {
      // Cleanup when unmounting
      setQualityValues({});
      setSelectedOptions({});
    };
  }, [offerId]); // Add offerId as dependency to reset when new offer is created


  //This changes the offer data when user changes the value of a clause
  const handleInputChange = (clauseName, componentName, value) => {
    setOfferData(prevData => ({
      ...prevData,
      [clauseName]: {
        ...prevData[clauseName],
        [componentName]: value
      }
    }));

    if (clauseName === 'Seller' && componentName === 'Selling Entity') {
      setSelectedSeller(value); // Update selected seller
    }

    if (clauseName === 'Buyer' && componentName === 'Buyer\'s Entity') {
      setSelectedBuyer(value); // Update selected buyer
      setBuyerFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedBuyer(true); // Mark as manually selected
    }

    if (clauseName === 'Incoterms' && componentName === 'Incoterm') {
      setSelectedIncoterm(value); // Update selected incoterm
      setIncotermFormattedText(''); // Clear formatted text so built text is used
    }

    if (clauseName === 'Commodity' && componentName === 'Product') {
      setSelectedProduct(value); // Update selected product
      setCommodityFormattedText(''); // Clear formatted text so built text is used
    }

    if (clauseName === 'Commodity' && componentName === 'Origin') {
      setSelectedOrigin(value); // Update selected origin
      setCommodityFormattedText(''); // Clear formatted text so built text is used
    }

    if (clauseName === 'Commodity' && componentName === 'Additional Information') {
      setAdditionalInfo(value); // Update additional information
      setCommodityFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedCommodityAdditionalInfo(true);
    }

    // if (clauseName === 'Commodity' && componentName === 'Overwrite Commodity') {
    //   setCommodityOverwrite(value); // Update commodity overwrite
    //   setManuallySelectedCommodityOverwrite(true);
    // }

    if (clauseName === 'Quantity' && componentName === 'Quantity') {
      // No need to strip commas since we're storing the raw text value
      setQuantityWeight(value); // Store the raw value
      setQuantityFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedQuantityWeight(true);
      // Update offerData with the raw value
      setOfferData(prevData => ({
        ...prevData,
        [clauseName]: {
          ...prevData[clauseName],
          [componentName]: value // Store the raw text
        }
      }));
      return; // Exit early since we've handled the state update
    }

    if (clauseName === 'Quantity' && componentName === 'Tolerance') {
      setQuantityTolerance(value);
      setQuantityFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedQuantityTolerance(true);
    }

    if (clauseName === 'Quantity' && componentName === 'Quantity Option') {
      setQuantityOption(value); // Update quantity option
      setQuantityFormattedText(''); // Clear formatted text so built text is used
    }

    // if (clauseName === 'Quantity' && componentName === 'Overwrite Quantity') {
    //   setQuantityOverwrite(value); // Update quantity overwrite
    //   setManuallySelectedQuantityOverwrite(true);
    // } 

    if (clauseName === 'Delivery' && componentName === 'Delivery') {
      setDelivery(value); // Update delivery
      setDeliveryFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedDelivery(true);
    }

    if (clauseName === 'Loading Port(s)' && componentName === 'Loading Port(s)') {
      setLoadingPort(value); // Update loading port
      setLoadingPortFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedLoadingPort(true);
    }
  
    if (clauseName === 'Base Price' && componentName === 'Price type') {
      setPriceType(value);
      setPriceFormattedText(''); // Clear formatted text so built text is used
    }

    if (clauseName === 'Base Price' && componentName === 'Currency') {
      setBasePriceCurrency(value);
      setPriceFormattedText(''); // Clear formatted text so built text is used
    }

    if (clauseName === 'Base Price' && componentName === 'For Fixed Price') {
      setBasePriceForFixedPrice(value);
      setPriceFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedBasePriceForFixedPrice(true);
    }

    if (clauseName === 'Base Price' && componentName === 'For Index: Premium/Discount') {
      setBasePriceForIndex(value);
      setPriceFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedBasePriceForIndexPremiumDiscount(true);
    }

    if (clauseName === 'Base Price' && componentName === 'CV Basis') {
      setBasePriceCVBasis(value);
      setPriceFormattedText(''); // Clear formatted text so built text is used
    }

    if (clauseName === 'Base Price' && componentName === 'Kcal/Kg') {
      setBasePriceKcalKg(value);
      setPriceFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedBasePriceKcalKg(true);
    }
    
    if (clauseName === 'Base Price' && componentName === 'Applicable Index Period') {
      setBasePriceApplicableIndexPeriod(value);
      setPriceFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedBasePriceApplicableIndexPeriod(true);
    }

    // if (clauseName === 'Base Price' && componentName === 'Overwrite Base Price') {
    //   setBasePriceOverwrite(value);
    //   setManuallySelectedBasePriceOverwrite(true);
    // }

    if (clauseName === 'Quality Adjustments' && componentName === 'Pro-rata CV adjustments') {
      setQualityAdjustmentsProRata(value);
    }

    if (clauseName === 'Quality Adjustments' && componentName === 'Other Premium/Penalties') {
      setQualityAdjustmentsOtherPremiumPenalties(value);
      setManuallySelectedQualityAdjustmentsOtherPremiumPenalties(true);
    }

    // if (clauseName === 'Quality Adjustments' && componentName === 'Overwrite Quality Adjustments') {
    //   setQualityAdjustmentsOverwrite(value);
    //   setManuallySelectedQualityAdjustmentsOverwrite(true);
    // }

    if (clauseName === 'Quality and Quantity Determination' && componentName === 'Surveyor(s) or leave empty for generic wording') {
      setQualityAndQuantityDeterminationSurveyor(value);
      setQualityQuantityFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedSurveyor(true);
    }

    // if (clauseName === 'Quality and Quantity Determination' && componentName === 'Overwrite Quality and Quantity Determination') {
    //   setQualityAndQuantityDeterminationOverwrite(value);
    //   setManuallySelectedQualityAndQuantityDeterminationOverwrite(true);
    // }

    if (clauseName === 'Payment' && componentName === 'Payment Method') {
      const paymentWording = paymentTermWording(paymentWordingPresets, value);
      setOfferData(prevData => ({
        ...prevData,
        [clauseName]: {
          ...prevData[clauseName],
          [componentName]: value,
          'Payment Wording': paymentWording
        }
      }));
      setSelectedPaymentMethod(value);
      setPaymentWording(paymentWording);
    } else if (clauseName === 'Payment' && componentName === 'Payment Wording') {
      setPaymentWording(value);
    }
  
    if (clauseName === 'Shipping Terms' && componentName === 'Shipping Terms') {
      const shippingTermsWording = shippingWordingPresets.find(preset => preset.name === value)?.wording || '';
      setOfferData(prevData => ({
        ...prevData,
        [clauseName]: {
          ...prevData[clauseName],
          [componentName]: value,
          'Shipping Terms Wording': shippingTermsWording
        }
      }));
      setSelectedShippingMethod(value);
      setShippingWording(shippingTermsWording);
    } else if (clauseName === 'Shipping Terms' && componentName === 'Shipping Terms Wording') {
      setShippingWording(value);
    }
  
    if (clauseName === 'Other Terms' && componentName === 'Other Terms') {
      const selectedPreset = otherTermsWordingPresets.find(preset => preset.name === value);
      if (selectedPreset) {
        // Remove the heading and any empty lines at the start of the wording
        const wording = selectedPreset.wording
          .split('\n')
          .slice(2) // Skip the heading and the empty line after it
          .join('\n')
          .trim();
        setOfferData(prevData => ({
          ...prevData,
          [clauseName]: {
            ...prevData[clauseName],
            [componentName]: value,
            'Other Terms Wording': wording
          }
        }));
        setSelectedOtherTermsMethod(value);
        setOtherTermsWording(wording);
      }
    } else if (clauseName === 'Other Terms' && componentName === 'Other Terms Wording') {
      setOtherTermsWording(value);
    }

    if (clauseName === 'Deal Validity' && componentName === 'Date') {
      setOfferValidityDate(value);
      setDealValidityFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedOfferValidityDate(true);
    }

    if (clauseName === 'Deal Validity' && componentName === 'Time') {
      setOfferValidityTime(value);
      setDealValidityFormattedText(''); // Clear formatted text so built text is used
      setManuallySelectedOfferValidityTime(true);
    }

    if (clauseName === 'Deal Validity' && componentName === 'Time-Zone') {
      setOfferValidityTimeZone(value);
      setDealValidityFormattedText(''); // Clear formatted text so built text is used
    }

    // Explicitly log before setting the flag
    console.log(`[AUTOSAVE] Input changed: ${clauseName}.${componentName}`, { value });
    
    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
    console.log('[AUTOSAVE] hasUnsavedChanges set to true');
    
    // Don't call performAutosave() directly, let the useEffect handle it
  };

  //Handle time change
  const handleTimeChange = (e) => {
    setOfferValidityTime(e.target.value);
  };

  // Date validation method
  const isValidDate = (dateString) => {
    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getDate() === day &&
      date.getMonth() === month - 1 &&
      date.getFullYear() === year
    );
  };

  //Time validation method
  const isValidTime = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return (
      hours >= 0 && hours <= 23 &&
      minutes >= 0 && minutes <= 59
    );
  };

  //Render clause component based on component type
 // Helper function to get the field key used in updatedFields
const getFieldKey = (clauseName, componentName) => {
  if (clauseName === 'Seller' && componentName === 'Selling Entity') return 'selectedSeller';
  if (clauseName === 'Buyer' && componentName === "Buyer's Entity") return 'selectedBuyer';
  if (clauseName === 'Incoterms' && componentName === 'Incoterm') return 'selectedIncoterm';
  if (clauseName === 'Commodity' && componentName === 'Product') return 'selectedProduct';
  if (clauseName === 'Commodity' && componentName === 'Origin') return 'selectedOrigin';
  if (clauseName === 'Commodity' && componentName === 'Additional Information') return 'additionalInfo';
  // if (clauseName === 'Commodity' && componentName === 'Overwrite Commodity') return 'commodityOverwrite';
  if (clauseName === 'Quantity' && componentName === 'Quantity') return 'quantityWeight';
  if (clauseName === 'Quantity' && componentName === 'Tolerance') return 'quantityTolerance';
  if (clauseName === 'Quantity' && componentName === 'Quantity Option') return 'quantityOption';
  // if (clauseName === 'Quantity' && componentName === 'Overwrite Quantity') return 'quantityOverwrite';
  if (clauseName === 'Delivery' && componentName === 'Delivery') return 'delivery';
  if (clauseName === 'Loading Port(s)' && componentName === 'Loading Port(s)') return 'loadingPort';
  if (clauseName === 'Base Price' && componentName === 'Price type') return 'priceType';
  if (clauseName === 'Base Price' && componentName === 'Currency') return 'basePriceCurrency';
  if (clauseName === 'Base Price' && componentName === 'For Fixed Price') return 'basePriceForFixedPrice';
  if (clauseName === 'Base Price' && componentName === 'For Index: Premium/Discount') return 'basePriceForIndex';
  if (clauseName === 'Base Price' && componentName === 'CV Basis') return 'basePriceCVBasis';
  if (clauseName === 'Base Price' && componentName === 'Kcal/Kg') return 'basePriceKcalKg';
  if (clauseName === 'Base Price' && componentName === 'Applicable Index Period') return 'basePriceApplicableIndexPeriod';
  // if (clauseName === 'Base Price' && componentName === 'Overwrite Base Price') return 'basePriceOverwrite';
  if (clauseName === 'Quality Adjustments' && componentName === 'Pro-rata CV adjustments') return 'qualityAdjustmentsProRata';
  if (clauseName === 'Quality Adjustments' && componentName === 'Other Premium/Penalties') return 'qualityAdjustmentsOtherPremiumPenalties';
  // if (clauseName === 'Quality Adjustments' && componentName === 'Overwrite Quality Adjustments') return 'qualityAdjustmentsOverwrite';
  if (clauseName === 'Payment' && componentName === 'Payment Wording') return 'paymentWording';
  if (clauseName === 'Payment' && componentName === 'Payment Method') return 'selectedPaymentMethod';
  if (clauseName === 'Quality and Quantity Determination' && componentName === 'Surveyor(s) or leave empty for generic wording') return 'qualityAndQuantityDeterminationSurveyor';
  // if (clauseName === 'Quality and Quantity Determination' && componentName === 'Overwrite Quality and Quantity Determination') return 'qualityAndQuantityDeterminationOverwrite';
  if (clauseName === 'Shipping Terms' && componentName === 'Shipping Terms Wording') return 'shippingWording';
  if (clauseName === 'Shipping Terms' && componentName === 'Shipping Terms') return 'selectedShippingMethod';
  if (clauseName === 'Other Terms' && componentName === 'Other Terms Wording') return 'otherTermsWording';
  if (clauseName === 'Other Terms' && componentName === 'Other Terms') return 'selectedOtherTermsMethod';
  if (clauseName === 'Deal Validity' && componentName === 'Date') return 'offerValidityDate';
  if (clauseName === 'Deal Validity' && componentName === 'Time') return 'offerValidityTime';
  if (clauseName === 'Deal Validity' && componentName === 'Time-Zone') return 'offerValidityTimeZone';

  return null;
};

// Several products: the lines fill the deal's own boxes. The first line is
// the Product, the lines added up the Quantity, their average the Price
// (exactly what the server stores: backend/offer/product_lines.py). Down to
// one line, the screen goes back to the single Product box.
const priceComponentFor = (type) => (type === 'Fix Price' ? 'For Fixed Price' : 'For Index: Premium/Discount');
const fillHeaderFromLines = (product, variantId, quantity, price) => {
  setSelectedProduct(product);
  setSelectedProductVariantId(variantId);
  setSelectedProductLabel(product);
  handleInputChange('Commodity', 'Product', product);
  handleInputChange('Quantity', 'Quantity', quantity);
  handleInputChange('Base Price', priceComponentFor(priceType), price);
  setHasUnsavedChanges(true);
};
const applyOfferProducts = (next) => {
  if (next.length === 1) {
    const only = next[0];
    setOfferProducts([]);
    fillHeaderFromLines(only.product_label || '', only.stock_item_variant || null, only.quantity || '', only.price || '');
    return;
  }
  setOfferProducts(next);
  const head = next[0] || {};
  const { total, average } = linesHeader(next, OFFER_LINE_KEYS);
  fillHeaderFromLines(head.product_label || '', head.stock_item_variant || null, total, average);
};
const startSeveralProducts = () => {
  const price = priceType === 'Fix Price' ? basePriceForFixedPrice : basePriceForIndex;
  applyOfferProducts([
    {
      ...blankLine(OFFER_LINE_KEYS),
      stock_item_variant: selectedProductVariantId || null,
      product_label: selectedProductLabel || selectedProduct || '',
      quantity: String(quantityWeight || '').replace(/[\s,]/g, ''),
      price: price || '',
    },
    blankLine(OFFER_LINE_KEYS),
  ]);
};

const renderClauseComponent = (component, clauseName) => {
  //console.log(`Rendering component: ${clauseName}.${component.name}`);

  const isFieldUpdated = (fieldKey) => !!updatedFields[fieldKey];
  const fieldKey = getFieldKey(clauseName, component.name);

  switch (component.component_type) {
    case 'text/dropdown':
      // Handle Seller and Buyer components based on deal type
      if (clauseName === 'Seller' && component.name === 'Selling Entity') {
        if (dealType === 'Offer') {
          // For Offer type, render HMS entities dropdown with support for new format
          return (
            <div className="select-container">
              <select
                onChange={(e) => {
                  setSelectedSeller(e.target.value);
                  setSellerFormattedText(''); // Clear formatted text so built text is used
                  handleInputChange(clauseName, component.name, e.target.value);
                  setManuallySelectedSeller(true);
                }}
                value={selectedSeller || ''}
                className={`${isFieldUpdated(fieldKey) && !manuallySelectedSeller ? 'highlighted-field' : ''}`}
              >
                <option value="">Select Entity</option>
                {hmsEntities.map((entity, index) => {
                  // Handle both old format (entity.hms_entity_name) and new format ({value, label})
                  // Always use the label/name as the value, NOT the ID
                  const entityName = typeof entity === 'object' && 'label' in entity ? entity.label : 
                                     (entity.hms_entity_name ? entity.hms_entity_name : entity);
                  return (
                    <option key={index} value={entityName}>
                      {entityName}
                    </option>
                  );
                })}
              </select>
            </div>
          );
        } else {
          // For Bid type, render CRM search for external seller
          return (
            <OfferPartnerSearch
              value={offerData[clauseName]?.[component.name] || ''}
              onChange={(partnerName) => handleInputChange(clauseName, component.name, partnerName)}
              placeholder="Search for Seller in CRM..."
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
            />
          );
        }
      }

      if (clauseName === 'Buyer' && component.name === "Buyer's Entity") {
        if (dealType === 'Bid') {
          // For Bid type, render HMS entities dropdown with support for new format
          return (
            <div className="select-container">
              <select
                onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
                value={offerData[clauseName]?.[component.name] || ''}
                className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
              >
                <option value="">Select Entity</option>
                {hmsEntities.map((entity, index) => {
                  // Handle both old format (entity.hms_entity_name) and new format ({value, label})
                  // Always use the label/name as the value, NOT the ID
                  const entityName = typeof entity === 'object' && 'label' in entity ? entity.label : 
                                     (entity.hms_entity_name ? entity.hms_entity_name : entity);
                  return (
                    <option key={index} value={entityName}>
                      {entityName}
                    </option>
                  );
                })}
              </select>
            </div>
          );
        } else {
          // For Offer type, render CRM search for external buyer
          return (
            <OfferPartnerSearch
              value={offerData[clauseName]?.[component.name] || ''}
              onChange={(partnerName) => handleInputChange(clauseName, component.name, partnerName)}
              placeholder="Search for Buyer in CRM..."
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
            />
          );
        }
      }
      break;

    case 'dropdown':
      // Handle 'Seller' and 'Buyer' clauses with special logic
      if (clauseName === 'Seller' && component.name === 'Selling Entity') {
        return (
          <select
            onChange={(e) => {
              setSelectedSeller(e.target.value);
              setSellerFormattedText(''); // Clear formatted text so built text is used
              handleInputChange(clauseName, component.name, e.target.value);
              setManuallySelectedSeller(true);
            }}
            value={selectedSeller || ''}
            className={`${isFieldUpdated(fieldKey) && !manuallySelectedSeller ? 'highlighted-field' : ''}`}
          >
            <option value="">Select Entity</option>
            {hmsEntities.map((entity, index) => {
              // Handle both old format (entity.hms_entity_name) and new format ({value, label})
              // Always use the label/name as the value, NOT the ID
              const entityName = typeof entity === 'object' && 'label' in entity ? entity.label : 
                                (entity.hms_entity_name ? entity.hms_entity_name : entity);
              return (
                <option key={index} value={entityName}>
                  {entityName}
                </option>
              );
            })}
          </select>
        );
      }

      // Handle Product with RecapProductSearch component
      if (component.name === 'Product') {
        if (offerProducts.length >= 2) {
          return (
            <DealProductLines
              lines={offerProducts}
              onChange={applyOfferProducts}
              keys={OFFER_LINE_KEYS}
              unit={quantityUnit}
              currency={basePriceCurrency}
              priceLabel={priceType && priceType !== 'Fix Price' ? 'Premium / discount' : 'Price'}
            />
          );
        }
        return (
          <>
            <RecapProductSearch
              label=""
              value={selectedProduct}
              variantId={selectedProductVariantId}
              onChange={(variantId, displayName, variant) => {
                console.log('Product selected:', { variantId, displayName, variant });
              
                // Extract just the product type (first part before |)
                const productType = displayName.split('|')[0]?.trim() || displayName;
                setSelectedProduct(productType);
                setSelectedProductVariantId(variantId);
                setSelectedProductLabel(variantId ? lineProductLabel(displayName, variant) : '');
                handleInputChange(clauseName, component.name, productType);
              
                // Auto-fill fields if variant data is available
                if (variant) {
                  console.log('=== FULL VARIANT DATA ===', variant);
                  console.log('Base stock item data:', variant.base_stock_item_data);
                  console.log('Country of origin path check:', {
                    has_base_data: !!variant.base_stock_item_data,
                    country: variant.base_stock_item_data?.country_of_origin,
                    direct_country: variant.country_of_origin
                  });
                
                  // Auto-fill quantity with space as thousands separator
                  if (variant.quantity) {
                    const qty = parseFloat(variant.quantity).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                    console.log('✓ Auto-filling quantity:', qty);
                    setQuantityWeight(qty);
                    handleInputChange('Quantity', 'Quantity', qty);
                    setManuallySelectedQuantityWeight(false);
                  }
                
                  // Auto-fill unit
                  if (variant.unit_code) {
                    console.log('✓ Auto-filling unit:', variant.unit_code);
                    setQuantityUnit(variant.unit_code);
                  }
                
                  // Auto-fill tolerance from variant (NEW)
                  if (variant.tolerance_lower_value || variant.tolerance_upper_value) {
                    console.log('✓ Auto-filling tolerance from variant:', {
                      lower: variant.tolerance_lower_value + variant.tolerance_lower_unit,
                      upper: variant.tolerance_upper_value + variant.tolerance_upper_unit
                    });
                    setQuantityToleranceLowerValue(variant.tolerance_lower_value || '');
                    setQuantityToleranceLowerUnit(variant.tolerance_lower_unit || '%');
                    setQuantityToleranceUpperValue(variant.tolerance_upper_value || '');
                    setQuantityToleranceUpperUnit(variant.tolerance_upper_unit || '%');
                    setManuallySelectedQuantityTolerance(false);
                  }
                
                  // Auto-fill origin - check both nested and flat structure
                  const origin = variant.base_stock_item_data?.country_of_origin || variant.country_of_origin;
                  if (origin) {
                    console.log('✓ Auto-filling origin from product:', origin);
                    setSelectedOrigin(origin);
                    handleInputChange('Commodity', 'Origin', origin);
                    setManuallySelectedOrigin(false);
                  } else {
                    console.log('✗ No origin found in variant data');
                  }
                
                  // Auto-fill currency from variant (NEW - currency propagation fix)
                  if (variant.price_currency) {
                    console.log('✓ Auto-filling currency from variant:', variant.price_currency);
                    setBasePriceCurrency(variant.price_currency);
                    handleInputChange('Base Price', 'Currency', variant.price_currency);
                    setManuallySelectedCurrency(false);
                  } else {
                    console.log('✗ No currency found in variant data');
                  }
                
                  // Auto-fill price based on deal type and variant prices
                  // Offer (Sale) uses sale_price, Bid (Purchase) uses purchase_price
                  const priceToUse = dealType === 'Offer' ? variant.sale_price : variant.purchase_price;
                  if (priceToUse) {
                    console.log('✓ Auto-filling price from variant:', priceToUse, 'Deal type:', dealType);
                  
                    // If no price type is set, default to "Fix Price"
                    const currentPriceType = priceType || 'Fix Price';
                    if (!priceType) {
                      console.log('✓ Setting default price type to "Fix Price"');
                      setPriceType('Fix Price');
                      handleInputChange('Base Price', 'Price type', 'Fix Price');
                    }
                  
                    // Auto-fill the appropriate price field based on current/default price type
                    if (currentPriceType === 'Fix Price') {
                      console.log('✓ Auto-filling "For Fixed Price" field:', priceToUse);
                      setBasePriceForFixedPrice(priceToUse);
                      handleInputChange('Base Price', 'For Fixed Price', priceToUse);
                      setManuallySelectedBasePriceForFixedPrice(false);
                    } else {
                      console.log('✓ Auto-filling "For Index: Premium/Discount" field:', priceToUse);
                      setBasePriceForIndex(priceToUse);
                      handleInputChange('Base Price', 'For Index: Premium/Discount', priceToUse);
                      setManuallySelectedBasePriceForIndexPremiumDiscount(false);
                    }
                  } else {
                    console.log('✗ No price found in variant data for deal type:', dealType);
                  }
                } else {
                  console.log('✗ No variant data available');
                }
              }}
              disabled={false}
            />
            <HMSButton variant="ghost" size="compact" className="hms-deal-lines-start" onClick={startSeveralProducts}>
              + Add another product
            </HMSButton>
          </>
        );
      }

      // Handle Origin with countries dropdown
      if (component.name === 'Origin') {
        return (
          <div className="select-container">
            <select
              onChange={(e) => {
                handleOriginChange(e);
                handleInputChange(clauseName, component.name, e.target.value);
              }}
              value={selectedOrigin || ''}
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
            >
              <option value="">Select Origin</option>
              {countries.map((country, index) => {
                // Handle both string format and object format {value, label}
                const countryValue = typeof country === 'string' ? country : country.value;
                const countryLabel = typeof country === 'string' ? country : country.label;
                return (
                  <option key={index} value={countryValue}>
                    {countryLabel}
                  </option>
                );
              })}
            </select>
          </div>
        );
      }

      // Handle Currency with CURRENCY_OPTIONS
      if (component.name === 'Currency') {
        return (
          <div className="select-container">
            <select
              onChange={(e) => {
                handleCurrencyChange(e);
                handleInputChange(clauseName, component.name, e.target.value);
              }}
              value={offerData[clauseName]?.[component.name] || ''}
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
            >
              <option value="">Select Currency</option>
              {CURRENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );
      }

      // Default dropdown rendering for other components
      return (
        <div className="select-container">
          <select
            onChange={(e) => {
              if (component.name === 'Incoterm') {
                handleIncotermChange(e); // Trigger handleIncotermChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else if (component.name === 'Quantity Option') {
                handleQuantityOptionChange(e); // Trigger handleQuantityOptionChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else if (component.name === 'Price type') {
                handlePriceTypeChange(e); // Trigger handlePriceTypeChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else if (component.name === 'CV Basis') {
                handleCVBasisChange(e); // Trigger handleCVBasisChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else if (component.name === 'Pro-rata CV adjustments') {
                handleProRataChange(e); // Trigger handleProRataChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else if (component.name === 'Payment Method') {
                handlePaymentMethodChange(e); // Trigger handlePaymentMethodChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else if (component.name === 'Shipping Terms') {
                handleShippingTermsChange(e); // Trigger handleShippingTermsChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else if (component.name === 'Other Terms') {
                handleOtherTermsChange(e); // Trigger handleOtherTermsChange
                handleInputChange(clauseName, component.name, e.target.value);
              } else {
                handleInputChange(clauseName, component.name, e.target.value);
              }
            }}
            value={offerData[clauseName]?.[component.name] || ''}
            className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
          >
            <option value="">Select an option</option>
            {/* Add Custom Wording option for specific clause types */}
            {(component.name === 'Payment Method' || 
              component.name === 'Shipping Terms' || 
              component.name === 'Other Terms') && (
              <option value="Custom Wording">Custom Wording</option>
            )}
            {component.options.map((option) => (
              <option key={option.id} value={option.option_value}>
                {option.option_value}
              </option>
            ))}
          </select>
        </div>
      );

    case 'text':
      if (clauseName === 'Buyer' && component.name === "Buyer's Entity") {
        if (dealType === 'Offer') {
          // For Offer type, render CRM search for external buyer
          return (
            <OfferPartnerSearch
              value={offerData[clauseName]?.[component.name] || ''}
              onChange={(partnerName) => handleInputChange(clauseName, component.name, partnerName)}
              placeholder="Search for Buyer in CRM..."
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
            />
          );
        } 

      }

      if (clauseName === 'Commodity' && 
          (component.name === 'Additional Information')) {
        return (
          <textarea
            className={`commodity-textarea ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
            onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
            value={offerData[clauseName]?.[component.name] || ''}
            placeholder={`Enter ${component.name.toLowerCase()}`}
          />
        );
      }

      if ((clauseName === 'Delivery' || clauseName === 'Loading Port(s)')
          ) {
        if (clauseName === 'Loading Port(s)') {
          return (
            <PortsDropdown
              value={loadingPort || ''}
              onChange={(value) => handleInputChange(clauseName, component.name, value)}
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
              placeholder={`Enter country name or port name`}
              isHighlighted={isFieldUpdated(fieldKey)}
            />
          );
        } else {
          return (
            <textarea
              className={`ports-textarea ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
              onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
              value={offerData[clauseName]?.[component.name] || ''}
              placeholder={`Enter ${component.name.toLowerCase()}`}
            />
          );
        }
      }

      if (clauseName === 'Quality Adjustments' && 
        (component.name === 'Other Premium/Penalties')) {
      return (
        <textarea
          className={`ports-textarea ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
          onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
          value={offerData[clauseName]?.[component.name] || ''}
          placeholder={`Enter ${component.name.toLowerCase()}`}
        />
      );
    }

    // In the renderClauseComponent function, add case for Quality and Quantity Determination
    if (clauseName === 'Quality and Quantity Determination' && 
      (component.name === 'Surveyor(s) or leave empty for generic wording')) {
    return (
      <textarea
        className={`ports-textarea ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
        onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
        value={offerData[clauseName]?.[component.name] || ''}
        placeholder={`Enter ${component.name.toLowerCase()}`}
      />
    );
    }

    if (clauseName === 'Base Price') {
      if (component.name === 'For Fixed Price') {
        return (
          <div className="price-input-container">
            <AmountInput
              value={offerData[clauseName]?.[component.name] || ''}
              onChange={(v) => {
                handleInputChange(clauseName, component.name, v);
                setBasePriceForFixedPrice(v);
                setManuallySelectedBasePriceForFixedPrice(true);
              }}
              maxDecimals={PRICE_DECIMALS}
              allowNegative
              disabled={priceType !== 'Fix Price' || offerProducts.length >= 2}
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
              placeholder="Enter Fixed Price (0.00)"
            />
            {offerProducts.length >= 2 && (
              <div className="hms-deal-lines-derived-hint">Average of the product lines</div>
            )}
          </div>
        );
      } else if (component.name === 'For Index: Premium/Discount') {
        return (
          <div className="price-input-container">
            <AmountInput
              value={offerData[clauseName]?.[component.name] || ''}
              onChange={(v) => {
                handleInputChange(clauseName, component.name, v);
                setBasePriceForIndex(v);
                setManuallySelectedBasePriceForIndexPremiumDiscount(true);
              }}
              maxDecimals={PRICE_DECIMALS}
              allowNegative
              disabled={priceType === 'Fix Price' || offerProducts.length >= 2}
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
              placeholder="Enter Premium/Discount (0.00)"
            />
            {offerProducts.length >= 2 && (
              <div className="hms-deal-lines-derived-hint">Average of the product lines</div>
            )}
          </div>
        );
      } else if (component.name === 'Applicable Index Period') {
        return (
          <textarea
            className={`ports-textarea ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
            onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
            value={(offerData[clauseName]?.[component.name] || '').replace('[INDEX]', priceType)}
            disabled={priceType === 'Fix Price'}
            placeholder="Enter Applicable Index Period"
          />
        );
      } else if (component.name === 'Kcal/Kg') {
        return (
          <div className="price-input-container">
            <AmountInput
              value={basePriceKcalKg || ''}
              onChange={(v) => {
                handleInputChange(clauseName, component.name, v);
                setBasePriceKcalKg(v);
                setManuallySelectedBasePriceKcalKg(true);
              }}
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
              placeholder="Enter kcal/kg value"
            />
          </div>
        );
      }
    }

    if (clauseName === 'Quality Adjustments' && component.name === 'Standard Wording') {
      return null; // Skip rendering for Standard Wording
    }

    if (
      (clauseName === 'Payment' && component.name === 'Payment Wording') ||
      (clauseName === 'Shipping Terms' && component.name === 'Shipping Terms Wording') ||
      (clauseName === 'Other Terms' && component.name === 'Other Terms Wording')
    ) {
      return (
        <textarea
          rows="10"
          cols="50"
          className={`textarea-upgrade ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
          value={
            clauseName === 'Payment' ? paymentWording :
            clauseName === 'Shipping Terms' ? shippingWording :
            clauseName === 'Other Terms' ? otherTermsWording : ''
          }
          onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
        />
      );
    }

    if (clauseName === 'Deal Validity' && component.name === 'Date') {
      return (
        <>
          <DatePicker
            id="deal_validity_date"
            name="deal_validity_date"
            value={isIndicationOnly ? '' : offerValidityDate}
            onChange={(e) => {
              if (!isIndicationOnly) {
                const newValue = e.target.value;
                setOfferValidityDate(newValue);
                setIsDateValid(true); // DatePicker always provides valid dates
                setManuallySelectedOfferValidityDate(true);
              }
            }}
            placeholder="Select validity date"
            format="DD/MM/YYYY"
            className={`deal-validity-date-picker ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
            disabled={isIndicationOnly}
          />
          {isIndicationOnly && <div className="deal-validity-disabled-notice">Not applicable for indication only deals</div>}
        </>
      );
    }

    if (clauseName === 'Deal Validity' && component.name === 'Time') {
      return (
        <>
          <InputMask
            mask="99:99"
            value={isIndicationOnly ? '' : offerValidityTime}
            onChange={(e) => {
              if (!isIndicationOnly) {
                const newValue = e.target.value;
                setOfferValidityTime(newValue);
                setIsTimeValid(isValidTime(newValue));
                setManuallySelectedOfferValidityTime(true);
              }
            }}
            placeholder="HH:mm"
            maskPlaceholder={null}
            className={`deal-validity-input ${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
            disabled={isIndicationOnly}
          />
          {!isTimeValid && !isIndicationOnly && <div className="deal-validity-error">Please enter a valid time</div>}
          {isIndicationOnly && <div className="deal-validity-disabled-notice">Not applicable for indication only deals</div>}
        </>
      );
    }

    if (clauseName === 'Deal Validity' && component.name === 'Time-Zone') {
      return (
        <>
          <div className="select-container">
            <select
              value={isIndicationOnly ? '' : offerValidityTimeZone}
              onChange={(e) => {
                if (!isIndicationOnly) {
                  handleTimeZoneChange(e);
                  handleInputChange(clauseName, component.name, e.target.value);
                  setManuallySelectedTimeZone(true);
                }
              }}
              className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
              disabled={isIndicationOnly}
            >
              <option value="">Select a timezone</option>
              {commonTimezones.map((timezone) => (
                <option key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </option>
              ))}
            </select>
          </div>
          {isIndicationOnly && <div className="deal-validity-disabled-notice">Not applicable for indication only deals</div>}
        </>
      );
    }

    if (clauseName === 'Quantity' && component.name === 'Quantity') {
      // Get the raw value without formatting for internal state
      const rawValue = offerData[clauseName]?.[component.name] || '';
      // Use the raw value directly rather than always formatting it
      const displayValue = rawValue ? rawValue : '';
      
      return (
        <>
        <div className="quantity-with-unit-container" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            onChange={(e) => {
              const value = e.target.value;
              // Since we're allowing any text, we don't need to strip commas
              setIsQuantityValid(true);
              handleInputChange(clauseName, component.name, value);
              setQuantityWeight(value); 
              setManuallySelectedQuantityWeight(true);
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value) {
                setQuantityWeight(value);
                setIsQuantityValid(true);
              }
            }}
            value={displayValue}
            className={`${isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}`}
            placeholder="Enter product quantity"
            disabled={offerProducts.length >= 2}
            inputMode="text"
            style={{ flex: 1 }}
          />
          <select
            value={quantityUnit || ''}
            onChange={(e) => {
              setQuantityUnit(e.target.value);
            }}
            className="unit-dropdown"
            style={{ 
              width: '120px',
              padding: '8px',
              paddingRight: '32px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '700',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              cursor: 'pointer',
              backgroundImage: 'url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 10" fill="%234a6b25"><path d="M0 3l5 5 5-5z"/></svg>\')',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
              backgroundSize: '10px 10px'
            }}
          >
            <option value="">Unit</option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.code}>
                {unit.name}
              </option>
            ))}
          </select>
        </div>
        {offerProducts.length >= 2 && (
          <div className="hms-deal-lines-derived-hint">Adds up the product lines</div>
        )}
        </>
      );
    }

    if (clauseName === 'Quantity' && component.name === 'Tolerance') {
      return (
        <div className="tolerance-inputs-wrapper">
          <div className="tolerance-inputs-container">
            <div className="tolerance-input-group">
              <div className="tolerance-input-with-dropdown">
                <AmountInput
                  value={quantityToleranceLowerValue}
                  onChange={(v) => {
                    setQuantityToleranceLowerValue(v);
                    setManuallySelectedQuantityTolerance(true);
                  }}
                  placeholder="0"
                  className={isFieldUpdated('quantityToleranceLowerValue') ? 'highlighted-field' : ''}
                />
                <select
                  value={quantityToleranceLowerUnit}
                  onChange={(e) => {
                    setQuantityToleranceLowerUnit(e.target.value);
                    setManuallySelectedQuantityTolerance(true);
                  }}
                  className="tolerance-unit-dropdown"
                >
                  <option value="%">%</option>
                  <option value="min">min</option>
                </select>
              </div>
              <small className="field-description">Lower</small>
            </div>
            
            <div className="tolerance-input-group">
              <div className="tolerance-input-with-dropdown">
                <AmountInput
                  value={quantityToleranceUpperValue}
                  onChange={(v) => {
                    setQuantityToleranceUpperValue(v);
                    setManuallySelectedQuantityTolerance(true);
                  }}
                  placeholder="0"
                  className={isFieldUpdated('quantityToleranceUpperValue') ? 'highlighted-field' : ''}
                />
                <select
                  value={quantityToleranceUpperUnit}
                  onChange={(e) => {
                    setQuantityToleranceUpperUnit(e.target.value);
                    setManuallySelectedQuantityTolerance(true);
                  }}
                  className="tolerance-unit-dropdown"
                >
                  <option value="%">%</option>
                  <option value="max">max</option>
                </select>
              </div>
              <small className="field-description">Upper</small>
            </div>
          </div>
        </div>
      );
    }

    return (
      <input
        type="text"
        onChange={(e) => handleInputChange(clauseName, component.name, e.target.value)}
        value={offerData[clauseName]?.[component.name] || ''}
        className={isFieldUpdated(fieldKey) ? 'highlighted-field' : ''}
        placeholder={`Enter ${component.name.toLowerCase()}`}
      />
    );

    default:
      return null;
  }
};


//This part of the code ensures the background of the main clause textbox reverts back to normal colours when the user enters manual values
const [manuallySelectedSeller, setManuallySelectedSeller] = useState(false);
const [manuallySelectedIncoterm, setManuallySelectedIncoterm] = useState(false);
const [manuallySelectedProduct, setManuallySelectedProduct] = useState(false);
const [manuallySelectedOrigin, setManuallySelectedOrigin] = useState(false);
const [manuallySelectedQuantityOption, setManuallySelectedQuantityOption] = useState(false);
const [manuallySelectedPriceType, setManuallySelectedPriceType] = useState(false);
const [manuallySelectedCurrency, setManuallySelectedCurrency] = useState(false);
const [manuallySelectedCVBasis, setManuallySelectedCVBasis] = useState(false);
const [manuallySelectedProRata, setManuallySelectedProRata] = useState(false);
const [manuallySelectedPaymentMethod, setManuallySelectedPaymentMethod] = useState(false);
const [manuallySelectedShippingTerms, setManuallySelectedShippingTerms] = useState(false);
const [manuallySelectedOtherTerms, setManuallySelectedOtherTerms] = useState(false);
const [manuallySelectedTimeZone, setManuallySelectedTimeZone] = useState(false);

//Handle manual changes for text inputs
const [manuallySelectedBuyer, setManuallySelectedBuyer] = useState(false);
const [manuallySelectedCommodityAdditionalInfo, setManuallySelectedCommodityAdditionalInfo] = useState(false);
const [manuallySelectedQuantityWeight, setManuallySelectedQuantityWeight] = useState(false);
const [manuallySelectedQuantityTolerance, setManuallySelectedQuantityTolerance] = useState(false);
const [manuallySelectedDelivery, setManuallySelectedDelivery] = useState(false);
const [manuallySelectedLoadingPort, setManuallySelectedLoadingPort] = useState(false);
const [manuallySelectedBasePriceForFixedPrice, setManuallySelectedBasePriceForFixedPrice] = useState(false);
const [manuallySelectedBasePriceForIndexPremiumDiscount, setManuallySelectedBasePriceForIndexPremiumDiscount] = useState(false);
const [manuallySelectedBasePriceKcalKg, setManuallySelectedBasePriceKcalKg] = useState(false);
const [manuallySelectedBasePriceApplicableIndexPeriod, setManuallySelectedBasePriceApplicableIndexPeriod] = useState(false);
const [manuallySelectedQualityAdjustmentsOtherPremiumPenalties, setManuallySelectedQualityAdjustmentsOtherPremiumPenalties] = useState(false);
const [manuallySelectedSurveyor, setManuallySelectedSurveyor] = useState(false);
const [manuallySelectedOfferValidityDate, setManuallySelectedOfferValidityDate] = useState(false);
const [manuallySelectedOfferValidityTime, setManuallySelectedOfferValidityTime] = useState(false);
const [portType, setPortType] = useState('Loading Port(s)');

// Add useEffect to ensure port type is always in sync with incoterm
useEffect(() => {
  if (selectedIncoterm) {
    const newPortType = selectedIncoterm === 'FOB' ? 'Loading Port(s)' : 'Discharging Port(s)';
    setPortType(newPortType);
  }
}, [selectedIncoterm]);

const handleIncotermChange = (e) => {
    const newIncoterm = e.target.value;
    setSelectedIncoterm(newIncoterm);
    setManuallySelectedIncoterm(true);
    
    // Update port type based on Incoterm
    const newPortType = newIncoterm === 'FOB' ? 'Loading Port(s)' : 'Discharging Port(s)';
    setPortType(newPortType);
    
    // Update quantity option based on incoterm
    const newQuantityOption = newIncoterm === 'CFR' ? "Quantity in Seller's option" : "Quantity in Buyer's option";
    setQuantityOption(newQuantityOption);
    
    // Update offerData
    setOfferData(prevData => ({
      ...prevData,
      'Incoterms': {
        ...prevData['Incoterms'],
        'Incoterm': newIncoterm
      },
      'Quantity': {
        ...prevData['Quantity'],
        'Quantity Option': newQuantityOption
      }
    }));
  };
const handleProductChange = (e) => {
  const newProduct = e.target.value;
  setSelectedProduct(newProduct);
  setManuallySelectedProduct(true); // Mark as manually selected
};
const handleOriginChange = (e) => {
  const newOrigin = e.target.value;
  setSelectedOrigin(newOrigin);
  setManuallySelectedOrigin(true); // Mark as manually selected
};
const handleQuantityOptionChange = (e) => {
  const newQuantityOption = e.target.value;
  setQuantityOption(newQuantityOption);
  setManuallySelectedQuantityOption(true); // Mark as manually selected
};
const handlePriceTypeChange = (e) => {
  const newPriceType = e.target.value;
  setPriceType(newPriceType);
  setManuallySelectedPriceType(true); // Mark as manually selected
};
const handleCurrencyChange = (e) => {
  const newCurrency = e.target.value;
  setBasePriceCurrency(newCurrency);
  setManuallySelectedCurrency(true); // Mark as manually selected
};
const handleCVBasisChange = (e) => {
  const newCVBasis = e.target.value;
  setBasePriceCVBasis(newCVBasis);
  setManuallySelectedCVBasis(true); // Mark as manually selected
  
  // Map the CV Basis to the quality table option format
  const cvBasisToQualityOption = {
    'NCV(ARB)': 'Net Calorific Value(ARB)',
    'GCV(ARB)': 'Gross Calorific Value(ARB)',
    'GCV(ADB)': 'Gross Calorific Value(ADB)',
    'GCV(DB)': 'Gross Calorific Value(DB)'
  };
  
  const qualityOption = cvBasisToQualityOption[newCVBasis];
   
  // Find the parameter ID for the calorific value parameter (ID should be 3 based on the API response)
  const calorificValueParamId = qualityParameters.find(param => 
    param.description === 'Net Calorific Value(ARB)' || 
    param.description.includes('Calorific Value')
  )?.id;
  
  if (calorificValueParamId && qualityOption) {
    console.log(`Updating quality parameter ${calorificValueParamId} to ${qualityOption}`);
    // Update the dropdown in the quality table
    setSelectedOptions(prevOptions => ({
      ...prevOptions,
      [calorificValueParamId]: qualityOption
    }));
  }
  
  handleInputChange('Base Price', 'CV Basis', e.target.value);
};

  // Function to add a new custom quality row
  const addCustomQualityRow = () => {
    // Get the highest order number from existing rows or default to 0
    const highestOrder = customQualityRows.length > 0 
      ? Math.max(...customQualityRows.map(row => row.order || 0)) 
      : 0;
    
    const newRow = {
      id: Date.now(), // Use timestamp as a simple unique ID
      propertyName: '',
      unit: '', // Add unit field
      typical_or_guaranteed_value: '',
      min_max_or_rejection_value: '',
      order: highestOrder + 1 // Add order property to preserve display order
    };
    setCustomQualityRows([...customQualityRows, newRow]);
  };

  // Function to delete a custom quality row
  const deleteCustomQualityRow = (id) => {
    console.log(`Deleting custom quality row with id: ${id}`);
    
    // Remove the row from the customQualityRows state
    const updatedRows = customQualityRows.filter(row => row.id !== id);
    setCustomQualityRows(updatedRows);
    
    // Remove the entire entry from qualityValues state instead of just clearing field values
    setQualityValues(prevValues => {
      const newValues = {...prevValues};
      // Delete the entire entry for this custom row
      delete newValues[`custom_${id}`];
      return newValues;
    });
    
    // Track this row as deleted if it was previously saved
    if (savedCustomRows.includes(id)) {
      setDeletedCustomRows(prev => [...prev, id]);
      unsaveCustomQualityRow(id);
    }
  };

  // Function to update a custom row property
  const updateCustomRow = (id, field, value) => {
    console.log(`Updating custom quality row: id=${id}, field=${field}, value=${value}`);
    
    const updatedRows = customQualityRows.map(row => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setCustomQualityRows(updatedRows);
    
    // Update the form data with the custom property values
    if (field === 'typical_or_guaranteed_value' || field === 'min_max_or_rejection_value') {
      handleQualityInputChange(`custom_${id}`, field, value);
    } else if (field === 'propertyName' || field === 'unit' || field === 'order') {
      // Store property name, unit, and order in quality values
      setQualityValues(prevValues => ({
        ...prevValues,
        [`custom_${id}`]: {
          ...prevValues[`custom_${id}`],
          [field]: value
        }
      }));
      
      // Enable autosave for custom quality row changes
      setHasUnsavedChanges(true);
    }
  };
  
  // Function to save a custom quality row (marks it as permanent)
  const saveCustomQualityRow = (id) => {
    console.log(`Saving custom quality row with id: ${id}`);
    
    // Find the row to save
    const rowToSave = customQualityRows.find(row => row.id === id);
    
    // Save the row without length restrictions on property name
    if (rowToSave) {
      // Add to saved rows list
      setSavedCustomRows(prev => [...prev, id]);
    }
  };
  
  // Function to toggle a row from saved back to editable
  const unsaveCustomQualityRow = (id) => {
    console.log(`Unsaving custom quality row with id: ${id}`);
    setSavedCustomRows(prev => prev.filter(rowId => rowId !== id));
  };

  // Function to toggle delete mode
  const toggleDeleteMode = () => {
    setDeleteModeActive(!deleteModeActive);
  };
  
  // Function to delete a selected saved row
  const deleteSavedRow = (id) => {
    // Unsave and delete the row
    unsaveCustomQualityRow(id);
    deleteCustomQualityRow(id);
    // Exit delete mode
    setDeleteModeActive(false);
  };

  // Extract custom rows from saved offer data
  const extractCustomQualityRows = () => {
    // Process qualityValues to find custom rows
    const customRows = Object.entries(qualityValues)
      .filter(([paramId, _]) => paramId.toString().startsWith('custom_'))
      .map(([paramId, values], index) => {
        const id = paramId.replace('custom_', '');
        return {
          id,
          propertyName: values.propertyName || '',
          unit: values.unit || '',
          typical_or_guaranteed_value: values.typical_or_guaranteed_value || '',
          min_max_or_rejection_value: values.min_max_or_rejection_value || '',
          // Use existing order if available, otherwise default to index for new items
          order: values.order !== undefined ? values.order : index
        };
      });
    
    if (customRows.length > 0 && customQualityRows.length === 0) {
      console.log('Found custom quality rows in saved data:', customRows);
      // Sort rows by order field in ascending order (not by database ID)
      const sortedRows = [...customRows].sort((a, b) => {
        // If orders are the same, maintain insertion order
        if (a.order === b.order) return 0;
        // Handle nulls by treating them as highest values
        if (a.order === null) return 1;
        if (b.order === null) return -1;
        // Normal numeric sort
        return a.order - b.order;
      });
      setCustomQualityRows(sortedRows);
      // Mark them all as saved since they came from the backend
      setSavedCustomRows(sortedRows.map(row => row.id));
    }
  };

  const handleProRataChange = (e) => {
    const newProRata = e.target.value;
    setQualityAdjustmentsProRata(newProRata);
    setManuallySelectedProRata(true); // Mark as manually selected
  };
const handlePaymentMethodChange = (e) => {
  const newPaymentMethod = e.target.value;
  setSelectedPaymentMethod(newPaymentMethod);
  setManuallySelectedPaymentMethod(true); // Mark as manually selected
  
  // Clear payment wording if Custom Wording is selected
  if (newPaymentMethod === 'Custom Wording') {
    setPaymentWording('');
  }
};
// Pledge (go-live checklist), next to the payment terms. Our company on the
// deal is the seller on an Offer and the buyer on a Bid; the pledge bank must
// be one it holds accounts at (the server refuses any other).
const pledgeEntity = dealType === 'Bid' ? selectedBuyer : selectedSeller;
const handlePledgeChange = (next) => {
  setPledge(next);
  setHasUnsavedChanges(true);
};
const handleShippingTermsChange = (e) => {
  const newShippingTerms = e.target.value;
  setSelectedShippingMethod(newShippingTerms);
  setManuallySelectedShippingTerms(true); // Mark as manually selected
  
  // Clear shipping wording if Custom Wording is selected
  if (newShippingTerms === 'Custom Wording') {
    setShippingWording('');
  }
};
const handleOtherTermsChange = (e) => {
  const newOtherTerms = e.target.value;
  setSelectedOtherTermsMethod(newOtherTerms);
  setManuallySelectedOtherTerms(true); // Mark as manually selected
  
  // Clear other terms wording if Custom Wording is selected
  if (newOtherTerms === 'Custom Wording') {
    setOtherTermsWording('');
  }
};
const handleTimeZoneChange = (e) => {
  const newTimeZone = e.target.value;
  setOfferValidityTimeZone(newTimeZone);
  setManuallySelectedTimeZone(true); // Mark as manually selected
};

  // The offer text for a deal with several products: a product table under
  // Product & Origin; Quantity and Base Price point at it. null otherwise.
  const severalText = (clauseName, displayOrigin = selectedOrigin) => severalProductsClauseText(clauseName, {
    lines: offerProducts,
    origin: displayOrigin,
    unit: quantityUnit,
    currency: basePriceCurrency,
    priceType,
    tolerance: quantityTolerance,
    option: quantityOption,
    incoterm: selectedIncoterm,
    portType,
    cvBasis: basePriceCVBasis,
    kcal: basePriceKcalKg,
    indexPeriod: basePriceApplicableIndexPeriod,
  });

  // Helper function to build formatted text for each clause
  function buildFormattedText(clauseName) {
    if (clauseName === 'Seller') {
      return `Seller: ${selectedSeller || 'No seller selected'}`;
    }
    if (clauseName === 'Buyer') {
      return `Buyer: ${selectedBuyer || 'No buyer specified'}`;
    }
    if (clauseName === 'Incoterms') {
      return selectedIncoterm ? `Incoterms: ${selectedIncoterm} as per Incoterms® 2020` : 'No incoterm selected';
    }
    if (clauseName === 'Commodity') {
      const productMapping = {
        'Met Coke': 'Metallurgical Coke',
        'Pet Coke': 'Petroleum Coke'
      };
      const originMapping = {
        'Australia': 'Australian',
        'Columbia': 'Colombian',
        'Indonesia': 'Indonesian',
        'Mozambique': 'Mozambican',
        'Poland': 'Polish',
        'Russia': 'Russian',
        'Saudi Arabia': 'Saudi Arabian',
        'South Africa': 'South African',
        'USA': 'American',
        'Vietnam': 'Vietnamese'
      };
      const displayProduct = productMapping[selectedProduct] || selectedProduct || 'No product selected';
      const displayOrigin = originMapping[selectedOrigin] || selectedOrigin;
      const severalCommodity = severalText('Commodity', displayOrigin);
      
      let text = severalCommodity ? severalCommodity : selectedProduct && selectedOrigin 
        ? `Product & Origin: ${displayProduct} in bulk of ${displayOrigin} Origin`
        : `Product & Origin: ${displayProduct}${selectedOrigin ? ` in bulk of ${displayOrigin} Origin` : ''}`;
      
      if (additionalInfo) {
        text += `\n${additionalInfo}`;
      }
      return text;
    }
    if (clauseName === 'Quantity') {
      const severalQuantity = severalText('Quantity');
      if (severalQuantity) return severalQuantity;
      let text = `Quantity: ${quantityWeight || ''}`;
      if (quantityUnit) {
        text += ` ${quantityUnit}`;
      }
      if (quantityTolerance) {
        text += ` with +/-${quantityTolerance}% tolerance`;
      }
      if (quantityOption) {
        text += ` in ${quantityOption?.replace('Quantity in ', '')}`;
      }
      return text;
    }
    if (clauseName === 'Delivery') {
      return `Delivery: ${delivery}`;
    }
    if (clauseName === 'Loading Port(s)') {
      return `${portType}: ${loadingPort}`;
    }
    if (clauseName === 'Base Price') {
      const severalPrice = severalText('Base Price');
      if (severalPrice) return severalPrice;
      const priceDisplay = priceType === 'Fix Price'
        ? `${basePriceCurrency} ${basePriceForFixedPrice}`
        : `${basePriceCurrency} ${formatFigure(Math.abs(parseFloat(basePriceForIndex || 0)), 2, PRICE_DECIMALS)}`;
      
      let priceTypeDisplay = '';
      if (priceType !== 'Fix Price') {
        const indexValue = parseFloat(basePriceForIndex || 0);
        priceTypeDisplay = `${priceType} ${indexValue < 0 ? 'minus' : 'plus'} `;
      }
      
      let text = `Base Price: ${priceTypeDisplay}${priceDisplay} per ${quantityUnit || 'metric ton'} ${selectedIncoterm} ${portType}`;
      if (basePriceKcalKg) {
        text += ` ${basePriceCVBasis} ${basePriceKcalKg} kcal/kg`;
      }
      if (priceType !== 'Fix Price' && basePriceApplicableIndexPeriod) {
        text += `\nApplicable Index Period: ${basePriceApplicableIndexPeriod.replace('[INDEX]', priceType)}`;
      }
      return text;
    }
    if (clauseName === 'Quality and Quantity Determination') {
      return `Quality and Quantity Determination: Quality and Quantity shall be determined at the Loading port by ${qualityAndQuantityDeterminationSurveyor || 'an internationally acknowledged Inspection Company'} as appointed and paid by the Seller.\nQuantity will be determined by means of draft survey of the vessel. BL-Weight will be final and binding for both parties and basis for invoicing.`;
    }
    if (clauseName === 'Deal Validity') {
      return isIndicationOnly 
        ? 'This offer is of indicative nature only and can be turned into a firm offer upon request and subject to Seller\'s reconfirmation and cargo unsold.' 
        : formatValidityDateTime(offerValidityDate, offerValidityTime, offerValidityTimeZone);
    }
    return '';
  }

  //Render uneditable textbox
  function renderUneditableTextbox(clauseName) {
    if (clauseName === 'Seller') {
      const isSellerUpdated = updatedFields['selectedSeller'] && !manuallySelectedSeller; // Check if the seller field is updated
      const builtText = `Seller: ${selectedSeller || 'No seller selected'}`;
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (sellerFormattedText && sellerFormattedText !== 'None') ? sellerFormattedText : builtText;
  
      return (
        <div 
          className={`uneditable-textbox ${isSellerUpdated ? 'highlighted-field-uneditable' : ''}`}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setSellerFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }

    if (clauseName === 'Buyer') {
      const isBuyerUpdated = updatedFields['selectedBuyer'] && !manuallySelectedBuyer; // Check if the buyer field is updated
      const builtText = `Buyer: ${selectedBuyer || 'No buyer specified'}`;
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (buyerFormattedText && buyerFormattedText !== 'None') ? buyerFormattedText : builtText;
      
      return (
        <div 
          className={`uneditable-textbox ${isBuyerUpdated ? 'highlighted-field-uneditable' : ''}`}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setBuyerFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }

    if (clauseName === 'Incoterms') {
      const isIncotermUpdated = updatedFields['selectedIncoterm'] && !manuallySelectedIncoterm; // Check if the incoterm field is updated
      const builtText = selectedIncoterm ? `Incoterms: ${selectedIncoterm} as per Incoterms® 2020` : 'No incoterm selected';
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (incotermFormattedText && incotermFormattedText !== 'None') ? incotermFormattedText : builtText;
      
      return (
        <div 
          className={`uneditable-textbox ${isIncotermUpdated ? 'highlighted-field-uneditable' : ''}`}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setIncotermFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }
    if (clauseName === 'Commodity') {
      const isProductUpdated = updatedFields['selectedProduct'] && !manuallySelectedProduct;
      const isOriginUpdated = updatedFields['selectedOrigin'] && !manuallySelectedOrigin;
      const isAdditionalInfoUpdated = updatedFields['additionalInfo'] && !manuallySelectedCommodityAdditionalInfo;
      
      const productMapping = {
        'Met Coke': 'Metallurgical Coke',
        'Pet Coke': 'Petroleum Coke'
      };
  
      const originMapping = {
        'Australia': 'Australian',
        'Columbia': 'Colombian',
        'Indonesia': 'Indonesian',
        'Mozambique': 'Mozambican',
        'Poland': 'Polish',
        'Russia': 'Russian',
        'Saudi Arabia': 'Saudi Arabian',
        'South Africa': 'South African',
        'USA': 'American',
        'Vietnam': 'Vietnamese'
      };
      const displayProduct = productMapping[selectedProduct] || selectedProduct || 'No product selected';
      const displayOrigin = originMapping[selectedOrigin] || selectedOrigin;
      const severalCommodity = severalText('Commodity', displayOrigin);
  
      let builtText = severalCommodity ? severalCommodity : selectedProduct && selectedOrigin 
        ? `Product & Origin: ${displayProduct} in bulk of ${displayOrigin} Origin`
        : `Product & Origin: ${displayProduct}${selectedOrigin ? ` in bulk of ${displayOrigin} Origin` : ''}`;
      
      if (additionalInfo) {
        builtText += `\n${additionalInfo}`;
      }
      
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (commodityFormattedText && commodityFormattedText !== 'None') ? commodityFormattedText : builtText;
  
      return (
        <div 
          className={`uneditable-textbox ${isProductUpdated || isOriginUpdated || isAdditionalInfoUpdated ? 'highlighted-field-uneditable' : ''}`}
          style={{ whiteSpace: 'pre-wrap' }}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setCommodityFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }
    if (clauseName === 'Quantity') {
      const isQuantityOptionUpdated = updatedFields['quantityOption'] && !manuallySelectedQuantityOption;
      const isWeightUpdated = updatedFields['quantityWeight'] && !manuallySelectedQuantityWeight;
      const isToleranceUpdated = updatedFields['quantityTolerance'] && !manuallySelectedQuantityTolerance;
    
      // Build dynamic quantity text
      let builtText = `Quantity: ${quantityWeight || ''}`;
      if (quantityUnit) {
        builtText += ` ${quantityUnit}`;
      }
      if (quantityTolerance) {
        builtText += ` with +/-${quantityTolerance}% tolerance`;
      }
      if (quantityOption) {
        builtText += ` in ${quantityOption?.replace('Quantity in ', '')}`;
      }
      
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      builtText = severalText('Quantity') || builtText;
      const displayText = (quantityFormattedText && quantityFormattedText !== 'None') ? quantityFormattedText : builtText;
      
      return (
        <div 
          className={`uneditable-textbox ${isQuantityOptionUpdated || isWeightUpdated || isToleranceUpdated ? 'highlighted-field-uneditable' : ''}`}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setQuantityFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }

    if (clauseName === 'Delivery') {
      const isDeliveryUpdated = updatedFields['delivery'] && !manuallySelectedDelivery; // Check if the delivery field is updated
      const builtText = `Delivery: ${delivery}`;
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (deliveryFormattedText && deliveryFormattedText !== 'None') ? deliveryFormattedText : builtText;
      
      return (
        <div 
          className={`uneditable-textbox ${isDeliveryUpdated ? 'highlighted-field-uneditable' : ''}`}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setDeliveryFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }

    if (clauseName === 'Loading Port(s)') {
      const isLoadingPortUpdated = updatedFields['loadingPort'] && !manuallySelectedLoadingPort;
      const builtText = `${portType}: ${loadingPort}`;
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (loadingPortFormattedText && loadingPortFormattedText !== 'None') ? loadingPortFormattedText : builtText;
      
      return (
        <div 
          className={`uneditable-textbox ${isLoadingPortUpdated ? 'highlighted-field-uneditable' : ''}`}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setLoadingPortFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }
    if (clauseName === 'Base Price') {
      const isPriceTypeUpdated = updatedFields['priceType'] && !manuallySelectedPriceType;
      const isCurrencyUpdated = updatedFields['basePriceCurrency'] && !manuallySelectedCurrency;
      const isCVBasisUpdated = updatedFields['basePriceCVBasis'] && !manuallySelectedCVBasis;
      const isKcalKgUpdated = updatedFields['basePriceKcalKg'] && !manuallySelectedBasePriceKcalKg;
      const isApplicableIndexPeriodUpdated = updatedFields['basePriceApplicableIndexPeriod'] && !manuallySelectedBasePriceApplicableIndexPeriod;
      const isForFixedPriceUpdated = updatedFields['basePriceForFixedPrice'] && !manuallySelectedBasePriceForFixedPrice;
      const isForIndexPremiumDiscountUpdated = updatedFields['basePriceForIndexPremiumDiscount'] && !manuallySelectedBasePriceForIndexPremiumDiscount;

      const priceDisplay =
        priceType === 'Fix Price'
          ? `${basePriceCurrency} ${basePriceForFixedPrice}`
          : `${basePriceCurrency} ${formatFigure(Math.abs(parseFloat(basePriceForIndex || 0)), 2, PRICE_DECIMALS)}`;

      let priceTypeDisplay = '';
      if (priceType !== 'Fix Price') {
        const indexValue = parseFloat(basePriceForIndex || 0);
        priceTypeDisplay = `${priceType} ${indexValue < 0 ? 'minus' : 'plus'} `;
      }

      // Build text from inputs
      let builtText = `Base Price: ${priceTypeDisplay}${priceDisplay} per ${quantityUnit || 'metric ton'} ${selectedIncoterm} ${portType}`;
      if (basePriceKcalKg) {
        builtText += ` ${basePriceCVBasis} ${basePriceKcalKg} kcal/kg`;
      }
      if (priceType !== 'Fix Price' && basePriceApplicableIndexPeriod) {
        builtText += `\nApplicable Index Period: ${basePriceApplicableIndexPeriod.replace('[INDEX]', priceType)}`;
      }
      
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      builtText = severalText('Base Price') || builtText;
      const displayText = (priceFormattedText && priceFormattedText !== 'None') ? priceFormattedText : builtText;

      return (
        <div 
          className={`uneditable-textbox ${isPriceTypeUpdated || isCurrencyUpdated || isCVBasisUpdated || isKcalKgUpdated || isApplicableIndexPeriodUpdated || isForFixedPriceUpdated || isForIndexPremiumDiscountUpdated ? 'highlighted-field-uneditable' : ''}`} 
          style={{ display: 'block', whiteSpace: 'pre-wrap' }}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setPriceFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }

    

    if (clauseName === 'Quality Adjustments') {
      const cvBasis = offerData['Base Price']?.['CV Basis'] || 'CV Basis';
      const kcalKg = offerData['Base Price']?.['Kcal/Kg'] || 'kcal/kg';
      const currency = offerData['Base Price']?.['Currency'] || 'USD';
      const incoterm = offerData['Incoterms']?.['Incoterm'] || 'FOB';
      const isProRataUpdated = updatedFields['qualityAdjustmentsProRata'] && !manuallySelectedProRata; // Check if the pro rata field is updated
      const isOtherPremiumPenaltiesUpdated = updatedFields['qualityAdjustmentsOtherPremiumPenalties'] && !manuallySelectedQualityAdjustmentsOtherPremiumPenalties; // Check if the other premium penalties field is updated
  
      let dynamicStandardWording;
  
      if (qualityAdjustmentsProRata === "Yes(Excl. Freight Portion)") {
        dynamicStandardWording = `If the actual ${cvBasis} as reported by the independent surveyor at the loading port is above or below ${kcalKg} kcal/kg, the Price shall be adjusted on the FOB Portion on pro rata basis in accordance with the following formula:
  
        FOB Base Price = ${currency} XX.XX (to be declared)
        Freight Portion = ${currency} XX.XX (to be declared)
  
        Adjusted ${incoterm} Price = FOB Base Price x Actual ${cvBasis} / ${kcalKg} kcal/kg + Freight Portion`;
      } else if (qualityAdjustmentsProRata === "No") {
        dynamicStandardWording = `None`;
      } else {
        dynamicStandardWording = `If the actual ${cvBasis} as reported by the independent surveyor at the loading port is above or below ${kcalKg} kcal/kg, the Base Price shall be adjusted on pro rata basis in accordance with the following formula: Adjusted Price = Base price x Actual ${cvBasis} / ${kcalKg} kcal/kg`;
      }
  
      return (
        <div className={`uneditable-textbox ${isProRataUpdated || isOtherPremiumPenaltiesUpdated ? 'highlighted-field-uneditable' : ''}`}>
          Quality Adjustments: {dynamicStandardWording}
          <br />
          {qualityAdjustmentsOtherPremiumPenalties}
        </div>
      );
    }

    if (clauseName === 'Quality and Quantity Determination') {
      const isSurveyorUpdated = updatedFields['qualityAndQuantityDeterminationSurveyor'] && !manuallySelectedSurveyor;
      
      const builtText = `Quality and Quantity Determination: Quality and Quantity shall be determined at the Loading port by ${qualityAndQuantityDeterminationSurveyor || 'an internationally acknowledged Inspection Company'} as appointed and paid by the Seller.\nQuantity will be determined by means of draft survey of the vessel. BL-Weight will be final and binding for both parties and basis for invoicing.`;
      
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (qualityQuantityFormattedText && qualityQuantityFormattedText !== 'None') ? qualityQuantityFormattedText : builtText;
      
      return (
        <div 
          className={`uneditable-textbox ${isSurveyorUpdated ? 'highlighted-field-uneditable' : ''}`}
          style={{ whiteSpace: 'pre-wrap' }}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setQualityQuantityFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }

    if (clauseName === 'Payment') {
      const isPaymentMethodUpdated = updatedFields['selectedPaymentMethod'] && !manuallySelectedPaymentMethod; // Check if the payment method field is updated
      return (
        <div className={`uneditable-textbox ${isPaymentMethodUpdated ? 'highlighted-field-uneditable' : ''}`}>
          Payment: {paymentWording}
        </div>
      );
    }

    if (clauseName === 'Shipping Terms') {
      const isShippingMethodUpdated = updatedFields['selectedShippingMethod'] && !manuallySelectedShippingTerms;
      return (
        <div className={`uneditable-textbox ${isShippingMethodUpdated ? 'highlighted-field-uneditable' : ''}`}>
          Shipping Terms: {shippingWording}
        </div>
      );
    }

    if (clauseName === 'Other Terms') {
      const isOtherTermsUpdated = updatedFields['selectedOtherTermsMethod'] && !manuallySelectedOtherTerms; // Check if the other terms field is updated
      return (
        <div className={`uneditable-textbox ${isOtherTermsUpdated ? 'highlighted-field-uneditable' : ''}`}>
          Other Terms: {otherTermsWording}
        </div>
      );
    }

    if (clauseName === 'Deal Validity') {
      const isDateUpdated = updatedFields['offerValidityDate'] && !manuallySelectedOfferValidityDate; // Check if the date field is updated
      const isTimeUpdated = updatedFields['offerValidityTime'] && !manuallySelectedOfferValidityTime; // Check if the time field is updated
      const isTimeZoneUpdated = updatedFields['offerValidityTimeZone'] && !manuallySelectedTimeZone; // Check if the time zone field is updated
      
      const builtText = isIndicationOnly 
        ? 'This offer is of indicative nature only and can be turned into a firm offer upon request and subject to Seller\'s reconfirmation and cargo unsold.' 
        : formatValidityDateTime(offerValidityDate, offerValidityTime, offerValidityTimeZone);
      
      // Use saved formatted text if it exists and is not 'None', otherwise use built text
      const displayText = (dealValidityFormattedText && dealValidityFormattedText !== 'None') ? dealValidityFormattedText : builtText;
      
      return (
        <div 
          className={`uneditable-textbox ${isDateUpdated || isTimeUpdated || isTimeZoneUpdated ? 'highlighted-field-uneditable' : ''}`}
          contentEditable={true}
          suppressContentEditableWarning={true}
          onBlur={(e) => setDealValidityFormattedText(e.target.innerText)}
        >
          {displayText}
        </div>
      );
    }

    return (
      <div className="uneditable-textbox">
        {/* Default content for other clauses */}
        This is an uneditable textbox.
      </div>
    );
  }

  // Add this new handler function with your other handlers
  const handleDealTypeChange = (e) => {
    const isBid = e.target.checked;
    setIsDealTypeBid(isBid);
    setDealType(isBid ? 'Bid' : 'Offer');

    // Only swap values if not viewing a draft
    if (!isViewingDraft) {
      // Get current seller and buyer values
      const currentSeller = offerData['Seller']?.['Selling Entity'] || '';
      const currentBuyer = offerData['Buyer']?.["Buyer's Entity"] || '';

      // Swap the values
      setSelectedSeller(currentBuyer);
      setSelectedBuyer(currentSeller);
      
      // Update offerData with swapped values
      setOfferData(prevData => ({
        ...prevData,
        'Seller': { 'Selling Entity': currentBuyer },
        'Buyer': { "Buyer's Entity": currentSeller }
      }));

      // Set both as manually selected to prevent overwriting
      setManuallySelectedSeller(true);
      setManuallySelectedBuyer(true);
    }
  };

  //Handle quality input change for typical and min/max values 
  const handleQualityInputChange = (paramId, field, value) => {
    setQualityValues(prevValues => ({
      ...prevValues,
      [paramId]: {
        ...prevValues[paramId],
        [field]: value
      }
    }));
    
    // Enable autosave for quality changes
    setHasUnsavedChanges(true);
  };

  const handlePresetSelection = (presetName) => {
    const selectedPreset = qualityPresets[presetName];
    if (selectedPreset) {
      // console.log('Selected Preset: ', selectedPreset);
        // Map preset values to match the structure used in qualityValues
        const mappedValues = {};
        Object.entries(selectedPreset.values).forEach(([key, value]) => {
            mappedValues[key] = {
                typical_or_guaranteed_value: value.typical_or_guaranteed_value,
                min_max_or_rejection_value: value.min_max_or_rejection_value
            };
        });

        // Update quality values
        setQualityValues(mappedValues);

        // Update column names
        setQualityColumnHeader(selectedPreset.columnNames.thirdColumn);
        setMinMaxColumnHeader(selectedPreset.columnNames.fourthColumn);

        // Update selected options
        setSelectedOptions(selectedPreset.options);
    }
};

  const handleDropdownChange = (paramId, value) => {
    console.log(`Quality dropdown changed: paramId=${paramId}, value=${value}`);
    setSelectedOptions(prevOptions => ({
      ...prevOptions,
      [paramId]: value
    }));
    
    // Check if this is a calorific value parameter (ID should be 3 based on the API response)
    // Also check if the value is related to calorific value
    if ((paramId === 3 || 
        qualityParameters.find(p => p.id === paramId)?.description.includes('Calorific Value')) && 
        value && (value.includes('Calorific Value') || value.includes('Net') || value.includes('Gross'))) {
      
      // Map the quality option to CV Basis format
      const qualityOptionToCVBasis = {
        'Net Calorific Value(ARB)': 'NCV(ARB)',
        'Gross Calorific Value(ARB)': 'GCV(ARB)', 
        'Gross Calorific Value(ADB)': 'GCV(ADB)',
        'Gross Calorific Value(DB)': 'GCV(DB)'
      };
      
      const cvBasisValue = qualityOptionToCVBasis[value];
      if (cvBasisValue) {
        console.log(`Updating CV Basis to ${cvBasisValue} based on quality parameter change`);
        setBasePriceCVBasis(cvBasisValue);
        
        // Update the form data as well to ensure it's properly reflected
        setOfferData(prevData => ({
          ...prevData,
          'Base Price': {
            ...(prevData['Base Price'] || {}),
            'CV Basis': cvBasisValue
          }
        }));
      }
    }
    
    // Enable autosave for quality dropdown changes
    setHasUnsavedChanges(true);
  };

  //Handle offer submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.put(`/api/offer/${offerId}/update/`, offerData);
      // Handle successful submission
    } catch (error) {
      console.error('Error submitting offer:', error);
      // Handle error
    }
  };

// Helper function to check if any changes were made
const hasActualChanges = () => {
  if (!originalDataRef.current || !originalDataRef.current.apiResponse) {
    return false; // No original data to compare
  }

  const original = originalDataRef.current.apiResponse;
  
  // Check all the key fields that matter
  const fieldsToCheck = [
    { current: selectedSeller, original: original.selling_company_entity },
    { current: selectedBuyer, original: original.buying_company_entity },
    { current: selectedIncoterm, original: original.trade_incoterm_type },
    { current: selectedProduct, original: original.commodity_product_type },
    { current: selectedOrigin, original: original.commodity_origin_country },
    { current: additionalInfo, original: original.commodity_additional_details },
    { current: quantityWeight, original: original.quantity_in_metric_tons },
    { current: quantityUnit, original: original.quantity_unit },
    { current: quantityTolerance, original: original.quantity_tolerance_percentage },
    { current: quantityOption, original: original.quantity_option_holder },
    { current: delivery, original: original.delivery_terms_and_conditions },
    { current: loadingPort, original: (original.trade_incoterm_type === 'FOB' ? original.loading_ports_details : original.discharging_ports_details) },
    { current: basePriceCurrency, original: original.price_currency_type },
    { current: basePriceForFixedPrice, original: original.price_fixed_amount },
    { current: basePriceForIndex, original: original.price_premium_or_discount },
    { current: basePriceCVBasis, original: original.price_cv_basis_type },
    { current: basePriceKcalKg, original: original.price_kcal_per_kg },
    { current: selectedPaymentMethod, original: original.payment_method_type },
    { current: pledge.is_pledged ? 'yes' : '', original: original.is_pledged ? 'yes' : '' },
    { current: pledge.pledge_bank_key, original: original.pledge_bank_key },
    { current: paymentWording, original: original.payment_detailed_terms },
    { current: selectedShippingMethod, original: original.shipping_terms_type },
    { current: shippingWording, original: original.shipping_detailed_conditions },
    { current: selectedOtherTermsMethod, original: original.other_terms_category },
    { current: otherTermsWording, original: original.other_terms_detailed_conditions },
    { current: offerValidityDate, original: original.deal_validity_date },
    { current: offerValidityTime, original: original.deal_validity_time },
    { current: offerValidityTimeZone, original: original.deal_validity_timezone },
    { current: customDealName, original: original.deal_display_name },
    { current: isIndicationOnly, original: original.offer_is_indication_only },
    {
      current: linesSignature(offerProducts, OFFER_LINE_KEYS),
      original: linesSignature(severalLines(original.products), OFFER_LINE_KEYS),
    },
  ];

  // Check if any field has changed
  for (const field of fieldsToCheck) {
    const currentVal = field.current || '';
    const originalVal = field.original || '';
    if (currentVal !== originalVal) {
      console.log('[CHANGE DETECTION] Found change:', { current: currentVal, original: originalVal });
      return true;
    }
  }

  console.log('[CHANGE DETECTION] No changes detected');
  return false;
};

// Handle back button click
// Handle back button click
const handleBackClick = () => {
  // Cancel any pending autosave
  if (debouncedSaveRef.current && debouncedSaveRef.current.cancel) {
    debouncedSaveRef.current.cancel();
    console.log('[AUTOSAVE] Canceled pending autosave operation');
  }

  // For existing deals, check if there are actual changes before showing the popup
  if (isDealInDatabase && offerId && originalState) {
    // Only show popup if there are actual changes
    if (hasActualChanges()) {
      setShowPopup(true);
      return;
    } else {
      // No changes, just navigate back
      console.log('[AUTOSAVE] No changes detected, navigating back without prompt');
      goToTradeOps(history, tradeOpsDesk, {
        selectedDate: location.state?.selectedDate,
        activeTab: location.state?.activeTab || 'deals',
      });
      return;
    }
  }

  // If we have an offerId but it's not from query params AND we're not in grouping mode AND it's in the database
  if (offerId && !offerIdFromQuery && !isGrouping && isDealInDatabase) {
    setShowPopup(true); // Show confirmation popup for deletion
  } else {
    // For deals not yet in the database, just navigate back without confirmation and restore previous state
    goToTradeOps(history, tradeOpsDesk, {
      selectedDate: location.state?.selectedDate,
      activeTab: location.state?.activeTab || 'deals',
    }); // Go back to trade operations dashboard
  }
};

  //Handle popup close
  const handlePopupClose = () => {
    setShowPopup(false);
  };

  const handleDraftAndLeave = async () => {
    try {
      // Save as draft using existing saveOfferData function
      const saved = await saveOfferData(false);
      if (saved) {
        localStorage.removeItem('previousOfferId');
        
        // The saveOfferData function properly resets change tracking
        // Close modal and navigate away
        setShowPopup(false);
        
        goToTradeOps(history, tradeOpsDesk);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      showError('Failed to save draft. Please try again.');
    }
  };

  //Handle confirm back
//Handle confirm back
const handleConfirmBack = async () => {
  try {
    // Cancel any pending autosave
    if (debouncedSaveRef.current && debouncedSaveRef.current.cancel) {
      debouncedSaveRef.current.cancel();
      console.log('[AUTOSAVE] Canceled pending autosave on confirm back');
    }
    
    // Always roll back changes for existing deals if we have the original data
    if (isDealInDatabase && offerId && originalDataRef.current) {
      console.log('[AUTOSAVE] Rolling back changes for existing deal');
      await rollbackChanges();
    }
    
    console.log('Attempting to delete offer:', offerId);
    // Only delete if the deal is in the database and we didn't come from a URL with offerId
    if (isDealInDatabase && offerId && !offerIdFromQuery && !isGrouping) {
      await axiosInstance.delete(`/api/offer/user-offers/${offerId}/`);
      localStorage.removeItem('previousOfferId');
      console.log('Successfully deleted offer:', offerId);
    }
    
    goToTradeOps(history, tradeOpsDesk, {
      selectedDate: location.state?.selectedDate,
      activeTab: location.state?.activeTab || 'deals',
    }); // Go back to trade operations dashboard
  } catch (error) {
    console.error('Error handling back button:', error);
    goToTradeOps(history, tradeOpsDesk, {
      selectedDate: location.state?.selectedDate,
      activeTab: location.state?.activeTab || 'deals',
    }); // Navigate even if deletion fails
  }
};

  //Save offer data function
  // Extracted saveOfferData function to handle data saving
  const saveOfferData = async (forRecap = false, isAutosave = false) => {
    try {
      const userId = parseInt(localStorage.getItem('user_id'), 10);

      // First handle quality data
      console.log('Current selectedOptions before saving:', selectedOptions);
      
      // Process quality data for all saves including autosave
      let qualityData = Object.keys(qualityValues).map(paramId => {
        // Check if this is a custom parameter
        const isCustomParam = paramId.toString().startsWith('custom_');
        
        const data = {
          param_id: paramId,  // Changed back to param_id as expected by backend
          typical_or_guaranteed_value: qualityValues[paramId]?.['typical_or_guaranteed_value'],
          min_max_or_rejection_value: qualityValues[paramId]?.['min_max_or_rejection_value'],
          selected_option: isCustomParam ? null : (selectedOptions[paramId] || null),
          typical_or_guaranteed: qualityColumnHeader,
          minmax_or_rejection: minMaxColumnHeader
        };
        
        // Add property name and unit for custom parameters
        if (isCustomParam) {
          const customId = paramId.replace('custom_', '');
          const customRow = customQualityRows.find(row => row.id.toString() === customId.toString());
          if (customRow) {
            data.property_name = customRow.propertyName;
            data.unit = customRow.unit; // Add unit to the data being saved
            data.order = customRow.order; // Add order to the data being saved
            data.is_custom = true;
          }
        }

        console.log(`Saving quality parameter ${paramId}`, data);
        return data;
      });
      
      // Add information about deleted custom rows
      if (deletedCustomRows.length > 0) {
        qualityData.push({
          deleted_custom_rows: deletedCustomRows.map(id => id.toString())
        });
        console.log('Including deleted custom rows:', deletedCustomRows);
      }
      
      // Prepare offer data with actual values from the form
      const offerPayload = {
        commodity_type: tradeOpsDesk.commodity,
        offer_transaction_type: dealType,
        offer_creator_employee_id: userId,
        // If deal is already linked to a recap (has associatedTradeRecapId), keep it as 'Dormant'
        // Otherwise, use 'Dormant' if forRecap is true, or 'Draft' if false
        offer_current_status: associatedTradeRecapId ? 'Dormant' : (forRecap ? 'Dormant' : 'Draft'),
        offer_is_indication_only: isIndicationOnly,
        deal_display_name: customDealName || null,
        // Don't set offer_group_identifier at all for new offers
        ...(isGrouping ? { offer_group_identifier: groupId } : {}),
        // Clause values
        selling_company_entity: selectedSeller || '',
        selling_company_formatted_text: sellerFormattedText || buildFormattedText('Seller'),
        buying_company_entity: selectedBuyer || '',
        buying_company_formatted_text: buyerFormattedText || buildFormattedText('Buyer'),
        trade_incoterm_type: selectedIncoterm || '',
        trade_incoterm_formatted_text: incotermFormattedText || buildFormattedText('Incoterms'),
        commodity_product_type: selectedProduct || '',
        commodity_origin_country: selectedOrigin || '',
        commodity_additional_details: additionalInfo || '',
        commodity_formatted_text: commodityFormattedText || buildFormattedText('Commodity'),
        quantity_in_metric_tons: quantityWeight || '',
        quantity_formatted_text: quantityFormattedText || buildFormattedText('Quantity'),
        quantity_unit: quantityUnit || '',
        quantity_tolerance_percentage: formatToleranceField(
          quantityToleranceLowerValue,
          quantityToleranceLowerUnit,
          quantityToleranceUpperValue,
          quantityToleranceUpperUnit
        ) || quantityTolerance || '',
        quantity_tolerance_lower_value: quantityToleranceLowerValue || '',
        quantity_tolerance_lower_unit: quantityToleranceLowerUnit || '%',
        quantity_tolerance_upper_value: quantityToleranceUpperValue || '',
        quantity_tolerance_upper_unit: quantityToleranceUpperUnit || '%',
        quantity_option_holder: quantityOption || '',
        delivery_terms_and_conditions: delivery || '',
        delivery_formatted_text: deliveryFormattedText || buildFormattedText('Delivery'),
        loading_ports_details: selectedIncoterm === 'FOB' ? loadingPort : '',
        loading_ports_formatted_text: loadingPortFormattedText || buildFormattedText('Loading Port(s)'),
        discharging_ports_details: selectedIncoterm !== 'FOB' ? loadingPort : '',
        price_determination_type: offerData['Base Price']?.['Price type'] || '',
        price_currency_type: offerData['Base Price']?.['Currency'] || '',
        price_fixed_amount: offerData['Base Price']?.['Fixed Price'] || '',
        price_premium_or_discount: offerData['Base Price']?.['Premium/Discount'] || '',
        price_cv_basis_type: offerData['Base Price']?.['CV Basis'] || '',
        price_kcal_per_kg: offerData['Base Price']?.['Kcal/Kg'] || '',
        price_index_applicable_period: offerData['Base Price']?.['Applicable Index Period'] || '',
        price_formatted_text: priceFormattedText || buildFormattedText('Base Price'),
        payment_method_type: selectedPaymentMethod || '',
        ...pledgeFieldsFor(pledge),
        shipping_terms_type: selectedShippingMethod || '',
        other_terms_category: selectedOtherTermsMethod || '',
        deal_validity_date: offerValidityDate || '',
        deal_validity_time: offerValidityTime || '',
        deal_validity_timezone: offerValidityTimeZone || '',
        deal_validity_formatted_text: dealValidityFormattedText || buildFormattedText('Deal Validity'),
        // Add these missing fields for Quality Adjustments
        quality_prorata_cv_adjustments: qualityAdjustmentsProRata ? "Yes" : "No",  // Convert boolean to string
        quality_other_adjustments: qualityAdjustmentsOtherPremiumPenalties || '',
        quality_standard_wording: qualityAdjustmentsStandardWording || '',
        
        // Add these missing fields for Quality and Quantity Determination
        quality_quantity_surveyor_details: qualityAndQuantityDeterminationSurveyor || '',
        quality_quantity_formatted_text: qualityQuantityFormattedText || buildFormattedText('Quality and Quantity Determination'),
        
        // Add these missing fields for Payment, Shipping, Other Terms
        payment_detailed_terms: paymentWording || '',
        shipping_detailed_conditions: shippingWording || '',
        other_terms_detailed_conditions: otherTermsWording || '',
        
        // Fix Base Price fields
        price_fixed_amount: basePriceForFixedPrice || '',  // This was wrong in original
        price_premium_or_discount: basePriceForIndex || '', // This was wrong in original

        // The catalogue product picked (with several products the server
        // sets it from the first line).
        stock_item_variant: selectedProductVariantId || null,

        // Several products: the lines (two or more), or none. With lines the
        // server works out Product, Quantity and Price from them. Cargo deals
        // only: a bunker offer's save leaves its lines as they were.
        ...(tradeOpsDesk.commodity !== 'fuels'
          ? { products: offerProducts.length >= 2 ? linesForSave(offerProducts) : [] }
          : {}),

        // Hamster Builder log for recap reference
        ...(hamsterBuilderLog.length > 0 ? { hamster_builder_log: hamsterBuilderLog } : {}),
      };

      console.log(isAutosave ? 'Autosaving offer data...' : 'Saving offer data...', offerPayload);

      let savedOfferId;

      // Row 125: with a deal number in the address that deal hasn't loaded
      // (still on its way, or failed), and the branches below would post a
      // brand-new deal from the empty form instead of saving that one.
      if (offerIdFromQuery && (!offerId || dealLoadProblem)) {
        if (!isAutosave) {
          showError("This deal hasn't loaded, so it can't be saved. Reload the page and try again.", DEFAULT_DURATION);
        }
        return forRecap ? null : false;
      }

      if (isGrouping) {
        const response = await axiosInstance.post('/api/offer/user-offers/', offerPayload);
        setOfferProducts((current) => adoptSavedIds(current, response.data?.products));
        savedOfferId = response.data.offer_id;
        setAutomaticDealName(response.data.automatic_name);
        
        // Only show success message if not an autosave
        if (!isAutosave) {
          showSuccess('Deal saved successfully. Any associated recaps will be automatically synced.', 5000);
        }
      } else if (isDealInDatabase && offerId) {
        // Update existing offer
        const response = await axiosInstance.put(`/api/offer/user-offers/${offerId}/`, offerPayload);
        setOfferProducts((current) => adoptSavedIds(current, response.data?.products));
        savedOfferId = offerId;
        // The automatic name follows what was just saved.
        setAutomaticDealName(response.data.automatic_name);
        
        // Only show success message if not an autosave
        if (!isAutosave) {
          showSuccess('Deal updated successfully. Any associated recaps have been automatically synced.', 5000);
        }
      } else {
        // Create new offer
        const response = await axiosInstance.post('/api/offer/user-offers/', offerPayload);
        setOfferProducts((current) => adoptSavedIds(current, response.data?.products));
        savedOfferId = response.data.offer_id;
        setOfferId(savedOfferId);
        setAutomaticDealName(response.data.automatic_name);
        setCreatedBy({
          name: response.data.created_by_name || null,
          when: response.data.offer_creation_timestamp || null,
        });
        setIsDealInDatabase(true); // Mark as now in the database
        
        // Only show success message if not an autosave
        if (!isAutosave) {
          showSuccess('New deal created successfully.', 5000);
        }
      }
      
      // Only save quality data if we have an offer ID AND this is NOT an autosave AND we have quality data
      if (savedOfferId && !isAutosave && qualityData.length > 0) {
        await axiosInstance.post(
          `/api/offer/user-offers/${savedOfferId}/quality-values/`,
          { quality_data: qualityData }
        );
        
        // Clear the deleted custom rows tracking after successful save
        setDeletedCustomRows([]);
      }

      // If this was a successful save, clear the unsaved changes flag
      if (!isAutosave) {
        setHasUnsavedChanges(false);
      }
      
      return forRecap ? savedOfferId : true;
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Only show error message if not an autosave. A refused save names
      // its field and reason (the pledge bank, say).
      if (!isAutosave) {
        showError(extractApiError(error, 'Failed to save deal. Please try again.'), 5000);
      }
      
      return forRecap ? null : false;
    }
  };

  // Function to save Trade Recap. ``recapLines``: a deal with several
  // products sends the products this recap takes (else null).
  const saveTradeRecap = async (offerId, recapLines = null) => {
    try {      
      // First get the offer details to access the transaction number
      const offerResponse = await axiosInstance.get(`/api/offer/user-offers/${offerId}/`);
      const offerDetails = offerResponse.data;
      
      // Get ports based on Incoterm type
      const isIncoTermFOB = selectedIncoterm === 'FOB';
      
      // Default port value if none is set
      const defaultPort = '1sb 1sp [Port], [Region], [Country]';
      const portValue = loadingPort || defaultPort;
      
      // For FOB, set loading port. For others (CFR/CIF), set discharging port
      const loadingPortValue = isIncoTermFOB ? portValue : null;
      const dischargingPortValue = !isIncoTermFOB ? portValue : null;

      console.log('Port details:', {
        incoterm: selectedIncoterm,
        loadingPort: loadingPortValue,
        dischargingPort: dischargingPortValue
      });
      
      const origin = selectedOrigin || '';
      const product = selectedProduct || '';

      // Convert Offer/Bid to Sale/Purchase
      const isDealTypePurchase = dealType === 'Bid';
      const recapType = isDealTypePurchase ? 'PURCHASE' : 'SALE';
      
      // Determine price value based on price type
      const priceType = offerData['Base Price']?.['Price type'] || '';
      const contractPriceValue = priceType.toLowerCase() === 'fix price' 
        ? basePriceForFixedPrice || ''
        : basePriceForIndex || '';
      
      // Set entity based on deal type
      const entityValue = isDealTypePurchase ? selectedBuyer : selectedSeller;
      
      const payload = {
        associated_offer_or_bid: offerId,
        recap_type: recapType,
        product: product,
        origin: origin,
        loading_port: loadingPortValue || '',
        discharging_port: dischargingPortValue || '',
        vessel_name: "",
        entity: entityValue,
        
        // For Sale deals (when original deal is an Offer)
        sale_contract_quantity: !isDealTypePurchase ? quantityWeight || '' : '',
        sale_contract_quantity_unit: !isDealTypePurchase ? quantityUnit || '' : '',
        sale_shipping_tolerance: !isDealTypePurchase ? formatToleranceField(
          quantityToleranceLowerValue,
          quantityToleranceLowerUnit,
          quantityToleranceUpperValue,
          quantityToleranceUpperUnit
        ) || quantityTolerance || '' : '',
        sale_shipping_tolerance_lower_value: !isDealTypePurchase ? quantityToleranceLowerValue || '' : '',
        sale_shipping_tolerance_lower_unit: !isDealTypePurchase ? quantityToleranceLowerUnit || '%' : '',
        sale_shipping_tolerance_upper_value: !isDealTypePurchase ? quantityToleranceUpperValue || '' : '',
        sale_shipping_tolerance_upper_unit: !isDealTypePurchase ? quantityToleranceUpperUnit || '%' : '',
        sale_incoterm: !isDealTypePurchase ? selectedIncoterm || '' : '',
        sale_delivery_point: '',
        sale_contract_price_currency: !isDealTypePurchase ? offerData['Base Price']?.['Currency'] || '' : '',
        sale_contract_price_value: !isDealTypePurchase ? contractPriceValue : '',
        sale_payment_terms: !isDealTypePurchase ? selectedPaymentMethod || '' : '',
        sale_payment_terms_wording: !isDealTypePurchase ? paymentWording || '' : '',
        ...pledgeFieldsFor(!isDealTypePurchase ? pledge : pledgeOf(null), 'sale_'),
        sale_cv_basis_option: !isDealTypePurchase ? offerData['Base Price']?.['CV Basis'] || '' : '',
        sale_cv_basis_value: !isDealTypePurchase ? offerData['Base Price']?.['Kcal/Kg'] || '' : '',
        sale_buyer: !isDealTypePurchase ? selectedBuyer || '' : '',
        
        // For Purchase deals (when original deal is a Bid)
        purchase_contract_quantity: isDealTypePurchase ? quantityWeight || '' : '',
        purchase_contract_quantity_unit: isDealTypePurchase ? quantityUnit || '' : '',
        purchase_shipping_tolerance: isDealTypePurchase ? formatToleranceField(
          quantityToleranceLowerValue,
          quantityToleranceLowerUnit,
          quantityToleranceUpperValue,
          quantityToleranceUpperUnit
        ) || quantityTolerance || '' : '',
        purchase_shipping_tolerance_lower_value: isDealTypePurchase ? quantityToleranceLowerValue || '' : '',
        purchase_shipping_tolerance_lower_unit: isDealTypePurchase ? quantityToleranceLowerUnit || '%' : '',
        purchase_shipping_tolerance_upper_value: isDealTypePurchase ? quantityToleranceUpperValue || '' : '',
        purchase_shipping_tolerance_upper_unit: isDealTypePurchase ? quantityToleranceUpperUnit || '%' : '',
        purchase_incoterm: isDealTypePurchase ? selectedIncoterm || '' : '',
        purchase_delivery_point: '',
        purchase_contract_price_currency: isDealTypePurchase ? offerData['Base Price']?.['Currency'] || '' : '',
        purchase_contract_price_value: isDealTypePurchase ? contractPriceValue : '',
        purchase_payment_terms: isDealTypePurchase ? selectedPaymentMethod || '' : '',
        purchase_payment_terms_wording: isDealTypePurchase ? paymentWording || '' : '',
        ...pledgeFieldsFor(isDealTypePurchase ? pledge : pledgeOf(null), 'purchase_'),
        purchase_price_basis_option: isDealTypePurchase ? offerData['Base Price']?.['CV Basis'] || '' : '',
        purchase_price_basis_value: isDealTypePurchase ? offerData['Base Price']?.['Kcal/Kg'] || '' : '',
        purchase_seller: isDealTypePurchase ? selectedSeller || '' : '',
        
        laycan: (laycanStartDate && laycanEndDate)
          ? `${laycanStartDate} to ${laycanEndDate}`
          : laycanStartDate || laycanEndDate || '',
      };
      
      // Only the trader's OWN deal name is stored on the recap. The name
      // people see is worked out by the server (offer/deal_names.py), so it
      // tracks later edits to the recap instead of freezing whatever this
      // deal said at creation time.
      payload.recap_display_name = customDealName || null;

      // Several products: the recap takes each product with the quantity chosen
      // in the pop-up and the bid's price for it; the server works out the
      // recap's own product, total and average from these lines.
      if (recapLines) payload.products = recapLines;

      console.log('Trade recap payload:', payload);

      const response = await axiosInstance.post('/api/offer/trade-recaps/', payload);
      console.log('Trade Recap saved successfully:', response.data);
      
      // Navigate to the trade recap page with the converted type in Title Case
      const displayType = recapType === 'SALE' ? 'Sale' : 'Purchase';
      history.push(`/trade-recap/${response.data.id}?type=${displayType}`);
      
      return true;
    } catch (error) {
      console.error('Error saving trade recap:', error);
      // Say why the recap was not made (it used to fail without a word). The
      // recap names a refused field; on this screen a refused unit is the
      // deal's own Quantity unit box (ledger 165: a Solids quantity needs one).
      showError(recapSaveRefusal(error, 'Failed to create trade recap. Please try again.', {
        sale_contract_quantity_unit: 'Quantity unit',
        purchase_contract_quantity_unit: 'Quantity unit',
      }), 7000);
      return false;
    }
  };

  //Handle draft offer
  const handleDraftOffer = () => {
    setShowDraftPopup(true);
  };

  // Handle confirm draft
  const handleConfirmDraft = async () => {
    try {
      const saved = await saveOfferData(false);
      if (saved) {
        localStorage.removeItem('previousOfferId');
        goToTradeOps(history, tradeOpsDesk);
      }
    } catch (error) {
      console.error('Error drafting offer:', error);
      showError(getErrorMessage(error, 'Failed to save deal. Please try again.'));
    }
  };

  //Recap Popup
  const handleRecapClick = async () => {
    // Several products: how much of each product this recap takes. Each box
    // starts at what is still free of that product; the bid closes on its
    // total (Chris's call), so the pop-up also knows what is left on it.
    if (offerProducts.length >= 2) {
      let consumed = '0';
      let taken = [];
      if (offerId) {
        try {
          const resp = await axiosInstance.get(`/api/offer/user-offers/${offerId}/`);
          consumed = String(resp.data?.consumed_quantity ?? '0');
        } catch (err) {
          // The server still refuses a recap beyond what is left on the bid.
          console.warn('[RECAP_POPUP] Could not load what earlier recaps took of this deal', err);
        }
        try {
          const resp = await axiosInstance.get(`/api/offer/trade-recaps/?offer_id=${offerId}`);
          const recaps = Array.isArray(resp.data) ? resp.data : (resp.data?.results || []);
          taken = takenByRecaps(recaps);
        } catch (err) {
          console.warn('[RECAP_POPUP] Could not load the earlier recaps of this deal', err);
        }
      }
      const { total } = linesHeader(offerProducts, OFFER_LINE_KEYS);
      const remaining = subtractFigures(total || '0', consumed, QUANTITY_DECIMALS);
      setRecapBidRemaining(Math.max(0, parseFloat(remaining) || 0));
      const free = remainingPerLine(offerProducts, taken);
      setRecapLineFree(free);
      setRecapLineQuantities(free);
    }
    setShowRecapPopup(true);
  };

// Handle confirm recap
const handleConfirmRecap = async () => {
  console.log('Starting handleConfirmRecap', { offerId, isDealInDatabase, loadingPort, selectedProduct, selectedOrigin });

  // Several products: how much of each product, within what is left on the bid.
  const severalProducts = offerProducts.length >= 2;
  if (severalProducts && recapQuantitiesProblem(recapLineQuantities, recapBidRemaining, formatQuantityDisplay)) {
    return;
  }
  const recapLines = severalProducts
    ? recapLinesFromOffer(offerProducts, recapLineQuantities, dealType === 'Bid' ? 'PURCHASE' : 'SALE')
    : null;
  
  try {
    // Save offer data first
    console.log('Saving offer data...');
    const savedOfferId = await saveOfferData(true);
    console.log('Saved offer ID:', savedOfferId);
    
    if (savedOfferId) {
      // Create trade recap
      const recapCreated = await saveTradeRecap(savedOfferId, recapLines);
      
      if (!recapCreated) {
        console.error('Failed to create trade recap');
        return;
      }
      
      // Navigation is now handled in saveTradeRecap
      setShowRecapPopup(false);
    } else {
      console.error('Failed to save offer data');
    }
  } catch (error) {
    console.error('Error in handleConfirmRecap:', error);
  }
};

  // Change the performAutosave function to be a regular function (not debounced)
  const performAutosave = useCallback(async () => {
    console.log('[AUTOSAVE] Autosave check', { 
      hasUnsavedChanges, 
      isDealInDatabase, 
      offerId 
    });
    
    // First check each condition individually for better logging
    if (!hasUnsavedChanges) {
      console.log('[AUTOSAVE] Skipping - No unsaved changes');
      return;
    }
    
    if (!isDealInDatabase) {
      console.log('[AUTOSAVE] Skipping - Deal not in database yet');
      // For new forms, save to localStorage instead
      try {
        const emergencyBackup = {
          offerData,
          qualityValues,
          selectedOptions,
          timestamp: Date.now()
        };
        localStorage.setItem('offerAutosave_new', JSON.stringify(emergencyBackup));
        console.log('[AUTOSAVE] Saved to localStorage for new form');
      } catch (error) {
        console.error('[AUTOSAVE] localStorage backup failed', error);
      }
      return;
    }
    
    if (!offerId) {
      console.log('[AUTOSAVE] Skipping - No offer ID available');
      return;
    }
    
    // All conditions passed, proceed with autosave
    try {
      console.log('%c[AUTOSAVE] Starting autosave operation at ' + new Date().toLocaleTimeString(), 'background: #4CAF50; color: white; padding: 4px; border-radius: 4px; font-weight: bold;');
      
      // Use existing saveOfferData with autosave parameter
      await saveOfferData(false, true);
      
      // Update tracked state
      setHasUnsavedChanges(false);
      setLastSaveTime(Date.now());
      console.log('%c[AUTOSAVE] Completed successfully at ' + new Date().toLocaleTimeString(), 'background: #2196F3; color: white; padding: 4px; border-radius: 4px; font-weight: bold;');
    } catch (error) {
      console.error('%c[AUTOSAVE] Failed at ' + new Date().toLocaleTimeString(), 'background: #F44336; color: white; padding: 4px; border-radius: 4px; font-weight: bold;', error);
      // Store emergency backup in localStorage
      try {
        const emergencyBackup = {
          offerData,
          qualityValues,
          selectedOptions,
          timestamp: Date.now()
        };
        localStorage.setItem(`offerAutosave_${offerId || 'new'}`, JSON.stringify(emergencyBackup));
        console.log('[AUTOSAVE] Emergency backup saved to localStorage');
      } catch (backupError) {
        console.error('[AUTOSAVE] Failed to create emergency backup:', backupError);
      }
    }
  }, [hasUnsavedChanges, isDealInDatabase, offerId, offerData, qualityValues, selectedOptions, saveOfferData]);

  // Store the function in a ref for cancellation capability
  useEffect(() => {
    debouncedSaveRef.current = performAutosave;
  }, [performAutosave]);

  // Remove the useEffect that responds to hasUnsavedChanges and replace with:
  // We're not responding to every change anymore, only relying on the interval

  // Update interval-based autosave for clearer logging
  useEffect(() => {
    console.log(`[AUTOSAVE ${new Date().toISOString()}] Setting up autosave interval`);
    
    const intervalId = setInterval(() => {
      const timeSinceLastSave = Date.now() - lastSaveTime;
      console.log(`[AUTOSAVE ${new Date().toISOString()}] Interval check`, { 
        hasUnsavedChanges, 
        isDealInDatabase, 
        offerId,
        timeSinceLastSave,
        AUTOSAVE_INTERVAL
      });
      
      if (hasUnsavedChanges && timeSinceLastSave >= AUTOSAVE_INTERVAL) {
        console.log(`[AUTOSAVE ${new Date().toISOString()}] Interval-based autosave triggered`);
        performAutosave();
      }
    }, 30000); // Check every 30 seconds
    
    return () => {
      console.log(`[AUTOSAVE ${new Date().toISOString()}] Clearing autosave interval`);
      clearInterval(intervalId);
    };
  }, [hasUnsavedChanges, isDealInDatabase, lastSaveTime, offerId, performAutosave]);

  // Try to recover data on component mount
  useEffect(() => {
    const recoverAutosavedData = () => {
      if (offerId) {
        try {
          const savedData = localStorage.getItem(`offerAutosave_${offerId}`);
          if (savedData) {
            const parsedData = JSON.parse(savedData);
            
            // Check if the backup is recent enough (within last 24 hours)
            const backupAge = Date.now() - parsedData.timestamp;
            const MAX_BACKUP_AGE = 24 * 60 * 60 * 1000; // 24 hours
            
            if (backupAge <= MAX_BACKUP_AGE) {
              console.log('Found emergency backup data. Recovering...');
              setOfferData(parsedData.offerData);
              setQualityValues(parsedData.qualityValues);
              setSelectedOptions(parsedData.selectedOptions);
              console.log('Emergency backup data recovered successfully');
            }
            
            // Clear the backup data regardless of whether we used it
            localStorage.removeItem(`offerAutosave_${offerId}`);
          }
        } catch (error) {
          console.error('Error recovering autosaved data:', error);
        }
      }
    };
    
    recoverAutosavedData();
  }, [offerId]);

  // Add this useEffect right before the component's return statement
  // After the last useEffect (search for the last one and add after it)

  // Add window beforeunload handler to show confirmation dialog
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        // Standard way to show confirmation dialog when leaving page
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Handle clicks outside Hamster panel to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hamsterPanelRef.current && !hamsterPanelRef.current.contains(event.target) && isHamsterBuilderOpen) {
        setIsHamsterBuilderOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHamsterBuilderOpen]);

  // Handle escape key to close Hamster panel
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isHamsterBuilderOpen) {
        setIsHamsterBuilderOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isHamsterBuilderOpen]);

  // Prevent body scroll when Hamster panel is open
  useEffect(() => {
    if (isHamsterBuilderOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isHamsterBuilderOpen]);

  // Store original state when data is first loaded (add after the useEffect that loads form data)
  useEffect(() => {
    if (isFormLoaded && !originalState && offerData && Object.keys(offerData).length > 0) {
      // Deep copy the initial form state for potential rollback
      setOriginalState({
        offerData: JSON.parse(JSON.stringify(offerData)),
        qualityValues: JSON.parse(JSON.stringify(qualityValues)),
        selectedOptions: JSON.parse(JSON.stringify(selectedOptions))
      });
      console.log('[AUTOSAVE] Stored original state for potential rollback');
    }
  }, [isFormLoaded, offerData, qualityValues, selectedOptions, originalState]);

  // Rollback function to revert to original state
  const rollbackChanges = async () => {
    if (!originalDataRef.current || !offerId) {
      console.log('[AUTOSAVE] Rollback not possible - missing original data or offer ID');
      return false;
    }
    
    const originalData = originalDataRef.current;
    
    // Skip if still loading
    if (originalData.loading || originalData.error) {
      console.log('[AUTOSAVE] Cannot rollback - original data not fully loaded');
      return false;
    }
    
    console.log('[AUTOSAVE] Rolling back to original state using preserved data');
    
    try {
      // Send original API data back to server
      if (isDealInDatabase && originalData.apiResponse) {
        console.log('[AUTOSAVE] Sending original API response back to server');
        await axiosInstance.put(`/api/offer/user-offers/${offerId}/`, originalData.apiResponse);
        
        if (originalData.qualityValues && originalData.qualityValues.length > 0) {
          console.log('[AUTOSAVE] Restoring original quality values');

          // Prepare quality data in the correct format expected by the server
          const formattedQualityData = originalData.qualityValues.map(item => {
            // Ensure param_id is correctly formatted as an integer where possible
            let paramId = item.quality_parameter_id;
            
            // Return the properly formatted object for the API endpoint
            return {
              param_id: paramId, // This is the key fix - using the correct field name and format
              typical_or_guaranteed_value: item.typical_or_guaranteed_value || '',
              min_max_or_rejection_value: item.min_max_or_rejection_value || '',
              selected_option: item.selected_option || null,
              typical_or_guaranteed: item.typical_or_guaranteed || 'Typical',
              minmax_or_rejection: item.minmax_or_rejection || 'Min/Max',
              property_name: item.property_name || null,
              unit: item.unit || null,
              order: item.order || 0,
              is_custom: item.is_custom || false
            };
          });
          
          console.log('[AUTOSAVE] Formatted quality data for rollback:', formattedQualityData);
          
          // Send the formatted quality data
          await axiosInstance.post(
            `/api/offer/user-offers/${offerId}/quality-values/`,
            { quality_data: formattedQualityData }
          );
        }
        
        // Restore UI state to match server data
        console.log('[AUTOSAVE] Restoring UI to match original data');
        const api = originalData.apiResponse;
        
        // Set individual fields from original data
        setSelectedSeller(api.selling_company_entity || '');
        setSellerFormattedText(api.selling_company_formatted_text || '');
        setSelectedBuyer(api.buying_company_entity || '');
        setBuyerFormattedText(api.buying_company_formatted_text || '');
        setSelectedIncoterm(api.trade_incoterm_type || '');
        setIncotermFormattedText(api.trade_incoterm_formatted_text || '');
        setSelectedProduct(api.commodity_product_type || '');
        setSelectedOrigin(api.commodity_origin_country || '');
        setAdditionalInfo(api.commodity_additional_details || '');
        setCommodityFormattedText(api.commodity_formatted_text || '');
        setQuantityWeight(api.quantity_in_metric_tons || '');
        setQuantityTolerance(api.quantity_tolerance_percentage || '');
        setQuantityFormattedText(api.quantity_formatted_text || '');
        
        // Parse tolerance into new fields or use existing new fields
        if (api.quantity_tolerance_lower_value || api.quantity_tolerance_upper_value) {
          setQuantityToleranceLowerValue(api.quantity_tolerance_lower_value || '');
          setQuantityToleranceLowerUnit(api.quantity_tolerance_lower_unit || '%');
          setQuantityToleranceUpperValue(api.quantity_tolerance_upper_value || '');
          setQuantityToleranceUpperUnit(api.quantity_tolerance_upper_unit || '%');
        } else if (api.quantity_tolerance_percentage) {
          const parsed = parseToleranceString(api.quantity_tolerance_percentage);
          setQuantityToleranceLowerValue(parsed.lowerValue);
          setQuantityToleranceLowerUnit(parsed.lowerUnit);
          setQuantityToleranceUpperValue(parsed.upperValue);
          setQuantityToleranceUpperUnit(parsed.upperUnit);
        }
        
        setQuantityOption(api.quantity_option_holder || '');
        setDelivery(api.delivery_terms_and_conditions || '');
        setDeliveryFormattedText(api.delivery_formatted_text || '');
        setLoadingPort(api.trade_incoterm_type === 'FOB' ? 
          (api.loading_ports_details || '') : 
          (api.discharging_ports_details || ''));
        setLoadingPortFormattedText(api.loading_ports_formatted_text || '');
        setPriceType(api.price_determination_type || '');
        setBasePriceCurrency(api.price_currency_type || '');
        setBasePriceForFixedPrice(api.price_fixed_amount || '');
        setBasePriceForIndex(api.price_premium_or_discount || '');
        setBasePriceCVBasis(api.price_cv_basis_type || '');
        setBasePriceKcalKg(api.price_kcal_per_kg || '');
        setBasePriceApplicableIndexPeriod(api.price_index_applicable_period || '');
        setPriceFormattedText(api.price_formatted_text || '');
        setQualityAdjustmentsProRata(api.quality_prorata_cv_adjustments === 'Yes');
        setQualityAdjustmentsOtherPremiumPenalties(api.quality_other_adjustments || '');
        setQualityAndQuantityDeterminationSurveyor(api.quality_quantity_surveyor_details || '');
        setQualityQuantityFormattedText(api.quality_quantity_formatted_text || '');
        setSelectedPaymentMethod(api.payment_method_type || '');
        setPledge(pledgeOf(api));
        setPaymentWording(api.payment_detailed_terms || '');
        setSelectedShippingMethod(api.shipping_terms_type || '');
        setShippingWording(api.shipping_detailed_conditions || '');
        setSelectedOtherTermsMethod(api.other_terms_category || '');
        setOtherTermsWording(api.other_terms_detailed_conditions || '');
        setOfferValidityDate(api.deal_validity_date || '');
        setDealValidityFormattedText(api.deal_validity_formatted_text || '');
        setOfferValidityTime(api.deal_validity_time || '');
        setOfferValidityTimeZone(api.deal_validity_timezone || '');
        
        // Initialize qualityValues state from original data
        const initialValues = {};
        if (originalData.qualityValues && originalData.qualityValues.length > 0) {
          originalData.qualityValues.forEach(item => {
            if (item.is_custom) {
              // For custom parameters, use custom_ID format and preserve property_name and unit
              const customId = `custom_${item.id}`;
              initialValues[customId] = {
                typical_or_guaranteed_value: item.typical_or_guaranteed_value,
                min_max_or_rejection_value: item.min_max_or_rejection_value,
                propertyName: item.property_name,
                unit: item.unit
              };
            } else {
              initialValues[item.quality_parameter_id] = {
                typical_or_guaranteed_value: item.typical_or_guaranteed_value,
                min_max_or_rejection_value: item.min_max_or_rejection_value
              };
            }
          });
          
          setQualityValues(initialValues);
          
          // Set column headers
          if (originalData.qualityValues.length > 0) {
            setQualityColumnHeader(originalData.qualityValues[0].typical_or_guaranteed || 'Typical');
            setMinMaxColumnHeader(originalData.qualityValues[0].minmax_or_rejection || 'Min/Max');
          }
          
          // Also restore selected options
          const options = {};
          originalData.qualityValues.forEach(item => {
            if (item.selected_option) {
              options[item.quality_parameter_id] = item.selected_option;
            }
          });
          
          setSelectedOptions(options);
          
          // Handle custom rows
          const customRows = originalData.qualityValues
            .filter(item => item.is_custom)
            .map(item => ({
              id: item.id,
              propertyName: item.property_name || '',
              unit: item.unit || '',
              typical_or_guaranteed_value: item.typical_or_guaranteed_value || '',
              min_max_or_rejection_value: item.min_max_or_rejection_value || '',
              order: item.order || 0
            }));
          
          if (customRows.length > 0) {
            setCustomQualityRows(customRows);
            setSavedCustomRows(customRows.map(row => row.id));
          }
        }
      }
      
      setHasUnsavedChanges(false);
      console.log('[AUTOSAVE] Successfully rolled back to original state');
      return true;
    } catch (error) {
      console.error('[AUTOSAVE] Rollback failed:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return false;
    }
  };

  // Add this useEffect after the useEffect that loads data from the API
  // Add after the existing useEffect that ends around line 555
  useEffect(() => {
    // Only fetch and store original data if we're viewing an existing offer
    if (offerId && !originalDataRef.current && isDealInDatabase) {
      console.log('[AUTOSAVE] Fetching original API data on initial load');
      
      // Store empty placeholder immediately to prevent multiple calls
      originalDataRef.current = { loading: true };
      
      // Fetch raw offer data
      axiosInstance.get(`/api/offer/user-offers/${offerId}/`)
        .then(response => {
          console.log('[AUTOSAVE] Stored original API response');
          
          // Fetch quality values
          return axiosInstance.get(`/api/offer/user-offers/${offerId}/quality-values/`)
            .then(qualityResponse => {
              // Store both responses in the ref - keep quality values exactly as received
              console.log('[AUTOSAVE] Original quality values:', qualityResponse.data);
              
              originalDataRef.current = {
                apiResponse: response.data,
                qualityValues: qualityResponse.data
              };
              
              console.log('[AUTOSAVE] Original data preserved for rollback');
            });
        })
        .catch(error => {
          console.error('[AUTOSAVE] Failed to fetch original data:', error);
          originalDataRef.current = { error };
        });
    }
  }, [offerId, isDealInDatabase]);

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//Render Deal Generator, this is where the deal generator is rendered, the visual elements are defined here
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  if (offerIdFromQuery && dealLoadProblem) {
    return (
      <Layout
        showLeftNavSidebar={true}
        showRightSidebar={false}
      >
        <RecordLoadProblem
          problem={dealLoadProblem}
          recordName="deal"
          areaName="Trade Ops"
          backLabel="Back to Trade Ops"
          onBack={() => goToTradeOps(history, tradeOpsDesk, {
            selectedDate: location.state?.selectedDate,
            activeTab: location.state?.activeTab || 'deals',
          })}
          onRetry={() => setDealLoadAttempt((n) => n + 1)}
        />
      </Layout>
    );
  }

return (
    //Layout is the main container for the offer generator
    <Layout 
      showLeftNavSidebar={true} 
      showRightSidebar={false}
    >
      <div className="offer-generator-wrapper">
        <div className="offer-generator">
          {/* HMS Screen Header */}
          <div className="hms-screen-header">
            {/* Back Button */}
            <button 
              className="offer-generator-back-button"
              onClick={handleBackClick}
              title="Back to Trade Operations"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <div className="header-logo">
              <img src={logo} alt="IMT" className="header-logo-img" />
              <div className="header-title-content">
                <div className="offer-header-stacked-fields">
                  <div className="offer-header-name-row">
                    <HeaderNameEditor
                      typedName={customDealName}
                      automaticName={automaticDealName}
                      onTypedNameChange={(name) => {
                        setCustomDealName(name);
                        setHasUnsavedChanges(true);
                      }}
                      emptyLabel={`${dealType} Deal`}
                      inputClassName="offer-header-name-input"
                      displayClassName="offer-header-name-display"
                      iconClassName="offer-header-edit-icon"
                    />
                  </div>
                  <div className="offer-header-info-row">
                    {offerId && (
                      <span className="offer-header-number-display">
                        • {dealType} ID: {offerId}
                      </span>
                    )}
                    {offerId && createdBy.when && (
                      <CreatedByLine className="hms-header-created-by" name={createdBy.name} when={createdBy.when} />
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action buttons - matching delivery screen style */}
            <div className="header-actions" style={{ display: 'flex', gap: '12px', position: 'relative', alignItems: 'center' }}>
              {/* Indication Only Toggle before buttons */}
              <div className="indication-toggle-inline">
                <label className="toggle-switch-inline">
                  <input 
                    type="checkbox" 
                    checked={isIndicationOnly}
                    onChange={(e) => setIsIndicationOnly(e.target.checked)}
                  />
                  <div className="toggle-slider-inline"></div>
                </label>
                <span className="toggle-label-inline">Indication Only</span>
              </div>
              
              <button 
                className="offer-button draft-button"
                onClick={handleDraftOffer}
                type="button"
              >
                Draft Deal
              </button>
              <button 
                className="offer-button recap-button"
                onClick={associatedTradeRecapId && !isGrouping ? () => history.push(`/trade-recap/${associatedTradeRecapId}`) : handleRecapClick}
                type="button"
              >
                {associatedTradeRecapId && !isGrouping ? 'View Recap' : 'Click to Recap'}
              </button>
              <button 
                className="offer-button hamster-button"
                onClick={() => setIsHamsterBuilderOpen(!isHamsterBuilderOpen)}
                type="button"
              >
                Hamster Builder
              </button>
            </div>
          </div>

        <div className="offer-form-wrapper">
        {/* Confirmation modal for back button */}
        {showPopup && (
          <TradeRecapBackModal
            message={isDealInDatabase && originalState 
              ? "Going back will discard ALL changes made to this deal, even if they were autosaved. Continue?"
              : "Undrafted deals will not be saved, are you sure you want to go back?"}
            onCancel={handlePopupClose}
            onConfirm={handleConfirmBack}
            onDraft={handleDraftAndLeave}
          />
        )}
        {/* Confirmation modal for drafting */}
        {showDraftPopup && (
          <DealConfirmationModal
            title="Save as Draft"
            message="This will save your current deal with all entered information as a draft that you can continue editing later."
            confirmText="Save Draft"
            cancelText="Cancel"
            iconType="info"
            onConfirm={handleConfirmDraft}
            onCancel={() => setShowDraftPopup(false)}
          />
        )}
        {/* Confirmation modal for creating recap */}
        {showRecapPopup && (
          <DealConfirmationModal
            title="Create Trade Recap"
            message="This will save your deal and create a new Trade Recap. You will be redirected to the Trade Recap screen to continue with deal details."
            confirmText="Create Recap"
            cancelText="Cancel"
            iconType="success"
            onConfirm={handleConfirmRecap}
            onCancel={() => setShowRecapPopup(false)}
            confirmDisabled={offerProducts.length >= 2
              && !!recapQuantitiesProblem(recapLineQuantities, recapBidRemaining, formatQuantityDisplay)}
          >
            {offerProducts.length >= 2 && (
              <RecapProductQuantities
                lines={offerProducts}
                free={recapLineFree}
                quantities={recapLineQuantities}
                onChange={setRecapLineQuantities}
                unit={quantityUnit}
                bidRemaining={recapBidRemaining}
                formatQuantity={formatQuantityDisplay}
                error={recapQuantitiesProblem(recapLineQuantities, recapBidRemaining, formatQuantityDisplay)}
              />
            )}
          </DealConfirmationModal>
        )}
        {/* Offer form */}
        <form className="offer-form">
          <div className="clause-component header-row">
            <div className="clause-header">Clause</div>
            <div className="component-description-header">Content</div>
            <div className="component-input"></div>
          </div>
          {clauses.map(clause => (
            clause.clause_name === 'Quality' ? (
            <>
            <div className="clause-name">Qualities</div> {/* Add this line to display "Qualities" */}
            <div className="select-container">
            <select onChange={(e) => handlePresetSelection(e.target.value)}>
                <option value="--Select a Preset--">--Select a Preset--</option>
                <option value="RSA - 4800nar / 4500 min">RSA - 4800nar / 4500 min</option>
                <option value="RSA - 4800nar / 4600 min">RSA - 4800nar / 4600 min</option>
                <option value="RSA - 5500nar (RB3)">RSA - 5500nar (RB3)</option>
                <option value="RSA - 6000nar (RB1)">RSA - 6000nar (RB1)</option>
                <option value="RSA - 6000nar / 5700 min (RB2)">RSA - 6000nar / 5700 min (RB2)</option>
                <option value="USA - 6150nar ILB">USA - 6150nar ILB</option>
                <option value="USA - Petcoke HS Generic">USA - Petcoke HS Generic</option>
                <option value="INDO - 5500gar GBU">INDO - 5500gar GBU</option>
                <option value="INDO - 4800gar IPC">INDO - 4800gar IPC</option>
                <option value="RSA - 5200nar (Osho Blend)">RSA - 5200nar (Osho Blend)</option>
            </select>
            </div>
            <div className="quality-table-container">
              <table className="quality-table">
                <thead>
                  <tr>
                  <th className="quality-specifications">Quality Specifications</th>
                  <th className="unit-column">Unit</th>
                    <th className="typical-guaranteed typical-header">
                      <select
                        className="qualities-table-dropdown"
                        value={qualityColumnHeader}
                        onChange={(e) => setQualityColumnHeader(e.target.value)}
                      >
                        <option value="Typical">Typical</option>
                        <option value="Guaranteed">Guaranteed</option>
                      </select>
                    </th>
                    <th className="minmax-rejection minmax-header">
                      <select
                        className="qualities-table-dropdown"
                        value={minMaxColumnHeader}
                        onChange={(e) => setMinMaxColumnHeader(e.target.value)}
                      >
                        <option value="Min/Max">Min/Max</option>
                        <option value="Rejection">Rejection</option>
                      </select>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {qualityParameters
                    .filter(param => param.description !== "Custom Quality Parameter")
                    .map((param) => (
                    <tr key={param.id}>
                      <td className="quality-specifications">
                        {param.has_options ? (
                          <select
                            className="qualities-table-dropdown"
                            value={selectedOptions[param.id] || ''}
                            onChange={(e) => handleDropdownChange(param.id, e.target.value)}
                          >
                            {param.options.map(option => (
                              <option key={option.id} value={option.option_value}>
                                {option.option_value}
                              </option>
                            ))}
                          </select>
                        ) : (
                          param.description
                        )}
                      </td>
                      <td className="unit-column">{param.unit}</td>
                      <td className="typical-guaranteed">
                        <input
                        type="text"
                        name={`typical-${param.id}`}
                        value={qualityValues[param.id]?.typical_or_guaranteed_value || ''}
                        onChange={(e) => handleQualityInputChange(param.id, 'typical_or_guaranteed_value', e.target.value)}
                      />
                      </td>
                      <td className="minmax-rejection">
                      <input
                          type="text"
                          name={`minMax-${param.id}`}
                          value={qualityValues[param.id]?.min_max_or_rejection_value || ''}
                          onChange={(e) => handleQualityInputChange(param.id, 'min_max_or_rejection_value', e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                  
                  {/* Render custom quality rows */}
                  {customQualityRows.map((row) => {
                    // Check if this row is in saved state
                    const isSaved = savedCustomRows.includes(row.id);
                    
                    // Function to handle row click in delete mode
                    const handleRowClick = () => {
                      if (deleteModeActive && isSaved) {
                        deleteSavedRow(row.id);
                      }
                    };
                    
                    return (
                      <React.Fragment key={row.id}>
                        <tr 
                          className={deleteModeActive && isSaved ? 'deletable-row' : ''}
                          onClick={handleRowClick}
                        >
                          <td className="quality-specifications">
                            {isSaved ? (
                              // Saved row format - similar to standard rows
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                position: 'relative'
                              }}>
                                {/* Delete button shown in delete mode */}
                                {deleteModeActive && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent row click
                                      deleteSavedRow(row.id);
                                    }}
                                    style={{
                                      marginRight: '8px',
                                      padding: '2px 6px',
                                      fontSize: '11px',
                                      background: '#fff0f0',
                                      border: '1px solid #ffd0d0',
                                      borderRadius: '3px',
                                      color: '#d32f2f',
                                      cursor: 'pointer',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    Delete
                                  </button>
                                )}
                                {row.propertyName}
                              </div>
                            ) : (
                              // Editable row format with input
                              <div style={{ display: 'flex', position: 'relative' }}>
                                <input
                                  type="text"
                                  value={row.propertyName}
                                  onChange={(e) => updateCustomRow(row.id, 'propertyName', e.target.value)}
                                  className="qualities-table-input"
                                  placeholder="Enter property name"
                                  style={{ 
                                    width: '100%',
                                    fontWeight: 'bold',
                                    border: '1px solid #e0e0e0',
                                    padding: '6px 10px'
                                  }}
                                />
                              </div>
                            )}
                          </td>
                          <td className="unit-column">
                            {isSaved ? (
                              // Show saved unit value
                              row.unit
                            ) : (
                              <input
                                type="text"
                                placeholder="Unit"
                                value={row.unit}
                                onChange={(e) => updateCustomRow(row.id, 'unit', e.target.value)}
                                className="qualities-table-input"
                                style={{ width: '100%' }}
                              />
                            )}
                          </td>
                          <td className="typical-guaranteed">
                            <input
                              type="text"
                              value={row.typical_or_guaranteed_value}
                              onChange={(e) => updateCustomRow(row.id, 'typical_or_guaranteed_value', e.target.value)}
                              className="qualities-table-input"
                              placeholder="Enter value"
                            />
                          </td>
                          <td className="minmax-rejection">
                            <input
                              type="text"
                              value={row.min_max_or_rejection_value}
                              onChange={(e) => updateCustomRow(row.id, 'min_max_or_rejection_value', e.target.value)}
                              className="qualities-table-input"
                              placeholder="Enter value"
                            />
                          </td>
                        </tr>
                        
                        {/* Action buttons row - only for non-saved rows */}
                        {!isSaved && !deleteModeActive && (
                          <tr className="custom-quality-actions">
                            <td colSpan={4} style={{ 
                              padding: '10px 0 10px 0', 
                              textAlign: 'right', 
                              borderBottom: '1px dashed #eaeaea' 
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'flex-end', 
                                gap: '8px', 
                                paddingRight: '10px' 
                              }}>
                                <button
                                  type="button"
                                  onClick={() => deleteCustomQualityRow(row.id)}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: '11px',
                                    background: '#ff0000',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Delete
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveCustomQualityRow(row.id)}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: '11px',
                                    background: '#00ff00',
                                    borderRadius: '3px',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Save
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Add row / Delete row buttons */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                marginTop: '10px',
                gap: '8px'
              }}>
                {/* Delete row button - only shows if there are saved rows */}
                {savedCustomRows.length > 0 && (
                  deleteModeActive ? (
                    <button 
                      type="button" 
                      className="delivery-cancel-delete-button"
                      onClick={toggleDeleteMode}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        background: '#f5f5f5',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      Cancel
                    </button>
                  ) : (
                    <button 
                      type="button" 
                      className="delivery-delete-row-button"
                      onClick={toggleDeleteMode}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        background: '#fff0f0',
                        border: '1px solid #ffd0d0',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>-</span> Delete row
                    </button>
                  )
                )}
                
                {/* Add row button - hide when in delete mode */}
                {!deleteModeActive && (
                  <button 
                    type="button" 
                    className="quality-add-row-button"
                    onClick={addCustomQualityRow}
                    style={{
                      padding: '4px 10px',
                      fontSize: '12px',
                      background: '#f0f7ff',
                      border: '1px solid #d0e3ff',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>+</span> Add row
                  </button>
                )}
              </div>
            </div>
              <div className="divider"></div> 
              </>
            ) : (
              <div key={clause.id} className="clause-section">
                {clause.components.map((component, index) => (
                  !(clause.clause_name === 'Quality Adjustments' && component.name === 'Standard Wording') && (
                  <div key={component.id} className="clause-component">
                    {index === 0 ? (
                      <div className="clause-name">
                        {clause.clause_name === 'Loading Port(s)' ? portType : clause.clause_name}
                      </div>
                    ) : (
                      <div className="clause-name"></div>
                    )}
                    <div className="component-description">
                      {clause.clause_name === 'Loading Port(s)' ? portType : component.name}
                      
                      {clause.clause_name === 'Commodity' && component.name === 'Additional Information' && (
                        <div className="fineprint">
                          Single mine/blend, Mine name, Producer, etc.
                        </div>
                        )}
                      {clause.clause_name === 'Quality Adjustments' && component.name === 'Other Premium/Penalties' && (
                        <div className="fineprint">
                          Examples: <br></br>
                          - Ash: for every 1% Ash (arb) above the guaranteed level a discount of $0.10/mt shall apply <br></br>
                          - TM: Total moisture exceeding the guaranteed level shall be deducted from BL weight <br></br>
                        </div>
                        )}
                      {clause.clause_name === 'Payment' && component.name === 'Payment Wording' && (
                        <div className="fineprint">
                          Choose standard wording from drop-down or select "Custom" to overwrite in full
                        </div>
                        )}
                      {clause.clause_name === 'Shipping Terms' && component.name === 'Shipping Terms Wording' && (
                        <div className="fineprint">
                          Choose standard wording from drop-down or select "Custom" to overwrite in full
                        </div>
                        )}
                      {clause.clause_name === 'Other Terms' && component.name === 'Other Terms Wording' && (
                        <div className="fineprint">
                          Choose standard wording from drop-down or select "Custom" to overwrite in full
                        </div>
                        )}
                      {clause.clause_name === 'Deal Validity' && component.name === 'Time-Zone' && (
                        <div className="fineprint">
                          Select timezone from drop-down
                        </div>
                        )}
                    </div>
                    <div className="component-input">
                      {renderClauseComponent(component, clause.clause_name)}
                    </div>
                  </div>
                )))}
                
                {/* Pledge row appended to the Payment clause (go-live checklist). */}
                {clause.clause_name === 'Payment' && (
                  <div className="clause-component">
                    <div className="clause-name"></div>
                    <div className="component-description">Pledge</div>
                    <div className="component-input">
                      <PledgeBankField
                        idPrefix="offer"
                        entity={pledgeEntity}
                        value={pledge}
                        onChange={handlePledgeChange}
                      />
                    </div>
                  </div>
                )}

                {/* Add Laycan component for Delivery clause */}
                {clause.clause_name === 'Delivery' && (
                  <div className="clause-component">
                    <div className="clause-name"></div>
                    <div className="component-description">Laycan</div>
                    <div className="component-input">
                      <DateRangePicker
                        startDateName="laycan_start_date"
                        endDateName="laycan_end_date"
                        startDateValue={laycanStartDate}
                        endDateValue={laycanEndDate}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          if (name === 'laycan_start_date') {
                            setLaycanStartDate(value);
                          } else if (name === 'laycan_end_date') {
                            setLaycanEndDate(value);
                          }
                          
                          // Update delivery textarea with laycan dates
                          if (laycanStartDate || laycanEndDate || value) {
                            const startDate = name === 'laycan_start_date' ? value : laycanStartDate;
                            const endDate = name === 'laycan_end_date' ? value : laycanEndDate;
                            
                            if (startDate && endDate) {
                              const laycanText = `Tentative loading laycan: ${startDate} to ${endDate}\nFinal 10 days laycan in Seller's option`;
                              setDelivery(laycanText);
                              handleInputChange('Delivery', 'Delivery', laycanText);
                            }
                          }
                        }}
                        placeholder="Select laycan date range"
                        className="delivery-laycan-picker"
                        format="DD/MM/YYYY"
                      />
                    </div>
                  </div>
                )}
                
                {clausesWithUneditableTextbox.includes(clause.clause_name) && renderUneditableTextbox(clause.clause_name)}

                {clause.clause_name === 'Seller' && <div className="divider"></div>}
                {clause.clause_name === 'Buyer' && <div className="divider"></div>}
                {clause.clause_name === 'Incoterms' && <div className="divider"></div>}
                {clause.clause_name === 'Commodity' && <div className="divider"></div>}
                {clause.clause_name === 'Quantity' && <div className="divider"></div>}
                {clause.clause_name === 'Delivery' && <div className="divider"></div>}
                {clause.clause_name === 'Loading Port(s)' && <div className="divider"></div>}
                {clause.clause_name === 'Quality' && <div className="divider"></div>}
                {clause.clause_name === 'Base Price' && <div className="divider"></div>}
                {clause.clause_name === 'Quality Adjustments' && <div className="divider"></div>}
                {clause.clause_name === 'Payment' && <div className="divider"></div>}
                {clause.clause_name === 'Quality and Quantity Determination' && <div className="divider"></div>}
                {clause.clause_name === 'Shipping Terms' && <div className="divider"></div>}
                {clause.clause_name === 'Other Terms' && <div className="divider"></div>}
              </div>
            )
          ))}
        </form>
        
        {/* Hamster Chat Integration */}
        </div> {/* Close offer-form-wrapper */}
        
        {/* Hamster Builder Slideout Panel - always mounted, toggled via CSS for persistence */}
        <div className="hamster-panel-overlay" style={{ display: isHamsterBuilderOpen ? 'flex' : 'none' }}>
          <div className="hamster-panel" ref={hamsterPanelRef}>
            <div className="hamster-panel-header">
              <div className="hamster-header-content">
                <h2>Hamster Deal Builder</h2>
                <p className="hamster-header-subtitle">Extracted information is automatically populated in the deal form</p>
              </div>
              <button 
                className="close-button"
                onClick={() => setIsHamsterBuilderOpen(false)}
                aria-label="Close Hamster Builder"
              >
                <CloseIconSmall />
              </button>
            </div>
            <div className="hamster-panel-content">
              <ChatWindow />
            </div>
          </div>
        </div>
        
        {/* Back to Top Button */}
        <BackToTopButton />
        
        {/* Footer */}
        <Footer />
      </div> {/* Close offer-generator */}
      </div> {/* Close offer-generator-wrapper */}
    </Layout>
  );
}

export default withAuth(OfferGenerator);